import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase, isSessionExpiredError } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SelectorTalleColor from '../components/pos/SelectorTalleColor';
import {
  isVariantTracked,
  isVariantActive,
  findVariantByCode,
  getVariantStockForSite,
  applyStockDelta,
  groupStockOperations,
} from '../utils/variantStock';
import {
  devolverVentaALotes,
  fetchLotTrackedProductIds,
  isExpiryTrackingEnabled,
  moverStockConLotes,
  summarizeSellableStock,
} from '../utils/stockLotes';
import {
  Search,
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Check,
  Package,
  Calendar,
  User,
  FileText,
  BadgeDollarSign,
  Loader2,
  Info,
  RefreshCcw,
  X,
  ScanBarcode,
  Percent,
  Filter,
  AlertTriangle,
  Calculator,
  Monitor,
  ChevronDown,
  Printer,
  MessageCircle,
  CheckCircle2,
  CreditCard
} from 'lucide-react';
import QRCode from 'qrcode';
import { processPendingBankSettlements } from '../utils/bankSettlements';
import { addSettlementDays } from '../utils/paymentSettlement';
import {
  goToFirstSaleOnboardingStep,
  matchesFirstSaleOnboardingIdentity,
  readFirstSaleOnboarding,
} from '../utils/firstSaleOnboarding';
import {
  fetchAllPaginated,
  getLegacyVariantStock,
  getProductStockForSite,
  hydrateLegacyVariantStock,
  resolvePreferredStockSiteId,
} from '../utils/productCatalog';
import {
  buildSalePaymentTerms,
  formatPaymentConditionLabel,
  getClientPaymentConditionDays,
  getSaleReceivableDueDate,
  isGenericConsumidorFinalClient,
  shouldUseClientAccountForSale,
  syncSaleClientDebit,
  syncSaleReceivable,
} from '../utils/salesReceivables';
import {
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  setSalePaymentTermsColumnsSupport,
} from '../utils/salesPaymentTermsSupport';
import {
  DEFAULT_POS_UI_CONFIG,
  getPosUiConfigFromPrintConfig,
} from '../utils/posUiConfig';
import {
  buildProductDisplayName,
  getCleanVariantLabel,
  getVariantName,
  sanitizeProductDisplayName,
} from '../utils/productLabels';
import {
  getPriceListAdjustedBasePrice,
  getPriceListAdjustedVariantPrice,
  isProductVisibleInPriceList,
  resolvePosPriceListForSite,
} from '../utils/priceLists';
import { getGestionCostosSellPrice } from '../utils/productSellPrice';
import { buildAfipIvaBreakdown, selectBillableItems } from '../utils/afipIva';
import { buildAfipQrUrl, buildFiscalTicketHtml, resolveTicketEmitter } from '../utils/ticketFiscal';
import {
  MOTIVOS_DESCUENTO_ITEM,
  aplicarDescuentoItem,
  quitarDescuentoItem,
  actualizarPrecioListaItem,
  precioListaItem,
  tieneDescuentoItem,
  normalizarConfigDescuentoItem,
  evaluarDescuentoItem,
  validarDescuentosCarrito,
  camposDescuentoSaleItem,
  canAutorizarDescuentoItem,
} from '../utils/descuentoItem';
import { fetchEffectiveModulePermissions } from '../utils/userModulePermissions';
import {
  formatLocalizedNumberInputValue,
  normalizeLocalizedNumberInput,
} from '../utils/numberFormat';
import { useSite } from '../context/SiteContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import {
  POINT_TIMEOUT_MS,
  SALE_STATUS_REVISION,
  isPaymentMethodAvailable,
} from '../utils/mpPoint';
import { applyActiveProductsFilter } from '../utils/productStatus';

// ELIMINADO: const BLOQUEAR_STOCK_NEGATIVO = true;
// Ahora se controla dinámicamente vía base de datos (allow_negative_stock).

const DEFAULT_PRINT_CONFIG = {
  presupuesto: {
    headerText: 'Presupuesto',
    subHeaderText: 'Documento no válido como factura',
    footerText: '',
    showLogo: true,
    logoBase64: null,
    printerWidth: 'a4',
    showClientInfo: true,
    showDiscounts: true,
    printAction: 'ask'
  },
  ticket_venta: {
    headerText: 'Ticket de Venta',
    subHeaderText: '',
    footerText: 'Gracias por su compra',
    showLogo: true,
    printerWidth: '80mm',
    showClientInfo: true,
    showDiscounts: true,
    showPaymentMethod: true,
    printAction: 'ask'
  }
};

const isMissingColumnError = (error, columnName) => {
  const needle = String(columnName || '').toLowerCase();
  const shortNeedle = needle.split('.').pop();
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!needle) return false;

  return Boolean(
    code === '42703'
    || code === 'PGRST204'
    || message.includes(`column "${needle}"`)
    || message.includes(`column '${needle}'`)
    || message.includes(`column ${needle}`)
    || message.includes(`column "${shortNeedle}"`)
    || message.includes(`column '${shortNeedle}'`)
    || message.includes(`column ${shortNeedle}`)
    || message.includes(`could not find the '${needle}' column`)
    || message.includes(`could not find the "${needle}" column`)
    || message.includes(`could not find the '${shortNeedle}' column`)
    || message.includes(`could not find the "${shortNeedle}" column`)
  );
};

const insertRowsWithMissingColumnFallback = async (tableName, rows, optionalColumns = []) => {
  let currentRows = rows;
  let remainingColumns = [...optionalColumns];

  while (true) {
    const response = await supabase.from(tableName).insert(currentRows);
    if (!response.error) return response;

    const removableColumn = remainingColumns.find((columnName) =>
      isMissingColumnError(response.error, columnName)
    );
    if (!removableColumn) return response;

    remainingColumns = remainingColumns.filter((columnName) => columnName !== removableColumn);
    currentRows = currentRows.map((row) => {
      const nextRow = { ...row };
      delete nextRow[removableColumn];
      return nextRow;
    });
  }
};

const DECIMAL_CART_QUANTITY_DECIMALS = 3;
const DECIMAL_CART_QUANTITY_STEP = 0.25;

// ============================================================================
// NUEVO: COMPONENTE MEMORIZADO PARA OPTIMIZAR EL RENDIMIENTO EXTREMO
// Esto evita que las 500+ imágenes se vuelvan a renderizar al tocar el carrito
// ============================================================================
const ProductCard = React.memo(({ prod, stockActual, price, allowNegativeStock, showNegativeStock, showImages, showBrand, abbreviateProductNames, onAdd, dataOnboardingId }) => {
  // Con stock negativo permitido el badge se ocultaba siempre, y el cajero se
  // quedaba sin ninguna señal de que el stock se estuviera moviendo (caso Memo:
  // el comercio concluyó que el POS había dejado de descontar). Ahora se puede
  // pedir que se muestre igual desde Configuración → Empresa.
  const showStockBadge = !allowNegativeStock || Boolean(showNegativeStock);
  const showImageSlot = Boolean(showImages);
  const hasImage = Boolean(prod.image_url);
  const displayName = sanitizeProductDisplayName(prod.name || 'Producto');
  const productBrand = String(prod.brand || '').trim();
  const shouldShowBrand = Boolean(showBrand && productBrand);
  const shouldAbbreviateName = abbreviateProductNames !== false;
  const cardHeightClass = showImageSlot
    ? (shouldAbbreviateName ? 'h-24 lg:h-52' : 'h-auto min-h-24 lg:min-h-52')
    : (shouldAbbreviateName ? 'h-24 lg:h-32' : 'h-auto min-h-24 lg:min-h-32');
  const buttonTitle = `${shouldShowBrand ? `${productBrand} - ` : ''}${prod.name} - $ ${price.toLocaleString('es-AR')}${showStockBadge ? ` - Stock: ${stockActual}` : ''}`;

  return (
    <button
      onClick={() => onAdd(prod)}
      data-onboarding-id={dataOnboardingId}
      className={`bg-slate-50 rounded-[1.5rem] p-0 border border-slate-100 hover:border-[#51A7FD] hover:shadow-md transition-all flex flex-col ${cardHeightClass} group relative text-left overflow-hidden`}
      title={`${prod.name} · $ ${price.toLocaleString('es-AR')}${showStockBadge ? ` · Stock: ${stockActual}` : ''}`}
      {...{ title: buttonTitle }}
    >
      {showImageSlot ? <div className="hidden lg:flex h-28 w-full bg-white items-center justify-center overflow-hidden border-b border-slate-100">
        {/* Condición: Si showImages es true Y tiene imagen, renderiza <img>, si no, el ícono */}
        {hasImage ? (
          <img
            src={prod.image_url}
            alt={displayName}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 bg-slate-100"
          />
        ) : (
          <Package size={32} className="text-slate-200" />
        )}
      </div> : null}

      <div className="p-2 lg:p-3 flex flex-col flex-1 justify-between">
        {shouldShowBrand ? (
          <p className="text-[8px] font-black uppercase tracking-[0.18em] text-slate-400 line-clamp-1 mb-1">
            {productBrand}
          </p>
        ) : null}

        <h3 className={`font-black text-slate-800 uppercase text-[10px] leading-tight mb-2 ${shouldAbbreviateName ? 'line-clamp-2' : 'break-words whitespace-normal'}`}>
          {displayName}
        </h3>

        <div className="flex justify-between items-end w-full mt-auto">
          <div>
            {showStockBadge && (
              <p className={`text-[8px] font-black uppercase tracking-tighter ${stockActual > 0 ? 'text-emerald-500' : 'text-rose-400'}`}>
                {stockActual} EN STOCK
              </p>
            )}
            <p className="text-[#51A7FD] font-black text-[12px] leading-none">$ {price.toLocaleString('es-AR')}</p>
          </div>
          <div className="hidden lg:block p-1.5 bg-blue-50 text-[#51A7FD] rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-sm">
            <Plus size={12} strokeWidth={3} />
          </div>
        </div>
      </div>
    </button>
  );
});

export default function VentasPOS({ onNavigate } = {}) {
  const { profile, user, organization, refreshOrganization } = useAuth();
  const { selectedSiteId } = useSite();
  // ✅ NUEVO offline: el POS sigue mostrando catálogo cacheado, pero NO permitimos
  // confirmar una venta sin red porque no hay garantía de descontar stock ni de
  // generar el comprobante AFIP. Avisamos antes de procesar.
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [comprobantes, setComprobantes] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [priceLists, setPriceLists] = useState([]);

  const [combos, setCombos] = useState([]);
  const [comboItems, setComboItems] = useState([]);
  // === Combos Dinámicos ===
  const [enableDynamicCombos, setEnableDynamicCombos] = useState(false);

  // === Descuento por producto (Empresa → Datos → Punto de venta) ===
  // Ver utils/descuentoItem.js. La config se toma de la organización del
  // AuthContext y se revalida contra la base al confirmar la venta.
  const itemDiscountConfig = useMemo(() => normalizarConfigDescuentoItem(organization), [organization]);
  const [posEffectiveModules, setPosEffectiveModules] = useState(null);
  const puedeAutorizarDescuento = canAutorizarDescuentoItem({
    profile,
    isSuperAdmin: String(user?.email || '').toLowerCase() === 'adpenia@gmail.com',
    effectiveModules: posEffectiveModules,
  });
  // { cartKey, modo: 'pct' | 'precio', valor, motivo, motivoOtro, email, password, enviando }
  const [descItemModal, setDescItemModal] = useState(null);

  useEffect(() => {
    if (!itemDiscountConfig.enabled || !profile?.organization_id || !profile?.id) return undefined;
    let cancelled = false;
    fetchEffectiveModulePermissions({
      supabase,
      organizationId: profile.organization_id,
      userId: profile.id,
      roleName: profile.role,
    })
      .then(({ effective }) => { if (!cancelled) setPosEffectiveModules(effective); })
      .catch((e) => {
        console.warn('[POS] No se pudieron resolver los permisos de descuento:', e?.message || e);
        if (!cancelled) setPosEffectiveModules(null);
      });
    return () => { cancelled = true; };
  }, [itemDiscountConfig.enabled, profile?.organization_id, profile?.id, profile?.role]);
  const [comboSteps, setComboSteps] = useState([]); // [{id, combo_id, step_order, step_name, quantity}]
  const [comboStepOptions, setComboStepOptions] = useState([]); // [{id, step_id, product_id}]
  const [dynamicComboModal, setDynamicComboModal] = useState({ open: false, prod: null, steps: [] });
  // Estado interno del modal: selecciones por paso { [step_id]: [{product_id, name, cost_price, qty}] }
  const [dynamicSelections, setDynamicSelections] = useState({});

  const [turnoCajaId, setTurnoCajaId] = useState(null);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [showNegativeStock, setShowNegativeStock] = useState(false);
  const [enableMiscProduct, setEnableMiscProduct] = useState(false);
  const [enableVariants, setEnableVariants] = useState(false);

  // NUEVO: Estado para controlar si se muestran o no las imágenes
  const [showPosImages, setShowPosImages] = useState(true);
  const [posUiConfig, setPosUiConfig] = useState(DEFAULT_POS_UI_CONFIG);
  const [isSaleContextCollapsed, setIsSaleContextCollapsed] = useState(false);

  const [empresa, setEmpresa] = useState(null);
  const [printConfig, setPrintConfig] = useState(DEFAULT_PRINT_CONFIG);

  const searchInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [catSeleccionada, setCatSeleccionada] = useState('Todos');
  const [showCatMenu, setShowCatMenu] = useState(false);

  // States for automatic invoicing configuration
  const [autoInvoiceEnabled, setAutoInvoiceEnabled] = useState(false);
  const [defaultBillingEntityId, setDefaultBillingEntityId] = useState(null);
  const [billingEntities, setBillingEntities] = useState([]);

  const getLocalToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = getLocalToday();

  const [fecha, setFecha] = useState(today);
  const [clienteId, setClienteId] = useState('');
  const [sitioId, setSitioId] = useState('');
  const [comprobanteId, setComprobanteId] = useState('');
  const [metodoPagoId, setMetodoPagoId] = useState('');
  const [salePaymentConditionDays, setSalePaymentConditionDays] = useState(0);
  const [salePaymentConditionTouched, setSalePaymentConditionTouched] = useState(false);
  // Cliente identificado: antes de cobrar se pregunta si la venta se cobra en el
  // momento o queda en cuenta corriente. Consumidor final no pasa por acá.
  const [cobroClienteModal, setCobroClienteModal] = useState(false);

  const [docMode, setDocMode] = useState('factura');
  const [presupuestoVenceEnDias, setPresupuestoVenceEnDias] = useState(7);

  const [carrito, setCarrito] = useState([]);
  const [cartQuantityDrafts, setCartQuantityDrafts] = useState({});
  const [procesando, setProcesando] = useState(false);
  const [animacionExito, setAnimacionExito] = useState(false);

  const [descPct, setDescPct] = useState(0);
  const [recPct, setRecPct] = useState(0);
  const [esperandoPagoQR, setEsperandoPagoQR] = useState(false);
  // 'QR' (el cliente escanea) | 'TERMINAL' (el cliente pasa la tarjeta por el
  // posnet). El mismo modal sirve para los dos: cambia el copy y el ícono.
  const [modoEspera, setModoEspera] = useState('QR');
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [cancelarQRPromise, setCancelarQRPromise] = useState(null);

  // Estados de Impresión
  const [printModal, setPrintModal] = useState({ open: false, html: '', autoPrint: false, afterCloseReset: false });
  const iframeRef = useRef(null);
  // Id del setTimeout de auto-impresion del modal. Se guarda para poder
  // cancelarlo si el modal se cierra / arranca una venta nueva antes de que
  // dispare: sin eso print() corre sobre un iframe ya desmontado y Chrome
  // rechaza asincronicamente con "The provided callback is no longer runnable"
  // (ref IMPULZA-6), que ningun try/catch sincrono atrapa.
  const printTimeoutRef = useRef(null);

  // Cancela la auto-impresion pendiente cuando el modal se cierra o el
  // componente se desmonta: el iframe deja de existir y print() sobre el
  // contentWindow destruido tira una DOMException asincrona (IMPULZA-6).
  useEffect(() => {
    if (printModal.open) return undefined;
    if (printTimeoutRef.current) {
      clearTimeout(printTimeoutRef.current);
      printTimeoutRef.current = null;
    }
    return undefined;
  }, [printModal.open]);

  useEffect(() => () => {
    if (printTimeoutRef.current) {
      clearTimeout(printTimeoutRef.current);
      printTimeoutRef.current = null;
    }
  }, []);

  // NUEVO CÓDIGO: Modal Inteligente Post-Venta
  const [smartModal, setSmartModal] = useState({
    open: false,
    htmlInterno: '',
    saleId: '',
    docMode: '',
    invoiceStatus: 'pendiente',
    cae: null,
    numeroFactura: null,
    caeVto: null,
    saleData: null
  });

  const [silentPolling, setSilentPolling] = useState({
    active: false,
    saleId: null,
    docMode: '',
    saleData: null
  });

  // Modal de selección de variante
  const [variantModal, setVariantModal] = useState({ open: false, prod: null });
  // Talles y colores: selector color → talle con stock por sucursal
  const [tallesModal, setTallesModal] = useState({ open: false, prod: null });
  const [firstSaleProductId, setFirstSaleProductId] = useState(null);
  // Producto del recorrido guiado (mock): {id,name,costPrice,sellPrice}. Cuando
  // esta seteado, el POS lo inyecta como tarjeta sintetica y simula su venta.
  const [firstSaleProduct, setFirstSaleProduct] = useState(null);

  const clienteSeleccionado = useMemo(
    () => clientes.find((client) => client.id === clienteId) || null,
    [clientes, clienteId]
  );

  const sitioSeleccionado = useMemo(
    () => sitios.find((site) => site.id === sitioId) || null,
    [sitios, sitioId]
  );

  // Métodos que REALMENTE se pueden cobrar acá y ahora.
  //
  // Antes se listaban todos: el cajero elegía "Mercado Pago", la venta se
  // procesaba a medias y recién al confirmar fallaba con un error largo porque
  // la sucursal no tenía cargado el ID de caja. Con Point el problema es peor
  // (hace falta la terminal física de ESA sucursal) y encima las pasarelas
  // necesitan conexión: el POS es offline-first, pero un cobro con terminal no
  // lo es. Un método que no se puede cobrar no se ofrece.
  const metodosDisponibles = useMemo(
    () => metodosPago.filter((m) => isPaymentMethodAvailable(m, { sitio: sitioSeleccionado, isOnline })),
    [metodosPago, sitioSeleccionado, isOnline]
  );

  // ✅ Resolución de lista de precios para el POS:
  //    - Si la empresa tiene activado "Lista de precios por sitio (POS)", se usa
  //      la lista asignada al sitio actual (fallback: precio base si no hay).
  //    - Si NO está activado, se respeta el comportamiento histórico: la lista
  //      del cliente seleccionado.
  const activePriceList = useMemo(() => {
    if (empresa?.use_pos_price_list_per_site) {
      return resolvePosPriceListForSite({
        organization: empresa,
        site: sitioSeleccionado,
        priceLists,
      });
    }
    return priceLists.find((priceList) => priceList.id === clienteSeleccionado?.price_list_id) || null;
  }, [empresa, sitioSeleccionado, priceLists, clienteSeleccionado?.price_list_id]);

  // NUEVO CÓDIGO: Helper para generar QR AFIP
  // Emisor de la venta: el que quedó en la venta (o el que devolvió ARCA), no el
  // CUIT de la organización — en empresas con varios emisores el QR apuntaba a
  // un comprobante ajeno.
  const findTicketBillingEntity = (billingEntityId) =>
    (billingEntities || []).find((e) => e.id === billingEntityId) || null;

  const generarQrBase64Afip = async (saleData, afipData) => {
    if (!afipData?.cae) return null;
    try {
      const billingEntity = findTicketBillingEntity(afipData?.billing_entity_id || saleData?.billingEntityId);
      const url = buildAfipQrUrl({
        sale: {
          cae: afipData.cae,
          numero_factura: afipData.numero_factura,
          invoice_cbte_tipo: afipData.invoice_cbte_tipo || afipData.cbte_tipo,
          invoice_sent_at: afipData.invoice_sent_at,
          fecha_venta: saleData?.fechaStr,
          total: saleData?.total,
        },
        emitterCuit: billingEntity?.cuit || empresa?.cuit,
        client: saleData?.cliente || null,
        ptoVentaFallback: billingEntity?.afip_pto_venta,
      });
      if (!url) return null;
      return await QRCode.toDataURL(url, { margin: 1, width: 300, errorCorrectionLevel: 'M' });
    } catch (e) {
      console.warn("Error generando QR AFIP:", e);
      return null;
    }
  };

  useEffect(() => {
    let interval;
    if (silentPolling.active && silentPolling.saleId && silentPolling.docMode === 'factura') {
      interval = setInterval(async () => {
        try {
          const { data, error } = await supabase.from('sales')
            .select('invoice_status, cae, numero_factura, cae_vencimiento, invoice_sent_at, invoice_cbte_tipo, billing_entity_id')
            .eq('id', silentPolling.saleId)
            .maybeSingle();

          if (!error && data && !['pendiente', 'enviado'].includes(data.invoice_status)) {
            setSilentPolling({ active: false, saleId: null, docMode: '', saleData: null });

            if (data.invoice_status === 'facturado' && silentPolling.saleData) {
              const base64QR = await generarQrBase64Afip(silentPolling.saleData, data);
              const finalHtml = buildTicketHtml({
                ...silentPolling.saleData,
                cae: data.cae,
                caeVto: data.cae_vencimiento,
                numeroFactura: data.numero_factura,
                fechaFacturacion: data.invoice_sent_at ? String(data.invoice_sent_at).split('T')[0] : null,
                invoiceSentAt: data.invoice_sent_at,
                invoiceCbteTipo: data.invoice_cbte_tipo,
                billingEntityId: data.billing_entity_id || silentPolling.saleData.billingEntityId,
                qrBase64: base64QR
              });
              setTimeout(() => { openPresupuestoPrint(finalHtml, true); }, 600);
            } else if (data.invoice_status === 'error') {
              toast.error('Error auto-facturando en 2do plano. Revise módulo Facturación.');
            }
          }
        } catch (e) {
          console.error('Error al sondear status silencioso:', e);
        }
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [silentPolling.active, silentPolling.saleId, silentPolling.docMode, silentPolling.saleData]);

  useEffect(() => {
    let interval;
    if (smartModal.open && ['pendiente', 'enviado'].includes(smartModal.invoiceStatus) && smartModal.docMode === 'factura') {
      interval = setInterval(async () => {
        try {
          const { data, error } = await supabase.from('sales')
            .select('invoice_status, cae, numero_factura, cae_vencimiento, invoice_sent_at, invoice_cbte_tipo, billing_entity_id')
            .eq('id', smartModal.saleId)
            .maybeSingle();

          if (!error && data && data.invoice_status !== smartModal.invoiceStatus) {
            setSmartModal(prev => ({
              ...prev,
              invoiceStatus: data.invoice_status,
              cae: data.cae,
              numeroFactura: data.numero_factura,
              caeVto: data.cae_vencimiento,
              fechaFacturacion: data.invoice_sent_at ? String(data.invoice_sent_at).split('T')[0] : null,
              invoiceSentAt: data.invoice_sent_at,
              invoiceCbteTipo: data.invoice_cbte_tipo,
              billingEntityId: data.billing_entity_id || prev.billingEntityId
            }));
          }
        } catch (e) {
          console.error('Error al sondear status:', e);
        }
      }, 2500); // Polling cada 2.5s
    }
    return () => clearInterval(interval);
  }, [smartModal.open, smartModal.invoiceStatus, smartModal.docMode, smartModal.saleId, smartModal.saleData]);

  const getForgedPOSDate = (dateInputValue) => {
    const now = new Date();
    const datePart = dateInputValue || getLocalToday();
    const [y, m, d] = datePart.split('-').map(Number);
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${hh}:${mm}:${ss}.000Z`;
  };

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const currency = (n) =>
    (Number(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalFinal = useMemo(() => {
    return carrito.reduce((acc, item) => {
      const itemSubtotal = Number(item.subtotal) || 0;
      let factorDesc = 1;
      if (Number(descPct) > 0 && !item.block_discounts) {
        factorDesc = 1 - (Number(descPct) / 100);
      }
      let factorRec = 1;
      if (Number(recPct) > 0 && !item.block_surcharges) {
        factorRec = 1 + (Number(recPct) / 100);
      }
      const itemFinal = itemSubtotal * factorDesc * factorRec;
      return acc + itemFinal;
    }, 0);
  }, [carrito, descPct, recPct]);

  const decimalCartQuantityEnabled = Boolean(posUiConfig.allowDecimalCartQuantity);

  const normalizeCartQuantityValue = useCallback((value) => {
    if (decimalCartQuantityEnabled) {
      const numericValue = Number(value);
      const precisionFactor = 10 ** DECIMAL_CART_QUANTITY_DECIMALS;
      const minQuantity = 1 / precisionFactor;
      if (!Number.isFinite(numericValue)) return minQuantity;
      return Math.max(minQuantity, Math.round(numericValue * precisionFactor) / precisionFactor);
    }

    return Math.max(1, Math.floor(Number(value) || 0));
  }, [decimalCartQuantityEnabled]);

  const formatCartQuantityValue = useCallback((value) => (
    formatLocalizedNumberInputValue(value, {
      decimals: decimalCartQuantityEnabled ? DECIMAL_CART_QUANTITY_DECIMALS : 0,
    })
  ), [decimalCartQuantityEnabled]);




  const normalizeFiscal = (txt) => String(txt || '').toLowerCase();

  const inferEmisorFiscal = (billingEntity) => {
    const raw =
      billingEntity?.fiscal_configuration ??
      billingEntity?.tax_condition ??
      empresa?.tax_condition ??
      'Responsable Inscripto';
    return normalizeFiscal(raw);
  };

  const inferClienteFiscal = (cliente) => normalizeFiscal(cliente?.tax_condition || 'Consumidor Final');

  const suggestedInvoiceName = (emisorFiscal, clienteFiscal) => {
    const e = emisorFiscal;
    const c = clienteFiscal;

    const emisorMono = e.includes('mono');
    const emisorRI = e.includes('inscrip') || e.includes('ri');
    const clienteRI = c.includes('inscrip') || c.includes('ri');
    const clienteCF = c.includes('consum') || c.includes('final') || c.includes('cf');

    if (emisorMono) return 'Factura C';
    if (emisorRI && clienteRI) return 'Factura A';
    if (emisorRI && clienteCF) return 'Factura B';

    return 'Factura B';
  };

  const pickComprobanteByName = (nameLike) => {
    const n = String(nameLike || '').toLowerCase();
    const exact = (comprobantes || []).find((x) => String(x?.name || '').toLowerCase() === n);
    if (exact) return exact.id;
    const contains = (comprobantes || []).find((x) => String(x?.name || '').toLowerCase().includes(n));
    return contains?.id || '';
  };

  const applySuggestedComprobante = ({ force = false } = {}) => {
    if (!comprobantes?.length) return;

    const cliente = clientes.find((c) => c.id === clienteId) || null;

    const emisorFiscal = inferEmisorFiscal(null);
    const clienteFiscal = inferClienteFiscal(cliente);

    const sugName = suggestedInvoiceName(emisorFiscal, clienteFiscal);
    const sugId = pickComprobanteByName(sugName);

    if (!sugId) return;

    if (!comprobanteId || force) {
      setComprobanteId(sugId);
    }
  };

  useEffect(() => {
    if (profile?.organization_id) fetchDatos();
  }, [profile?.organization_id, selectedSiteId]);

  useEffect(() => {
    if (!profile?.organization_id) return;

    const firstSaleOnboarding = readFirstSaleOnboarding();
    const isFirstSaleFlowActive =
      matchesFirstSaleOnboardingIdentity(firstSaleOnboarding, {
        organizationId: profile.organization_id,
        userId: profile?.id || null,
      }) &&
      firstSaleOnboarding?.stepId === 'sell-product';

    if (!isFirstSaleFlowActive) {
      setFirstSaleProductId(null);
      setFirstSaleProduct(null);
      return;
    }

    const nextProductId = firstSaleOnboarding?.product?.id || null;
    setFirstSaleProductId(nextProductId);
    setFirstSaleProduct(firstSaleOnboarding?.product || null);

    if (!busqueda && firstSaleOnboarding?.product?.name) {
      setBusqueda(firstSaleOnboarding.product.name);
    }

    if (!metodoPagoId && metodosDisponibles.length > 0) {
      const preferredMethod = metodosDisponibles.find((method) => {
        const normalizedName = String(method?.name || '').toLowerCase();
        return normalizedName.includes('efectivo') || normalizedName.includes('transferencia');
      }) || metodosDisponibles[0];

      if (preferredMethod?.id) {
        handleMetodoPagoChange(preferredMethod.id);
      }
    }
  }, [profile?.organization_id, profile?.id, metodosDisponibles, metodoPagoId, busqueda]);

  // Si el método elegido dejó de estar disponible (cambió la sucursal, o se cayó
  // la red con una pasarela seleccionada), lo soltamos: dejarlo seleccionado
  // pero fuera de la lista hace que el POS intente cobrar por un camino que ya
  // no existe.
  useEffect(() => {
    if (!metodoPagoId) return;
    if (metodosPago.length === 0) return;
    if (metodosDisponibles.some((m) => m.id === metodoPagoId)) return;
    handleMetodoPagoChange('');
    toast.error('El método de cobro no está disponible en esta sucursal o sin conexión.');
  }, [metodoPagoId, metodosDisponibles, metodosPago.length]);

  useEffect(() => {
    applySuggestedComprobante({ force: false });
  }, [clienteId, totalFinal, comprobantes.length, clientes.length]);

  useEffect(() => {
    if (salePaymentConditionTouched) return;
    const selectedClient = clientes.find((client) => client.id === clienteId) || null;
    setSalePaymentConditionDays(getClientPaymentConditionDays(selectedClient));
  }, [clientes, clienteId, salePaymentConditionTouched]);

  useEffect(() => {
    const contextSiteId = sitios.some((site) => site.id === selectedSiteId)
      ? selectedSiteId
      : '';
    if (sitioId === contextSiteId) return;
    setSitioId(contextSiteId);
  }, [selectedSiteId, sitios, sitioId]);

  useEffect(() => {
    setIsSaleContextCollapsed(Boolean(posUiConfig.startCollapsedSaleContext));
  }, [posUiConfig.startCollapsedSaleContext]);

  async function fetchDatos() {
    setLoading(true);
    try {
      const orgId = profile.organization_id;
      await processPendingBankSettlements(supabase, orgId);

      // resOrg sale del AuthContext — antes era un select('*') que se hacía
      // en cada página, ahora se carga una sola vez por sesión (ref IMPULZA-4).
      const resOrg = { data: organization };
      const [resClis, resSit, resComp, resMet, resCat, resCombos, resComboItems, resEntities, resPriceLists, resComboSteps, resComboStepOpts] = await Promise.all([
        supabase.from('clients').select('*').eq('organization_id', orgId).eq('is_active', true),
        supabase.from('sitios').select('*').eq('organization_id', orgId).eq('is_active', true),
        supabase.from('invoice_types').select('*').eq('organization_id', orgId).eq('is_active', true),
        supabase.from('payment_methods').select('*').eq('organization_id', orgId).eq('is_active', true),
        supabase.from('product_categories').select('*').eq('organization_id', orgId).eq('is_active', true),
        supabase.from('product_combos').select('*').eq('organization_id', orgId),
        supabase.from('product_combo_items').select('*').eq('organization_id', orgId),
        supabase.from('billing_entities').select('id,tax_name,cuit,tax_condition,fiscal_configuration,afip_pto_venta,iibb,inicio_actividades,domicilio_comercial').eq('organization_id', orgId),
        supabase.from('price_lists').select('*').eq('organization_id', orgId).eq('is_active', true),
        supabase.from('product_combo_steps').select('*').eq('organization_id', orgId),
        supabase.from('product_combo_step_options').select('*').eq('organization_id', orgId)
      ]);

      const defaultSiteId = resolvePreferredStockSiteId(resOrg?.data, resSit.data || []);
      const legacyStockResult = await hydrateLegacyVariantStock({
        supabase,
        organizationId: orgId,
        defaultSiteId,
      });

      // ============================================================
      // FILTRO DE TIPOS NO VENDIBLES EN EL POS
      // ============================================================
      // Antes usábamos `.in(['Producto Terminado','Combo'])` esperando que
      // sea exhaustivo, pero registros con casing distinto, espacios sobrantes
      // o tipos custom (p.ej. "Servicio") quedaban afuera o pasaban erróneos.
      // Ahora invertimos la lógica: excluimos EXPLÍCITAMENTE los tipos
      // cost-only (Materia Prima / Insumo) — todo lo demás (Producto
      // Terminado, Combo, Servicio, NULL, etc.) puede venderse en el POS.
      // Replica el mismo criterio que la Tienda virtual para mantener
      // coherencia entre ambos canales.
      // Talles y colores: el catálogo trae además colores y stock por talle.
      // Si esa consulta falla (p.ej. esquema de talles todavía no aplicado en
      // la base de este deploy), se reintenta con la consulta de siempre: el
      // POS nunca debe quedar sin catálogo por esta funcionalidad.
      const POS_PRODUCT_SELECT_BASE = 'id,name,brand,barcode,image_url,sell_price,price_usd,category,category_id,product_type,is_dynamic_combo,active,block_discounts,block_surcharges,iva_percentage,stock_levels(*)';
      const fetchPosProducts = (variantSelect) => fetchAllPaginated(() =>
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select(`${POS_PRODUCT_SELECT_BASE},${variantSelect}`)
          .eq('organization_id', orgId)
        )
          .not('product_type', 'in', '("Materia Prima","Insumo","Insumo / Materia Prima")')
          .order('name', { ascending: true })
      );
      let productosData;
      try {
        productosData = await fetchPosProducts('stock_tracking,product_variants(*,colores(id,nombre,hex,codigo_corto)),stock_variantes(variant_id,site_id,quantity)');
      } catch (variantSelectError) {
        console.error('POS: catálogo con talles no disponible, uso el catálogo clásico:', variantSelectError);
        productosData = await fetchPosProducts('product_variants(*)');
      }

      // Defensa en profundidad: si el filtro de DB dejó pasar algún registro
      // con casing distinto, lo cortamos acá normalizando el string.
      const COST_ONLY_TYPES_POS = new Set([
        'materia prima',
        'insumo',
        'insumo / materia prima',
      ]);
      const filteredProductos = (productosData || []).filter((p) => {
        const normalizedType = String(p?.product_type || '').trim().toLowerCase();
        return !COST_ONLY_TYPES_POS.has(normalizedType);
      }).map((p) => (
        // Talles y colores: las variantes archivadas (p.ej. la "Base" de un
        // producto convertido) no se venden ni se muestran.
        Array.isArray(p.product_variants) && p.product_variants.some((v) => v?.archivado_at)
          ? { ...p, product_variants: p.product_variants.filter((v) => !v?.archivado_at) }
          : p
      ));

      setProductos(filteredProductos);
      setClientes(resClis.data || []);
      setSitios(resSit.data || []);
      setComprobantes(resComp.data || []);
      setMetodosPago(resMet.data || []);
      setCategorias(resCat.data || []);
      setPriceLists(resPriceLists.data || []);
      setEmpresa(resOrg?.data || null);

      setAutoInvoiceEnabled(resOrg?.data?.auto_invoice_enabled ?? false);
      setDefaultBillingEntityId(resOrg?.data?.default_billing_entity_id ?? null);
      setBillingEntities(resEntities?.data || []);

      setCombos(resCombos?.data || []);
      setComboItems(resComboItems?.data || []);
      setComboSteps(resComboSteps?.data || []);
      setComboStepOptions(resComboStepOpts?.data || []);
      setEnableDynamicCombos(resOrg?.data?.enable_dynamic_combos ?? false);

      setAllowNegativeStock(resOrg?.data?.allow_negative_stock ?? false);
      setShowNegativeStock(resOrg?.data?.show_pos_negative_stock ?? false);
      setEnableMiscProduct(resOrg?.data?.enable_misc_product ?? false);
      setEnableVariants(resOrg?.data?.enable_variants ?? false);

      // NUEVO: Lee el parámetro show_pos_images, si no existe o es nulo asume que es true
      setShowPosImages(resOrg?.data?.show_pos_images ?? true);
      setPosUiConfig(getPosUiConfigFromPrintConfig(resOrg?.data?.print_config));
      setIsSaleContextCollapsed(Boolean(getPosUiConfigFromPrintConfig(resOrg?.data?.print_config).startCollapsedSaleContext));

      if (resOrg?.data?.print_config) {
        setPrintConfig(prev => ({
          ...prev,
          ...resOrg.data.print_config
        }));
      }

      const consFinal = (resClis.data || []).find((c) => c?.full_name?.toLowerCase() === 'consumidor final');
      const availableSites = resSit.data || [];
      const contextSiteId = availableSites.some((site) => site.id === selectedSiteId)
        ? selectedSiteId
        : '';
      const nextSiteId = contextSiteId || (availableSites.length === 1 ? availableSites[0]?.id || '' : '');

      if (nextSiteId) {
        const { data: turnoAbierto, error: turnoError } = await supabase
          .from('turnos_caja')
          .select('id')
          .eq('organization_id', orgId)
          .eq('estado', 'Abierto')
          .eq('sitio_id', nextSiteId)
          .maybeSingle();

        if (turnoError) throw turnoError;

        if (turnoAbierto?.id) {
          setTurnoCajaId(turnoAbierto.id);
        } else {
          setTurnoCajaId(null);
        }
      } else {
        setTurnoCajaId(null);
      }

      if (consFinal) setClienteId(consFinal.id);
      setSitioId(nextSiteId);

      if (legacyStockResult.inserted > 0) {
        toast.success(`Se recuperó el stock importado en ${legacyStockResult.inserted} productos.`);
      }

      setTimeout(() => {
        applySuggestedComprobante({ force: true });
      }, 0);

      if (!comprobanteId && (resComp.data || []).length > 0) setComprobanteId(resComp.data[0].id);

      setTimeout(() => searchInputRef.current?.focus(), 120);
    } catch (e) {
      console.error(e);
      toast.error('Error de sincronización con la central.');
    } finally {
      setLoading(false);
    }
  }

  const categoriasOrdenadas = useMemo(() => {
    return [...categorias].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [categorias]);

  const categoriaKey = useMemo(() => {
    const catObj = categorias.find((c) => c.name === catSeleccionada || c.id === catSeleccionada);
    return catObj?.id ? 'category_id' : 'category';
  }, [catSeleccionada, categorias]);

  const abbreviateProductNames = posUiConfig.abbreviateProductNames !== false;

  // Talles y colores: "remera negra m" encuentra el producto si alguna variante
  // activa cubre todas las palabras (nombre + color + talle + SKU/barcode).
  const variantSearchMatch = (p, q) => {
    const tokens = q.split(/\s+/).filter(Boolean);
    if (!tokens.length) return false;
    const base = `${p.name || ''} ${p.brand || ''}`.toLowerCase();
    return (p.product_variants || []).some((v) => {
      if (!isVariantActive(v)) return false;
      const hay = `${base} ${getVariantName(v)} ${v.sku || ''} ${v.barcode || ''}`.toLowerCase();
      return tokens.every((t) => hay.split(/[\s/]+/).some((w) => w === t || w.startsWith(t)) || hay.includes(t));
    });
  };

  const filtrados = useMemo(() => {
    const q = (busqueda || '').trim().toLowerCase();

    const visibleProducts = productos.filter(p => ['Producto Terminado', 'Combo'].includes(p.product_type));

    return visibleProducts.filter((p) => {
      const matchesText = !q
        ? true
        : (p.name || '').toLowerCase().includes(q) ||
          (p.brand || '').toLowerCase().includes(q) ||
          (p.barcode || '').toLowerCase() === q ||
          (isVariantTracked(p) && variantSearchMatch(p, q));

      const matchesCat =
        catSeleccionada === 'Todos'
          ? true
          : categoriaKey === 'category_id'
            ? (p.category_id || '') === (categorias.find((c) => c.name === catSeleccionada)?.id || catSeleccionada)
            : (p.category || '') === catSeleccionada;

      // ✅ Visibilidad por lista activa: si está oculto en la lista del sitio
      //    (o del cliente, según la regla), no aparece en el catálogo del POS.
      const matchesPriceListVisibility = isProductVisibleInPriceList(p.id, activePriceList);

      return matchesText && matchesCat && matchesPriceListVisibility;
    });
  }, [productos, busqueda, catSeleccionada, categoriaKey, categorias, activePriceList]);

  // === DEMO GUIADO = MOCK: producto sintetico en el catalogo del POS ===
  // El producto creado durante el recorrido NO existe en la base (fue simulado).
  // Lo materializamos como una tarjeta sintetica con stock alto para que el
  // usuario pueda "venderlo" sin tocar datos reales. La venta tambien se intercepta
  // (ver finalizarVenta) para no escribir nada.
  const demoCatalogProduct = useMemo(() => {
    if (!firstSaleProduct?.id) return null;
    const sellPrice = Number(firstSaleProduct.sellPrice || 0);
    const costPrice = Number(firstSaleProduct.costPrice || 0);
    return {
      id: firstSaleProduct.id,
      name: firstSaleProduct.name || 'Producto demo',
      brand: '',
      barcode: null,
      image_url: null,
      sell_price: sellPrice,
      price_usd: 0,
      priced_in_usd: false,
      cost_price: costPrice,
      category: null,
      category_id: null,
      product_type: 'Producto Terminado',
      is_dynamic_combo: false,
      active: true,
      block_discounts: false,
      block_surcharges: false,
      __demoMock: true,
      // Variante "Base" generica: getVariante la devuelve (evita el aviso de "$0")
      // y getMeaningfulVariants la filtra (no abre el modal de variantes).
      product_variants: [{
        id: `${firstSaleProduct.id}-base`,
        attributes: { variant_name: 'Base', variant_active: true },
        cost: costPrice,
        sell_price: sellPrice,
      }],
      // Stock sintetico alto en el sitio activo para que la tarjeta muestre stock.
      stock_levels: [{
        id: `${firstSaleProduct.id}-stock`,
        site_id: sitioId || null,
        quantity: 999,
        min_stock: 0,
      }],
    };
  }, [firstSaleProduct, sitioId]);

  const filtradosConDemo = useMemo(() => {
    if (!demoCatalogProduct) return filtrados;
    return [demoCatalogProduct, ...filtrados.filter((p) => p.id !== demoCatalogProduct.id)];
  }, [filtrados, demoCatalogProduct]);

  const fechaResumen = useMemo(() => {
    if (!fecha) return '-';

    const [year, month, day] = String(fecha).split('-');
    if (!year || !month || !day) return fecha;

    return `${day}/${month}/${year}`;
  }, [fecha]);

  // Con varias sucursales y ninguna resuelta, getProductStockForSite(prod, null)
  // devuelve la SUMA de todas: el POS mostraba stock que está en otro local y
  // dejaba vender contra él. En ese caso el stock es desconocido, no la suma.
  const stockSiteIsAmbiguous = useCallback(
    (site) => !site && sitios.length > 1,
    [sitios.length],
  );

  const getStock = useCallback((prod, site) => {
    if (!prod) return 0;
    if (stockSiteIsAmbiguous(site)) return 0;

    if (prod.product_type !== 'Combo') {
      return getProductStockForSite(prod, site || null);
    }

    const comboDef = combos.find(c => c.product_id === prod.id);
    if (!comboDef) return 0;

    const items = comboItems.filter(i => i.combo_id === comboDef.id);
    if (items.length === 0) return 0;

    let maxPossible = Infinity;

    for (const item of items) {
      const ingredient = productos.find(p => p.id === item.product_id);
      if (!ingredient) return 0;

      const ingredientStock = getProductStockForSite(ingredient, site || null);
      const neededPerUnit = Number(item.quantity) || 1;

      if (neededPerUnit <= 0) continue;

      const possible = Math.floor(ingredientStock / neededPerUnit);
      if (possible < maxPossible) {
        maxPossible = possible;
      }
    }

    return maxPossible === Infinity ? 0 : maxPossible;
  }, [combos, comboItems, productos, stockSiteIsAmbiguous]);

  // Talles y colores: refleja en pantalla el stock de una variante y el total
  // del producto en la sucursal (que en la base mantiene un trigger).
  const syncVariantStockInState = useCallback(({
    productId,
    variantId,
    siteId: targetSiteId,
    quantity,
    organizationId = null,
  }) => {
    if (!Number.isFinite(Number(quantity))) return;
    setProductos((currentProducts) => currentProducts.map((product) => {
      if (product.id !== productId) return product;
      const rows = Array.isArray(product.stock_variantes) ? product.stock_variantes : [];
      const isTarget = (row) => String(row?.variant_id) === String(variantId) && String(row?.site_id) === String(targetSiteId);
      const nextRows = rows.some(isTarget)
        ? rows.map((row) => (isTarget(row) ? { ...row, quantity: Number(quantity) } : row))
        : [...rows, { variant_id: variantId, site_id: targetSiteId, quantity: Number(quantity) }];
      const total = nextRows
        .filter((row) => String(row?.site_id) === String(targetSiteId))
        .reduce((acc, row) => acc + Number(row?.quantity || 0), 0);
      const levels = Array.isArray(product.stock_levels) ? product.stock_levels : [];
      const hasLevel = levels.some((level) => String(level?.site_id) === String(targetSiteId));
      const nextLevels = hasLevel
        ? levels.map((level) => (String(level?.site_id) === String(targetSiteId) ? { ...level, quantity: total } : level))
        : [...levels, { organization_id: organizationId, product_id: productId, site_id: targetSiteId, quantity: total }];
      return { ...product, stock_variantes: nextRows, stock_levels: nextLevels };
    }));
  }, []);

  const syncProductStockLevelInState = useCallback(({
    productId,
    siteId: targetSiteId,
    quantity,
    rowId = null,
    remove = false,
    organizationId = null,
  }) => {
    setProductos((currentProducts) => currentProducts.map((product) => {
      if (product.id !== productId) return product;

      const currentLevels = Array.isArray(product.stock_levels) ? product.stock_levels : [];
      const remainingLevels = currentLevels.filter(
        (level) => String(level?.site_id || '') !== String(targetSiteId || ''),
      );

      if (remove) {
        return {
          ...product,
          stock_levels: remainingLevels,
        };
      }

      const existingLevel = currentLevels.find(
        (level) => String(level?.site_id || '') === String(targetSiteId || ''),
      );

      return {
        ...product,
        stock_levels: [
          ...remainingLevels,
          {
            ...(existingLevel || {}),
            id: rowId || existingLevel?.id || `${productId}-${String(targetSiteId || 'null')}`,
            organization_id: organizationId || existingLevel?.organization_id || profile?.organization_id || null,
            product_id: productId,
            site_id: targetSiteId || null,
            quantity,
            min_stock: Number(existingLevel?.min_stock || 0),
            updated_at: new Date().toISOString(),
          },
        ],
      };
    }));
  }, [profile?.organization_id]);

  const loadStockSnapshotForSale = useCallback(async ({ product, productId, siteId: targetSiteId }) => {
    if (!productId) {
      throw new Error('Producto inválido al intentar descontar stock.');
    }

    const { data: levels, error } = await supabase
      .from('stock_levels')
      .select('id, quantity, min_stock, site_id')
      .eq('organization_id', profile.organization_id)
      .eq('product_id', productId);

    if (error) {
      throw new Error(`No se pudo consultar el stock actual de ${product?.name || 'un producto'}. ${error.message || ''}`.trim());
    }

    const safeLevels = Array.isArray(levels) ? levels : [];
    const siteRow = safeLevels.find(
      (level) => String(level?.site_id || '') === String(targetSiteId || ''),
    ) || null;
    const hasAnyRows = safeLevels.length > 0;
    const legacyQty = getLegacyVariantStock(product);
    const effectiveQty = siteRow
      ? Number(siteRow.quantity || 0)
      : (hasAnyRows ? 0 : legacyQty);

    return {
      productId,
      productName: product?.name || 'Producto',
      siteId: targetSiteId,
      previousRowId: siteRow?.id || null,
      previousRowQuantity: siteRow ? Number(siteRow.quantity || 0) : null,
      previousMinStock: siteRow ? Number(siteRow.min_stock || 0) : 0,
      effectiveQty,
      usedLegacyFallback: !siteRow && !hasAnyRows,
      createdRowId: null,
    };
  }, [profile?.organization_id]);

  // Talles y colores: prechequeo por talle antes de cobrar con QR/Point/Getnet.
  // El chequeo previo mira el total del producto; si otra caja vendió ESE talle,
  // el cobro se aprobaría y la venta fallaría después. Solo actúa sobre
  // operaciones con variante (productos clásicos: no hace nada).
  const assertVariantStockBeforeCharge = useCallback(async (operations, targetSiteId, canalLabel) => {
    const requeridoPorVariante = new Map();
    for (const operation of Array.isArray(operations) ? operations : []) {
      if (!operation?.variantId) continue;
      const current = requeridoPorVariante.get(operation.variantId);
      requeridoPorVariante.set(operation.variantId, {
        productName: operation.product?.name || 'Producto',
        quantity: (current?.quantity || 0) + (Number(operation.quantity) || 0),
      });
    }
    if (requeridoPorVariante.size === 0) return;
    const { data, error } = await supabase
      .from('stock_variantes')
      .select('variant_id, quantity')
      .eq('site_id', targetSiteId)
      .in('variant_id', [...requeridoPorVariante.keys()]);
    if (error) {
      throw new Error(`No se pudo consultar el stock por talle antes de cobrar. ${error.message || ''}`.trim());
    }
    const disponiblePorVariante = new Map((data || []).map((row) => [row.variant_id, Number(row.quantity) || 0]));
    for (const [variantId, requerido] of requeridoPorVariante.entries()) {
      const disponible = disponiblePorVariante.get(variantId) || 0;
      if (disponible - requerido.quantity < 0) {
        throw new Error(`Stock insuficiente para cobrar con ${canalLabel}: ${requerido.productName} (talle/color sin stock). Disponible ${disponible}.`);
      }
    }
  }, []);

  // --- Vencimientos y lotes ---------------------------------------------------
  // Productos que llevan lotes y sus lotes en la sucursal del POS. Sirve para
  // avisar al agregar al carrito si lo que queda está vencido y para que la
  // venta descuente por la base con la referencia de la venta (así una
  // anulación o devolución vuelve a los mismos lotes).
  const expiryTrackingOn = isExpiryTrackingEnabled(organization);
  const [lotesPos, setLotesPos] = useState({ tracked: new Set(), porProducto: new Map() });

  const refreshLotesPos = useCallback(async () => {
    if (!expiryTrackingOn || !profile?.organization_id || !sitioId) {
      setLotesPos({ tracked: new Set(), porProducto: new Map() });
      return;
    }
    try {
      const [{ data: trackedRows, error: trackedError }, { data: lotRows, error: lotError }] = await Promise.all([
        supabase.from('products').select('id, stock_tracking')
          .eq('organization_id', profile.organization_id).eq('controla_vencimiento', true),
        supabase.from('stock_lotes').select('product_id, fecha_vencimiento, cantidad_actual')
          .eq('organization_id', profile.organization_id).eq('site_id', sitioId).gt('cantidad_actual', 0),
      ]);
      if (trackedError) throw trackedError;
      if (lotError) throw lotError;
      const tracked = new Set((trackedRows || [])
        .filter((p) => (p.stock_tracking || 'product') !== 'variant')
        .map((p) => p.id));
      const porProducto = new Map();
      (lotRows || []).forEach((l) => {
        if (!porProducto.has(l.product_id)) porProducto.set(l.product_id, []);
        porProducto.get(l.product_id).push(l);
      });
      setLotesPos({ tracked, porProducto });
    } catch (lotesError) {
      // Sin esta info el POS vende igual: la base aplica FEFO y el bloqueo.
      console.error('POS: no se pudieron leer los lotes de vencimiento:', lotesError);
    }
  }, [expiryTrackingOn, profile?.organization_id, sitioId]);

  useEffect(() => { refreshLotesPos(); }, [refreshLotesPos]);

  const rollbackStockOperations = useCallback(async (rollbackContext) => {
    // Vencimientos: lo que salió por lotes vuelve a los mismos lotes.
    const lotDeltas = Array.isArray(rollbackContext?.lotDeltas) ? rollbackContext.lotDeltas : [];
    for (const delta of [...lotDeltas].reverse()) {
      try {
        const restaurada = await devolverVentaALotes({
          supabase,
          organizationId: profile.organization_id,
          productId: delta.productId,
          siteId: delta.siteId,
          cantidad: Number(delta.quantity || 0),
          saleId: delta.saleId,
          motivo: 'Reversión de venta POS no completada',
          refTipo: 'reversion_venta',
        });
        syncProductStockLevelInState({
          productId: delta.productId,
          siteId: delta.siteId,
          quantity: Number(restaurada),
        });
      } catch (lotRollbackError) {
        console.error('No se pudo revertir el stock por lotes del POS:', lotRollbackError);
      }
    }

    // Talles y colores: se compensa con movimientos inversos (quedan en el kardex).
    const variantDeltas = Array.isArray(rollbackContext?.variantDeltas) ? rollbackContext.variantDeltas : [];
    for (const delta of [...variantDeltas].reverse()) {
      try {
        const restaurada = await applyStockDelta({
          supabase,
          organizationId: profile.organization_id,
          productId: delta.productId,
          variantId: delta.variantId,
          siteId: delta.siteId,
          delta: Number(delta.quantity || 0),
          motivo: 'Reversión de venta POS no completada',
          canal: 'pos',
          allowNegative: true,
        });
        syncVariantStockInState({
          productId: delta.productId,
          variantId: delta.variantId,
          siteId: delta.siteId,
          quantity: Number(restaurada),
          organizationId: profile.organization_id,
        });
      } catch (variantRollbackError) {
        console.error('No se pudo revertir el stock por talle del POS:', variantRollbackError);
      }
    }

    const movementIds = Array.isArray(rollbackContext?.movementIds) ? rollbackContext.movementIds : [];
    const stockSnapshots = Array.isArray(rollbackContext?.stockSnapshots) ? rollbackContext.stockSnapshots : [];

    if (movementIds.length > 0) {
      const { error: deleteMovementError } = await supabase
        .from('movimientos_stock')
        .delete()
        .in('id', movementIds);

      if (deleteMovementError) {
        console.error('No se pudieron revertir movimientos de stock del POS:', deleteMovementError);
      }
    }

    for (const snapshot of [...stockSnapshots].reverse()) {
      try {
        if (snapshot.previousRowId) {
          const { error: restoreError } = await supabase
            .from('stock_levels')
            .update({
              quantity: snapshot.previousRowQuantity,
              updated_at: new Date().toISOString(),
            })
            .eq('id', snapshot.previousRowId);

          if (restoreError) throw restoreError;

          syncProductStockLevelInState({
            productId: snapshot.productId,
            siteId: snapshot.siteId,
            quantity: snapshot.previousRowQuantity,
            rowId: snapshot.previousRowId,
          });
          continue;
        }

        if (snapshot.createdRowId) {
          const { error: deleteLevelError } = await supabase
            .from('stock_levels')
            .delete()
            .eq('id', snapshot.createdRowId);

          if (deleteLevelError) throw deleteLevelError;

          syncProductStockLevelInState({
            productId: snapshot.productId,
            siteId: snapshot.siteId,
            remove: true,
          });
        }
      } catch (rollbackError) {
        console.error('No se pudo restaurar el stock del POS luego de un fallo:', rollbackError);
      }
    }
  }, [syncProductStockLevelInState, syncVariantStockInState, profile?.organization_id]);

  const cleanupFailedSale = useCallback(async (saleId) => {
    if (!saleId) return;

    // V4: si la venta fallida ya había debitado la cuenta corriente del cliente,
    // borrar el client_movement sin más dejaba clients.current_balance inflado.
    // syncSaleClientDebit({ shouldApply:false }) recompone el saldo desde el timeline
    // (idempotente); luego el delete de abajo limpia el movimiento. Best-effort.
    try {
      const { data: failedSale } = await supabase
        .from('sales')
        .select('client_id, organization_id, total')
        .eq('id', saleId)
        .maybeSingle();

      if (failedSale?.client_id && failedSale?.organization_id) {
        await syncSaleClientDebit({
          saleId,
          organizationId: failedSale.organization_id,
          clientId: failedSale.client_id,
          totalAmount: Number(failedSale.total) || 0,
          movementType: 'Venta',
          observations: `Reversion por venta POS fallida #${String(saleId).slice(0, 8)}.`,
          shouldApply: false,
        });
      }
    } catch (balanceRevertError) {
      console.error('No se pudo revertir el saldo del cliente de una venta POS fallida:', balanceRevertError);
    }

    const cleanupTasks = [
      supabase.from('store_orders').delete().eq('sale_id', saleId),
      supabase.from('sales_receivables').delete().eq('sale_id', saleId),
      supabase.from('client_movements').delete().eq('sale_id', saleId),
      supabase.from('movimientos_financieros').delete().eq('documento_tipo', 'Venta').eq('documento_id', saleId),
      supabase.from('sale_items').delete().eq('sale_id', saleId),
      supabase.from('sales').delete().eq('id', saleId),
    ];

    const results = await Promise.allSettled(cleanupTasks);
    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.error('No se pudo limpiar una venta POS fallida:', result.reason);
      } else if (result.value?.error) {
        console.error('No se pudo limpiar una venta POS fallida:', result.value.error);
      }
    });
  }, []);

  const buildStockOperationsForSale = useCallback((saleItems, comprobanteLabel, saleShortId) => {
    const operations = [];

    for (const item of saleItems) {
      if (item.is_misc) continue;

      const itemQuantity = Number(item.cantidad) || 0;
      if (itemQuantity <= 0) continue;

      const isCombo = item.product_type === 'Combo';
      if (!isCombo) {
        const soldProduct = productos.find((product) => product.id === item.product_id);
        if (!soldProduct) {
          throw new Error(`No se encontró el producto "${item.nombre || 'sin nombre'}" en el catálogo activo.`);
        }

        const variantTracked = isVariantTracked(soldProduct);
        if (variantTracked && !item.variant_id) {
          throw new Error(`Elegí el talle/color de "${soldProduct.name}" antes de cobrar.`);
        }

        operations.push({
          productId: item.product_id,
          variantId: variantTracked ? item.variant_id : null,
          product: soldProduct,
          quantity: itemQuantity,
          motivo: `Venta POS #${saleShortId} (${comprobanteLabel || 'Comprobante'}).`,
        });
        continue;
      }

      const isDynamicCombo = item.is_dynamic_combo && Array.isArray(item.dynamic_components) && item.dynamic_components.length > 0;
      const comboDef = combos.find((combo) => combo.product_id === item.product_id);
      const comboIngredients = isDynamicCombo
        ? item.dynamic_components.map((component) => ({
          product_id: component.product_id,
          quantity: component.quantity,
          _dyn_name: component.name,
          _dyn_step: component.step_name,
        }))
        : (comboDef ? comboItems.filter((comboItem) => comboItem.combo_id === comboDef.id) : []);

      if (!comboIngredients.length) {
        throw new Error(`El combo "${item.nombre}" no tiene ingredientes configurados. Corrígelo antes de venderlo.`);
      }

      for (const ingredient of comboIngredients) {
        const ingredientProduct = productos.find((product) => product.id === ingredient.product_id);
        if (!ingredientProduct) {
          throw new Error(`El combo "${item.nombre}" referencia un ingrediente inexistente o inactivo.`);
        }
        if (isVariantTracked(ingredientProduct)) {
          throw new Error(`El combo "${item.nombre}" incluye "${ingredientProduct.name}", que maneja talles/colores: los combos con talles todavía no están soportados.`);
        }

        const quantityToDiscount = (Number(ingredient.quantity) || 0) * itemQuantity;
        if (quantityToDiscount <= 0) continue;

        const motivo = isDynamicCombo
          ? `Venta POS #${saleShortId} (Combo dinámico: ${item.nombre} -> ${ingredient._dyn_step || 'paso'}: ${ingredient._dyn_name || ingredientProduct.name}).`
          : `Venta POS #${saleShortId} (Combo: ${item.nombre}).`;

        operations.push({
          productId: ingredient.product_id,
          product: ingredientProduct,
          quantity: quantityToDiscount,
          motivo,
        });
      }
    }

    return operations;
  }, [combos, comboItems, productos]);

  const applyStockOperationsForSale = useCallback(async ({
    operations,
    organizationId,
    siteId: targetSiteId,
    saleId = null,
  }) => {
    if (!Array.isArray(operations) || operations.length === 0) {
      return { stockSnapshots: [], movementIds: [], variantDeltas: [], lotDeltas: [] };
    }

    // Talles y colores: las operaciones con variante van por fn_mover_stock
    // (stock por talle + kardex en la base). Vencimientos: los productos con
    // lotes van por fn_mover_stock_lotes con la referencia de la venta. El resto
    // sigue el camino de siempre.
    const variantOperations = operations.filter((operation) => operation.variantId);
    // Qué productos llevan lotes se consulta al cobrar (no del estado cargado al
    // abrir el POS): un producto recién marcado o una carga lenta no se escapa.
    const trackedNow = await fetchLotTrackedProductIds({
      supabase,
      organization,
      productIds: operations.filter((operation) => !operation.variantId).map((operation) => operation.productId),
    });
    const lotOperations = operations.filter((operation) => !operation.variantId && trackedNow.has(operation.productId));
    const legacyOperations = operations.filter((operation) => !operation.variantId && !trackedNow.has(operation.productId));

    const aggregatedOperations = Array.from(
      legacyOperations.reduce((map, operation) => {
        const current = map.get(operation.productId);
        if (current) {
          current.quantity += Number(operation.quantity) || 0;
          return map;
        }

        map.set(operation.productId, {
          productId: operation.productId,
          product: operation.product,
          quantity: Number(operation.quantity) || 0,
        });
        return map;
      }, new Map()).values(),
    );

    const rollbackContext = {
      stockSnapshots: [],
      movementIds: [],
      variantDeltas: [],
      lotDeltas: [],
    };

    try {
      const lotAggregates = Array.from(lotOperations.reduce((map, operation) => {
        const current = map.get(operation.productId);
        if (current) {
          current.quantity += Number(operation.quantity) || 0;
          return map;
        }
        map.set(operation.productId, {
          productId: operation.productId,
          quantity: Number(operation.quantity) || 0,
          motivo: operation.motivo,
        });
        return map;
      }, new Map()).values());

      for (const aggregate of lotAggregates) {
        if (aggregate.quantity <= 0) continue;
        const nuevaCantidad = await moverStockConLotes({
          supabase,
          organizationId,
          productId: aggregate.productId,
          siteId: targetSiteId,
          delta: -aggregate.quantity,
          tipo: 'Egreso',
          motivo: aggregate.motivo,
          lotes: [],
          refTipo: 'venta',
          refId: saleId,
          allowNegative: Boolean(allowNegativeStock),
        });
        rollbackContext.lotDeltas.push({
          productId: aggregate.productId,
          siteId: targetSiteId,
          quantity: aggregate.quantity,
          saleId,
        });
        syncProductStockLevelInState({
          productId: aggregate.productId,
          siteId: targetSiteId,
          quantity: Number(nuevaCantidad),
          organizationId,
        });
      }

      for (const aggregate of aggregatedOperations) {
        const snapshot = await loadStockSnapshotForSale({
          product: aggregate.product,
          productId: aggregate.productId,
          siteId: targetSiteId,
        });

        const nextQuantity = snapshot.effectiveQty - aggregate.quantity;
        if (!allowNegativeStock && nextQuantity < 0) {
          throw new Error(`Stock insuficiente: ${snapshot.productName}. Disponible ${snapshot.effectiveQty}.`);
        }

        if (snapshot.previousRowId) {
          const { error: updateError } = await supabase
            .from('stock_levels')
            .update({
              quantity: nextQuantity,
              updated_at: new Date().toISOString(),
            })
            .eq('id', snapshot.previousRowId);

          if (updateError) {
            throw new Error(`No se pudo actualizar el stock de ${snapshot.productName}. ${updateError.message || ''}`.trim());
          }

          syncProductStockLevelInState({
            productId: snapshot.productId,
            siteId: snapshot.siteId,
            quantity: nextQuantity,
            rowId: snapshot.previousRowId,
            organizationId,
          });
        } else {
          const { data: insertedLevel, error: insertError } = await supabase
            .from('stock_levels')
            .insert([
              {
                organization_id: organizationId,
                product_id: snapshot.productId,
                site_id: snapshot.siteId,
                quantity: nextQuantity,
                min_stock: snapshot.previousMinStock || 0,
              },
            ])
            .select('id')
            .single();

          if (insertError) {
            const fallbackSource = snapshot.usedLegacyFallback
              ? ' usando el stock legacy como base'
              : '';
            throw new Error(`No se pudo crear el stock por sucursal de ${snapshot.productName}${fallbackSource}. ${insertError.message || ''}`.trim());
          }

          snapshot.createdRowId = insertedLevel?.id || null;

          syncProductStockLevelInState({
            productId: snapshot.productId,
            siteId: snapshot.siteId,
            quantity: nextQuantity,
            rowId: snapshot.createdRowId,
            organizationId,
          });
        }

        rollbackContext.stockSnapshots.push(snapshot);
      }

      if (legacyOperations.length > 0) {
        const movementRows = legacyOperations.map((operation) => ({
          organization_id: organizationId,
          producto_id: operation.productId,
          tipo: 'Egreso',
          cantidad: Number(operation.quantity) || 0,
          motivo: operation.motivo,
          sitio_origen: targetSiteId,
          sitio_destino: null,
        }));

        const { data: movementResults, error: movementError } = await supabase
          .from('movimientos_stock')
          .insert(movementRows)
          .select('id');

        if (movementError) {
          throw new Error(`No se pudo registrar el movimiento de stock del POS. ${movementError.message || ''}`.trim());
        }

        rollbackContext.movementIds = (movementResults || [])
          .map((movement) => movement?.id)
          .filter(Boolean);
      }

      for (const operation of groupStockOperations(variantOperations)) {
        const nuevaCantidad = await applyStockDelta({
          supabase,
          organizationId,
          productId: operation.productId,
          variantId: operation.variantId,
          siteId: targetSiteId,
          delta: -Number(operation.quantity || 0),
          motivo: operation.motivo,
          canal: 'pos',
          allowNegative: Boolean(allowNegativeStock),
        });
        rollbackContext.variantDeltas.push({
          productId: operation.productId,
          variantId: operation.variantId,
          siteId: targetSiteId,
          quantity: Number(operation.quantity || 0),
        });
        syncVariantStockInState({
          productId: operation.productId,
          variantId: operation.variantId,
          siteId: targetSiteId,
          quantity: Number(nuevaCantidad),
          organizationId,
        });
      }

      if (lotOperations.length > 0) refreshLotesPos();
      return rollbackContext;
    } catch (stockError) {
      await rollbackStockOperations(rollbackContext);
      throw stockError;
    }
  }, [allowNegativeStock, loadStockSnapshotForSale, organization, refreshLotesPos, rollbackStockOperations, syncProductStockLevelInState, syncVariantStockInState]);

  const getVariante = useCallback((prod) => {
    const pv = prod?.product_variants;
    if (Array.isArray(pv) && pv.length > 0) return pv[0];
    return null;
  }, []);

  const getMeaningfulVariants = useCallback((prod) => {
    const variantes = prod?.product_variants;
    return (Array.isArray(variantes) ? variantes : []).filter((variant) =>
      Boolean(getCleanVariantLabel(getVariantName(variant))),
    );
  }, []);

  const getPosPricingProduct = useCallback((prod) => {
    if (!prod) return null;

    return {
      ...prod,
      sell_price: getGestionCostosSellPrice(prod, empresa?.usd_exchange_rate),
      product_variants: []
    };
  }, [empresa?.usd_exchange_rate]);

  const getEffectiveVariantPrice = useCallback((prod, variante = null) => {
    const pricingProduct = getPosPricingProduct(prod);
    if (!pricingProduct) return 0;

    return variante
      ? getPriceListAdjustedVariantPrice(pricingProduct, variante, activePriceList)
      : getPriceListAdjustedBasePrice(pricingProduct, activePriceList);
  }, [activePriceList, getPosPricingProduct]);

  const getCartUnitPriceForProduct = useCallback((prod, variante = null) => {
    if (!prod) return 0;

    const meaningfulVariants = getMeaningfulVariants(prod);
    if (variante && (meaningfulVariants.length > 1 || isVariantTracked(prod))) {
      return Number(getEffectiveVariantPrice(prod, variante)) || 0;
    }

    return Number(getEffectiveVariantPrice(prod, null)) || 0;
  }, [getEffectiveVariantPrice, getMeaningfulVariants]);

  useEffect(() => {
    setCarrito((current) => current.map((item) => {
      if (!item?.product_id) return item;
      if (item.price_overridden) return item;

      const product = productos.find((prod) => prod.id === item.product_id);
      if (!product) return item;

      const variant = item.variant_id
        ? (product.product_variants || []).find((productVariant) => productVariant.id === item.variant_id) || null
        : getVariante(product);
      const nextUnitPrice = getCartUnitPriceForProduct(product, variant);

      // Con descuento por producto, se mantiene el % sobre el nuevo precio de lista.
      if (tieneDescuentoItem(item)) return actualizarPrecioListaItem(item, nextUnitPrice);

      return {
        ...item,
        unit_price: nextUnitPrice,
        subtotal: Number(item.cantidad || 0) * nextUnitPrice,
      };
    }));
  }, [productos, getCartUnitPriceForProduct, getVariante]);

  // === Talles y colores: agrega una o varias variantes de un producto con talles ===
  // El stock se controla por variante y sucursal (stock_variantes), no por el
  // total del producto: con 1 S y 5 XL no se pueden vender 2 S.
  const agregarVariantesAlCarrito = useCallback((prod, selecciones) => {
    if (!prod || !Array.isArray(selecciones) || selecciones.length === 0) return;

    if (!allowNegativeStock) {
      for (const { variant, cantidad } of selecciones) {
        const disponible = getVariantStockForSite(prod.stock_variantes, variant.id, sitioId);
        const yaEnCarrito = Number(carrito.find((item) => item.cart_key === variant.id)?.cantidad || 0);
        if (disponible - yaEnCarrito - Number(cantidad || 0) < 0) {
          toast.error(
            `Sin stock suficiente de ${buildProductDisplayName(prod.name, getVariantName(variant), { separator: 'dash' })}: disponible ${disponible}${yaEnCarrito ? `, ya tenés ${yaEnCarrito} en el ticket` : ''}.`,
          );
          return;
        }
      }
    }

    setCarrito((prev) => {
      let next = [...prev];
      for (const { variant, cantidad } of selecciones) {
        const qty = Number(cantidad || 0);
        if (qty <= 0) continue;
        const idx = next.findIndex((item) => item.cart_key === variant.id);
        if (idx >= 0) {
          const item = next[idx];
          const nuevaCantidad = Number(item.cantidad) + qty;
          next[idx] = { ...item, cantidad: nuevaCantidad, subtotal: nuevaCantidad * Number(item.unit_price) };
        } else {
          const unitPrice = Number(getEffectiveVariantPrice(prod, variant)) || 0;
          next = [
            {
              cart_key: variant.id,
              product_id: prod.id,
              variant_id: variant.id,
              nombre: buildProductDisplayName(prod.name, getVariantName(variant), { separator: 'dash' }),
              product_type: prod.product_type,
              unit_price: unitPrice,
              cantidad: qty,
              subtotal: unitPrice * qty,
              price_overridden: false,
              block_discounts: Boolean(prod.block_discounts),
              block_surcharges: Boolean(prod.block_surcharges),
            },
            ...next,
          ];
        }
      }
      return next;
    });
    setTallesModal({ open: false, prod: null });
  }, [allowNegativeStock, carrito, getEffectiveVariantPrice, sitioId]);

  const agregarAlCarrito = useCallback((prod) => {
    try {
      if (!prod) return;

      // === COMBO DINÁMICO: abrir modal multi-paso ===
      if (
        enableDynamicCombos &&
        prod.product_type === 'Combo' &&
        prod.is_dynamic_combo
      ) {
        const comboDef = combos.find(c => c.product_id === prod.id);
        if (!comboDef) {
          toast.error('Este combo dinámico no tiene configuración. Editalo en Combos.');
          return;
        }
        const stepsForCombo = comboSteps
          .filter(s => s.combo_id === comboDef.id)
          .sort((a, b) => Number(a.step_order || 0) - Number(b.step_order || 0));
        if (stepsForCombo.length === 0) {
          toast.error('Este combo dinámico no tiene pasos definidos.');
          return;
        }
        // Hidratar opciones con datos del producto (nombre, costo, precio)
        const stepsHydrated = stepsForCombo.map(st => {
          const optRows = comboStepOptions.filter(o => o.step_id === st.id);
          const options = optRows.map(o => {
            const p = productos.find(pp => pp.id === o.product_id);
            return p ? {
              product_id: p.id,
              name: p.name,
              cost_price: Number((getMeaningfulVariants(p)[0]?.cost ?? p.cost_price ?? 0)),
              sell_price: Number(p.sell_price || 0),
              image_url: p.image_url || null,
            } : null;
          }).filter(Boolean);
          return { ...st, options };
        });

        // Inicializar selecciones vacías por paso
        const initSel = {};
        stepsHydrated.forEach(st => { initSel[st.id] = []; });
        setDynamicSelections(initSel);
        setDynamicComboModal({ open: true, prod, steps: stepsHydrated });
        return;
      }

      // Talles y colores: siempre por selector (o directo si hay un solo talle activo)
      if (isVariantTracked(prod)) {
        const activas = (prod.product_variants || []).filter(isVariantActive);
        if (activas.length === 0) {
          toast.error(`${prod.name} no tiene talles/colores activos.`);
          return;
        }
        if (activas.length === 1) {
          agregarVariantesAlCarrito(prod, [{ variant: activas[0], cantidad: 1 }]);
          return;
        }
        setTallesModal({ open: true, prod });
        return;
      }

      const meaningfulVariants = getMeaningfulVariants(prod);

      // Si tiene más de una variante Y el flag de variantes está habilitado, abrir el modal
      if (enableVariants && meaningfulVariants.length > 1) {
        setVariantModal({ open: true, prod: { ...prod, product_variants: meaningfulVariants } });
        return;
      }

      // Si tiene 0 o 1 variante, agregar directo (comportamiento anterior)
      const variante = meaningfulVariants.length === 1 ? meaningfulVariants[0] : getVariante(prod);
      const unitPrice = getCartUnitPriceForProduct(prod, variante);
      if (!variante) toast.error('Producto sin variante/precio: se agregó en $0.');

      const cartKey = variante?.id || prod.id;

      // El stock se avisa al AGREGAR, no recién al cobrar: antes se podía armar
      // el ticket entero y enterarse del faltante al final, con el cliente
      // enfrente. Los servicios y los combos no descuentan stock propio.
      if (!allowNegativeStock && prod.product_type !== 'Servicio' && prod.product_type !== 'Combo') {
        const disponible = Number(getStock(prod, sitioId) || 0);
        const yaEnCarrito = Number(
          (carrito.find((item) => item.cart_key === cartKey)?.cantidad) || 0,
        );
        if (disponible - yaEnCarrito < 1) {
          toast.error(
            yaEnCarrito > 0
              ? `No queda más stock de ${prod.name}: disponible ${disponible}, ya tenés ${yaEnCarrito} en el ticket.`
              : `Sin stock de ${prod.name}. Registrá la compra o ajustá el inventario antes de venderlo.`,
          );
          return;
        }
      }

      // Vencimientos: si lo que queda está vencido, se avisa al agregar (con
      // "No vender vencidos" no se deja agregar; la base igual lo frena al cobrar).
      if (lotesPos.tracked.has(prod.id) && prod.product_type !== 'Servicio' && prod.product_type !== 'Combo') {
        const resumen = summarizeSellableStock(lotesPos.porProducto.get(prod.id) || []);
        const yaEnCarritoLote = Number((carrito.find((item) => item.cart_key === cartKey)?.cantidad) || 0);
        if (resumen.vencido > 0 && resumen.vigente - yaEnCarritoLote < 1) {
          if (organization?.block_expired_sale) {
            toast.error(`Lo que queda de ${prod.name} está vencido. Dalo de baja desde Inventario → Vencimientos.`);
            return;
          }
          toast(`Atención: lo que queda de ${prod.name} está vencido.`, { icon: '⚠️' });
        }
      }
      setCarrito((prev) => {
        const existe = prev.find((item) => item.cart_key === cartKey);
        if (existe) {
          return prev.map((item) =>
            item.cart_key === cartKey
              ? { ...item, cantidad: Number(item.cantidad) + 1, subtotal: (Number(item.cantidad) + 1) * Number(item.unit_price) }
              : item
          );
        }
        return [
          {
            cart_key: cartKey,
            product_id: prod.id,
            variant_id: variante?.id || null,
            nombre: sanitizeProductDisplayName(prod.name || 'Producto'),
            product_type: prod.product_type,
            unit_price: unitPrice,
            cantidad: 1,
            subtotal: unitPrice,
            price_overridden: false,
            block_discounts: Boolean(prod.block_discounts),
            block_surcharges: Boolean(prod.block_surcharges)
          },
          ...prev
        ];
      });
    } catch (e) {
      console.error(e);
      toast.error('No se pudo agregar el producto.');
    }
  }, [getCartUnitPriceForProduct, getMeaningfulVariants, getVariante, enableVariants, enableDynamicCombos, combos, comboSteps, comboStepOptions, productos, allowNegativeStock, getStock, sitioId, carrito, agregarVariantesAlCarrito, lotesPos, organization?.block_expired_sale]);

  // === Auto-agregar por código de barras (sin necesidad de Enter) ===
  // Mejora UX en el POS: cuando lo que se escribe/escanea coincide
  // exactamente con un código de barras del catálogo, se agrega el
  // producto al carrito de forma automática y se limpia el input para
  // estar listo para el próximo escaneo. Se aplica un pequeño debounce
  // (120ms) para evitar coincidencias accidentales mientras se tipea
  // y se exige longitud mínima (>=6) para no chocar con búsquedas cortas.
  useEffect(() => {
    const q = (busqueda || '').trim();
    if (!q || q.length < 6) return;

    const timer = setTimeout(() => {
      const qLower = q.toLowerCase();
      // Talles y colores: el código de una variante la agrega directo.
      const matchVariante = findVariantByCode(productos, q);
      if (matchVariante && isVariantTracked(matchVariante.product)) {
        agregarVariantesAlCarrito(matchVariante.product, [{ variant: matchVariante.variant, cantidad: 1 }]);
        setBusqueda('');
        setTimeout(() => searchInputRef.current?.focus(), 0);
        return;
      }
      const matchBarcode = productos.find(
        (p) => (p.barcode || '').toLowerCase() === qLower
      );
      if (!matchBarcode) return;

      agregarAlCarrito(matchBarcode);
      setBusqueda('');
      // Re-enfocar para encadenar escaneos sin tocar el mouse
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }, 120);

    return () => clearTimeout(timer);
  }, [busqueda, productos, agregarAlCarrito, agregarVariantesAlCarrito]);

  // === Confirmar selección de combo dinámico → agregar al carrito ===
  const confirmarComboDinamico = useCallback(() => {
    const { prod, steps } = dynamicComboModal;
    if (!prod || !steps?.length) return;

    // Validar: cada paso debe tener exactamente 'quantity' unidades elegidas
    for (const st of steps) {
      const sel = dynamicSelections[st.id] || [];
      const totalQty = sel.reduce((acc, x) => acc + Number(x.qty || 0), 0);
      const reqQty = Number(st.quantity || 1);
      if (totalQty !== reqQty) {
        toast.error(`Falta(n) ${reqQty - totalQty} producto(s) en "${st.step_name}".`);
        return;
      }
    }

    // Aplanar componentes elegidos (varios productos por paso, varias unidades por producto)
    const dynamic_components = [];
    let costRealEstimado = 0;
    steps.forEach(st => {
      const sel = dynamicSelections[st.id] || [];
      sel.forEach(item => {
        if (Number(item.qty) > 0) {
          dynamic_components.push({
            product_id: item.product_id,
            name: item.name,
            quantity: Number(item.qty),
            step_name: st.step_name,
          });
          costRealEstimado += Number(item.cost_price || 0) * Number(item.qty || 0);
        }
      });
    });

    // Resumen visual del combo (ej: "tarta carne + coca")
    const resumen = dynamic_components.map(c => `${c.quantity > 1 ? c.quantity + 'x ' : ''}${c.name}`).join(' + ');

    // Precio fijo del combo
    const variante = getVariante(prod);
    const unitPrice = getCartUnitPriceForProduct(prod, variante);

    // Cada combo dinámico es único por la combinación elegida → cart_key único
    const cartKey = `dyncombo-${prod.id}-${Date.now()}`;
    setCarrito((prev) => [
      {
        cart_key: cartKey,
        product_id: prod.id,
        variant_id: variante?.id || null,
        nombre: sanitizeProductDisplayName(prod.name || 'Combo Dinámico'),
        nombre_extendido: resumen,
        product_type: prod.product_type,
        is_dynamic_combo: true,
        dynamic_components,
        cost_estimado: costRealEstimado,
        unit_price: unitPrice,
        cantidad: 1,
        subtotal: unitPrice,
        price_overridden: false,
        block_discounts: Boolean(prod.block_discounts),
        block_surcharges: Boolean(prod.block_surcharges)
      },
      ...prev
    ]);

    setDynamicComboModal({ open: false, prod: null, steps: [] });
    setDynamicSelections({});
    toast.success(`Combo "${prod.name}" agregado.`);
  }, [dynamicComboModal, dynamicSelections, getVariante, getCartUnitPriceForProduct]);

  // Helper: incrementar/decrementar la unidad de una opción en un paso
  const setSelectionQty = useCallback((stepId, opt, deltaOrSet, isAbsolute = false) => {
    setDynamicSelections(prev => {
      const list = [...(prev[stepId] || [])];
      const idx = list.findIndex(x => x.product_id === opt.product_id);
      const step = dynamicComboModal.steps.find(s => s.id === stepId);
      const maxStepQty = Number(step?.quantity || 1);

      if (idx < 0) {
        // Agregar nuevo
        const totalActual = list.reduce((acc, x) => acc + Number(x.qty || 0), 0);
        if (totalActual >= maxStepQty) {
          toast.error(`Ya elegiste ${maxStepQty} en "${step?.step_name}". Quitá uno para cambiar.`);
          return prev;
        }
        return { ...prev, [stepId]: [...list, { ...opt, qty: 1 }] };
      }

      const current = list[idx];
      const totalActual = list.reduce((acc, x) => acc + Number(x.qty || 0), 0);
      const newQty = isAbsolute ? deltaOrSet : Math.max(0, current.qty + deltaOrSet);

      // Si excede el cupo del paso
      if (newQty > current.qty && totalActual >= maxStepQty) {
        toast.error(`Llegaste al máximo (${maxStepQty}) en "${step?.step_name}".`);
        return prev;
      }

      if (newQty <= 0) {
        list.splice(idx, 1);
      } else {
        list[idx] = { ...current, qty: newQty };
      }
      return { ...prev, [stepId]: list };
    });
  }, [dynamicComboModal.steps]);

  const confirmarVariante = useCallback((prod, variante) => {
    const unitPrice = Number(getEffectiveVariantPrice(prod, variante)) || 0;
    const cartKey = variante?.id || prod.id;
    setCarrito((prev) => {
      const existe = prev.find((item) => item.cart_key === cartKey);
      if (existe) {
        return prev.map((item) =>
          item.cart_key === cartKey
            ? { ...item, cantidad: Number(item.cantidad) + 1, subtotal: (Number(item.cantidad) + 1) * Number(item.unit_price) }
            : item
        );
      }
      return [
        {
          cart_key: cartKey,
          product_id: prod.id,
          variant_id: variante?.id || null,
          nombre: buildProductDisplayName(prod.name, getVariantName(variante), { separator: 'dash' }),
          product_type: prod.product_type,
          unit_price: unitPrice,
          cantidad: 1,
          subtotal: unitPrice,
          price_overridden: false,
          block_discounts: Boolean(prod.block_discounts),
          block_surcharges: Boolean(prod.block_surcharges)
        },
        ...prev
      ];
    });
    setVariantModal({ open: false, prod: null });
  }, [getEffectiveVariantPrice]);

  const agregarMiscItem = () => {
    const miscId = 'MISC-ITEM-' + Date.now();
    setCarrito((prev) => [
      {
        product_id: miscId,
        variant_id: null,
        nombre: 'Productos Varios',
        product_type: 'Misc',
        unit_price: 0,
        cantidad: 1,
        subtotal: 0,
        price_overridden: false,
        is_misc: true,
        block_discounts: false,
        block_surcharges: false
      },
      ...prev
    ]);
  };

  useEffect(() => {
    setCartQuantityDrafts((current) => {
      const validKeys = new Set(carrito.map((item) => String(item.cart_key || item.product_id)));
      let changed = false;
      const next = {};

      Object.entries(current).forEach(([key, value]) => {
        if (validKeys.has(String(key))) {
          next[key] = value;
        } else {
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [carrito]);

  const setCantidadCarrito = useCallback((cartKey, nextCantidad) => {
    const cantidadNormalizada = normalizeCartQuantityValue(nextCantidad);

    setCarrito((curr) =>
      curr.map((item) => {
        if (item.cart_key === cartKey || (!item.cart_key && item.product_id === cartKey)) {
          return {
            ...item,
            cantidad: cantidadNormalizada,
            subtotal: cantidadNormalizada * Number(item.unit_price),
          };
        }
        return item;
      })
    );
  }, [normalizeCartQuantityValue]);

  const modificarCantidad = (cartKey, delta) => {
    setCartQuantityDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, cartKey)) return current;
      const next = { ...current };
      delete next[cartKey];
      return next;
    });

    setCarrito((curr) =>
      curr.map((item) => {
        if (item.cart_key === cartKey || (!item.cart_key && item.product_id === cartKey)) {
          const nuevaCant = normalizeCartQuantityValue(Number(item.cantidad) + delta);
          return { ...item, cantidad: nuevaCant, subtotal: nuevaCant * Number(item.unit_price) };
        }
        return item;
      })
    );
  };

  const handleCantidadInputChange = useCallback((cartKey, value) => {
    if (decimalCartQuantityEnabled) {
      const normalized = normalizeLocalizedNumberInput(value, {
        decimals: DECIMAL_CART_QUANTITY_DECIMALS,
        allowNegative: false,
        preferSingleDotAsDecimal: true,
      });

      setCartQuantityDrafts((current) => ({
        ...current,
        [cartKey]: normalized.display,
      }));

      if (!normalized.hasValue || normalized.number === null) return;
      if (normalized.number > 0) {
        setCantidadCarrito(cartKey, normalized.number);
      }
      return;
    }

    if (!/^\d*$/.test(value)) {
      if (/[.,]/.test(value)) {
        toast.error('Las cantidades deben ser números enteros. Activá "Cantidades decimales" en configuración si necesitás decimales.');
      }
      return;
    }

    setCartQuantityDrafts((current) => ({
      ...current,
      [cartKey]: value,
    }));

    if (value === '') return;

    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      setCantidadCarrito(cartKey, parsed);
    }
  }, [decimalCartQuantityEnabled, setCantidadCarrito]);

  const commitCantidadInput = useCallback((cartKey, fallbackCantidad) => {
    const rawValue = cartQuantityDrafts[cartKey];
    if (rawValue === undefined) return;

    if (decimalCartQuantityEnabled) {
      const normalized = normalizeLocalizedNumberInput(rawValue, {
        decimals: DECIMAL_CART_QUANTITY_DECIMALS,
        allowNegative: false,
        preferSingleDotAsDecimal: true,
      });

      const nextCantidad = normalized.hasValue && normalized.number && normalized.number > 0
        ? normalizeCartQuantityValue(normalized.number)
        : normalizeCartQuantityValue(fallbackCantidad);

      setCantidadCarrito(cartKey, nextCantidad);
      setCartQuantityDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, cartKey)) return current;
        const next = { ...current };
        delete next[cartKey];
        return next;
      });
      return;
    }

    const parsed = Number.parseInt(rawValue, 10);
    const nextCantidad = Number.isFinite(parsed) && parsed > 0
      ? parsed
      : normalizeCartQuantityValue(fallbackCantidad);

    setCantidadCarrito(cartKey, nextCantidad);
    setCartQuantityDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, cartKey)) return current;
      const next = { ...current };
      delete next[cartKey];
      return next;
    });
  }, [cartQuantityDrafts, decimalCartQuantityEnabled, normalizeCartQuantityValue, setCantidadCarrito]);

  const modificarPrecio = (cartKey, newPrice) => {
    const normalized = String(newPrice).replace(',', '.');
    const p = Math.max(0, Number(normalized));
    setCarrito((curr) =>
      curr.map((item) => {
        if (item.cart_key === cartKey || (!item.cart_key && item.product_id === cartKey)) {
          // Editar el precio a mano redefine el precio de lista: el descuento por
          // producto que tuviera la línea se descarta.
          const base = tieneDescuentoItem(item) ? quitarDescuentoItem(item) : item;
          return { ...base, unit_price: p, subtotal: Number(base.cantidad) * p, price_overridden: true };
        }
        return item;
      })
    );
  };

  // === Descuento por producto: modal por línea ===
  const cartKeyDe = (item) => item?.cart_key || item?.product_id;

  const abrirDescuentoItem = (item) => {
    if (!itemDiscountConfig.enabled) return;
    if (item.block_discounts && !puedeAutorizarDescuento) {
      toast.error('Este producto está marcado sin descuentos.');
      return;
    }
    const pctActual = Number(item.item_discount_pct) || 0;
    const motivoActual = String(item.item_discount_reason || '');
    const esMotivoDeLista = MOTIVOS_DESCUENTO_ITEM.includes(motivoActual);
    setDescItemModal({
      cartKey: cartKeyDe(item),
      modo: 'pct',
      valor: pctActual > 0 ? String(Math.round(pctActual * 100) / 100) : '',
      motivo: esMotivoDeLista ? motivoActual : (motivoActual ? 'Otro' : ''),
      motivoOtro: esMotivoDeLista ? '' : motivoActual,
      email: '',
      password: '',
      enviando: false,
    });
  };

  const descItemModalItem = descItemModal
    ? carrito.find((it) => cartKeyDe(it) === descItemModal.cartKey) || null
    : null;

  const parseValorDescItem = (valor) => {
    if (valor === '' || valor === null || valor === undefined) return null;
    const n = Number(String(valor).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const calcularPctDescItem = (modal, item) => {
    if (!modal || !item) return 0;
    const raw = parseValorDescItem(modal.valor);
    if (raw === null) return 0;
    if (modal.modo === 'precio') {
      const lista = precioListaItem(item);
      return lista > 0 ? Math.max(0, Math.min(100, (1 - raw / lista) * 100)) : 0;
    }
    return Math.max(0, Math.min(100, raw));
  };

  const evaluarDescItemModal = (modal, item) => evaluarDescuentoItem({
    pct: calcularPctDescItem(modal, item),
    config: itemDiscountConfig,
    puedeAutorizar: puedeAutorizarDescuento,
    bloqueado: Boolean(item?.block_discounts),
    allowZeroPrice: Boolean(organization?.allow_zero_sell_price),
    precioLista: precioListaItem(item),
  });

  const quitarDescuentoLinea = (cartKey) => {
    setCarrito((curr) => curr.map((it) => (cartKeyDe(it) === cartKey ? quitarDescuentoItem(it) : it)));
    setDescItemModal(null);
  };

  const confirmarDescuentoItem = async () => {
    const modal = descItemModal;
    const item = descItemModalItem;
    if (modal?.enviando) return;
    if (!modal || !item) {
      setDescItemModal(null);
      return;
    }

    const pct = calcularPctDescItem(modal, item);
    if (!(pct > 0)) {
      quitarDescuentoLinea(modal.cartKey);
      return;
    }

    const ev = evaluarDescItemModal(modal, item);
    if (!ev.ok) {
      toast.error(ev.error);
      return;
    }

    const motivo = modal.motivo === 'Otro'
      ? String(modal.motivoOtro || '').trim()
      : String(modal.motivo || '').trim();
    if (ev.requiereMotivo && !motivo) {
      toast.error('Elegí el motivo del descuento.');
      return;
    }

    let autorizadoPor = null;
    let autorizadoNombre = '';
    if (ev.requiereAutorizacion) {
      if (!String(modal.email || '').trim() || !modal.password) {
        toast.error(`Supera el tope de ${itemDiscountConfig.maxPct}%: tiene que autorizarlo un usuario habilitado.`);
        return;
      }
      setDescItemModal((m) => (m ? { ...m, enviando: true } : m));
      const { data, error } = await supabase.rpc('autorizar_descuento_item', {
        p_email: String(modal.email).trim(),
        p_password: modal.password,
      });
      const row = Array.isArray(data) ? data[0] : data;
      // Compatible con las dos firmas de la RPC: jsonb {ok, user_id, error} (con
      // límite de intentos) y la original, que devolvía filas {user_id, full_name}.
      if (error || row?.ok === false || !row?.user_id) {
        setDescItemModal((m) => (m ? { ...m, enviando: false, password: '' } : m));
        toast.error(row?.error || error?.message || 'No se pudo autorizar el descuento.');
        return;
      }
      autorizadoPor = row.user_id;
      autorizadoNombre = row.full_name || '';
    }

    const opcion = modal.modo === 'precio'
      ? { precioFinal: parseValorDescItem(modal.valor) }
      : { pct };
    setCarrito((curr) => curr.map((it) => (
      cartKeyDe(it) === modal.cartKey
        ? aplicarDescuentoItem(it, opcion, {
          item_discount_by: autorizadoPor,
          item_discount_reason: motivo || null,
        })
        : it
    )));
    setDescItemModal(null);
    if (autorizadoPor) toast.success(`Descuento autorizado${autorizadoNombre ? ` por ${autorizadoNombre}` : ''}.`);
  };

  const quitarItem = (cartKey) => setCarrito((curr) => curr.filter((i) => (i.cart_key || i.product_id) !== cartKey));

  const handleMetodoPagoChange = (id) => {
    setMetodoPagoId(id);
    const metodo = metodosPago.find((m) => m.id === id);
    setDescPct(Number(metodo?.discount_percentage) || 0);
    setRecPct(Number(metodo?.surcharge_percentage) || 0);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const q = (busqueda || '').trim();
    if (!q) return;

    const matchVariante = findVariantByCode(productos, q);
    if (matchVariante && isVariantTracked(matchVariante.product)) {
      agregarVariantesAlCarrito(matchVariante.product, [{ variant: matchVariante.variant, cantidad: 1 }]);
      setBusqueda('');
      return;
    }

    const matchBarcode = productos.find((p) => (p.barcode || '') === q);
    if (matchBarcode) {
      agregarAlCarrito(matchBarcode);
      setBusqueda('');
      return;
    }

    if (filtrados.length === 1) {
      agregarAlCarrito(filtrados[0]);
      setBusqueda('');
    }
  };

  const handleCameraScan = () => {
    toast('Funcionalidad de escáner de cámara: Click!', { icon: '📷' });
  };

  const validarOperacion = () => {
    if (!profile?.organization_id) return 'Sesión inválida. Reingresá.';
    // ✅ NUEVO offline: bloqueamos confirmación sin red. El SW mantiene el catálogo
    // visible para no perder productividad, pero la venta debe realizarse con red.
    if (!isOnline) return 'Sin conexión a internet. La venta no se puede confirmar hasta restablecer la red.';
    if (!sitioId) return 'Seleccioná sucursal/sitio.';
    if (!metodoPagoId) return 'Seleccioná medio de pago.';
    if (carrito.length === 0) return 'Agregá al menos un producto.';

    if (docMode !== 'presupuesto' && Number(salePaymentConditionDays || 0) > 0 && !clienteId) {
      return 'Selecciona un cliente para registrar una venta con plazo.';
    }

    if (!allowNegativeStock) {
      for (const item of carrito) {
        if (item.is_misc) continue;

        const prod = productos.find((p) => p.id === item.product_id);
        if (isVariantTracked(prod)) {
          if (!item.variant_id) return `Elegí el talle/color de ${prod?.name || 'un producto'}.`;
          const stockVariante = getVariantStockForSite(prod.stock_variantes, item.variant_id, sitioId);
          if (stockVariante - Number(item.cantidad) < 0) {
            return `Stock insuficiente: ${item.nombre || prod?.name}. Disponible ${stockVariante}.`;
          }
          continue;
        }
        const stock = getStock(prod, sitioId);
        if (stock - Number(item.cantidad) < 0) {
          return `Stock insuficiente: ${prod?.name || 'Producto'}. Disponible ${stock}.`;
        }
      }
    }

    return null;
  };

  const escapeHtml = (str) =>
    String(str ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatDateAR = (isoOrDate) => {
    try {
      const d = isoOrDate ? new Date(isoOrDate) : new Date();
      return d.toLocaleDateString('es-AR', { timeZone: 'UTC' });
    } catch {
      return String(isoOrDate || '');
    }
  };

  const formatTimeAR = (isoOrDate) => {
    if (!isoOrDate) return '';
    try {
      const d = new Date(isoOrDate);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('es-AR', {
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const buildPresupuestoHtml = ({
    saleId, cliente, items, total, fechaStr, venceISO,
    subtotalRaw, discountAmount, surchargeAmount,
    config = {}
  }) => {
    const header = config.headerText || empresa?.name || 'Presupuesto';
    const subHeader = config.subHeaderText || 'Documento no válido como factura';
    const footer = config.footerText || '';

    const width = config.printerWidth === '58mm' ? '58mm' : config.printerWidth === '80mm' ? '80mm' : '210mm';
    const isThermal = width !== '210mm';

    const empCuit = empresa?.cuit || '';
    const cliNombre = cliente?.full_name || 'Consumidor Final';
    const cliDoc = cliente?.doc_number || '';
    const cliCond = cliente?.tax_condition || 'Consumidor Final';
    const cliDir = cliente?.address || '';
    const fechaDoc = fechaStr || today;
    const venceTxt = venceISO ? formatDateAR(venceISO) : '';

    // Lógica para calcular desglose de IVA de manera cruda (asumiendo 21% por defecto si no viene del item)
    let totalTax = 0;
    const breakdown = {};
    items.forEach(it => {
      const p = Number(it.unit_price) * Number(it.quantity);
      const rate = 21; // Como VentasPOS no guarda rate, asumimos 21% para el comprobante visual
      const net = p / (1 + (rate / 100));
      const tax = p - net;
      if (!breakdown[rate]) breakdown[rate] = { net: 0, tax: 0 };
      breakdown[rate].net += net;
      breakdown[rate].tax += tax;
      totalTax += tax;
    });

    const rows = items.map(it => `
      <div class="${isThermal ? 'flex-between mb-1' : 'row'}" style="${isThermal ? '' : 'margin-bottom:2px;'}">
        <div ${isThermal ? 'style="flex: 1; padding-right: 5px;"' : ''}>${it.quantity}x ${escapeHtml(sanitizeProductDisplayName(it.product_name_snapshot || 'Producto'))}</div>
        <div ${isThermal ? 'style="white-space: nowrap;"' : ''}>$${Number(it.unit_price * it.quantity).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div>
      </div>
      ${Number(it.item_discount_amount) > 0 && Number(it.list_unit_price) > 0 ? `
         <div style="font-size: ${isThermal ? '7px' : '8px'}; color: #059669; text-align: right;">
            Precio lista $${Number(it.list_unit_price).toLocaleString('es-AR', { minimumFractionDigits: 2 })} · Desc. ${Number(Number(it.item_discount_pct).toFixed(2))}% (-$${Number(it.item_discount_amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })})
         </div>
      ` : ''}
      ${config.showItemTax ? `
         <div style="font-size: ${isThermal ? '7px' : '8px'}; color: #64748b; text-align: right;">
            (Neto: $${(Number(it.unit_price * it.quantity) / 1.21).toLocaleString('es-AR', { minimumFractionDigits: 2 })} | IVA: $${(Number(it.unit_price * it.quantity) - (Number(it.unit_price * it.quantity) / 1.21)).toLocaleString('es-AR', { minimumFractionDigits: 2 })})
         </div>
      ` : ''}
    `).join('');

    // Descuento por producto: ahorro total (líneas + descuento global).
    const ahorroItems = items
      .filter((it) => it?.combo_role !== 'component')
      .reduce((acc, it) => acc + (Number(it.item_discount_amount) > 0 ? Number(it.item_discount_amount) : 0), 0);
    const ahorroHtml = ahorroItems >= 0.01 ? `
      <div class="${isThermal ? 'flex-between mb-1' : 'row'}" style="color:#059669;font-weight:bold;">
        <span>Ahorro total</span><span>$${(ahorroItems + (Number(discountAmount) > 0 ? Number(discountAmount) : 0)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
      </div>` : '';

    // --- RENDERIZADO DINÁMICO SEGÚN EL ORDEN DE CONFIGREPORTES ---
    // Si no tiene un orden guardado, usamos uno por defecto
    const DEFAULT_ORDER = [
      'showClientInfo', 'showTaxBreakdown', 'showDiscounts', 'showQr', 'showPaymentMethod', 'showMonotributoLegend'
    ];
    const orderToUse = config.fieldsOrder && config.fieldsOrder.length > 0 ? config.fieldsOrder : DEFAULT_ORDER;

    let dynamicContent = '';

    orderToUse.forEach(field => {
      if (!config[field]) return; // Si la opción está desactivada, saltar

      switch (field) {
        case 'showClientInfo':
          dynamicContent += `
            <div class="${isThermal ? 'dashed-bottom' : 'sep'}"></div>
            <div class="${isThermal ? 'flex-between' : 'row'}"><span>Cliente:</span> <span class="bold">${escapeHtml(cliNombre)}</span></div>
            <div class="${isThermal ? 'flex-between' : 'row'}"><span>CUIT/DNI:</span> <span>${cliDoc || '-'}</span></div>
            <div class="${isThermal ? 'flex-between' : 'row'}"><span>Condición:</span> <span>${cliCond}</span></div>
            ${cliDir ? `<div class="${isThermal ? 'flex-between' : 'row'}"><span>Dir:</span> <span>${escapeHtml(cliDir)}</span></div>` : ''}
          `;
          break;

        case 'showDiscounts': // Desc/Recargos
          if (discountAmount > 0 || surchargeAmount > 0) {
            dynamicContent += `<div class="${isThermal ? 'dashed-bottom' : 'sep'}"></div>`;
            dynamicContent += `<div class="${isThermal ? 'flex-between mb-1' : 'row'} text-slate"><span>Subtotal</span><span>$ ${Number(subtotalRaw).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>`;
            if (discountAmount > 0) {
              dynamicContent += `<div class="${isThermal ? 'flex-between mb-1' : 'row'} text-emerald"><span>Descuento</span><span>-$${Number(discountAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>`;
            }
            if (surchargeAmount > 0) {
              dynamicContent += `<div class="${isThermal ? 'flex-between mb-1' : 'row'} text-rose"><span>Recargo</span><span>+$${Number(surchargeAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>`;
            }
          }
          break;

        case 'showTaxBreakdown':
          dynamicContent += `
            <div class="${isThermal ? 'dashed-bottom' : 'sep'}"></div>
            <div class="bold ${isThermal ? 'mb-1' : ''}" style="margin-bottom: 2px;">Desglose de Impuestos</div>
          `;
          if (config.showTaxPerRate) {
            Object.entries(breakdown).forEach(([rate, vals]) => {
              dynamicContent += `<div class="${isThermal ? 'flex-between' : 'row'} text-slate"><span>IVA (${rate}%)</span><span>$ ${vals.tax.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>`;
            });
          } else {
            dynamicContent += `<div class="${isThermal ? 'flex-between' : 'row'} text-slate"><span>Total IVA</span><span>$ ${totalTax.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>`;
          }
          break;

        case 'showPaymentMethod':
          // En presupuesto normalmente no hay método de pago definitivo pero si lo pide:
          dynamicContent += `
             <div class="${isThermal ? 'dashed-bottom' : 'sep'}"></div>
             <div class="${isThermal ? 'flex-between mb-1' : 'row text-slate'}"><span>Cond. de Venta:</span> <span class="bold">A Convenir</span></div>
           `;
          break;

        case 'showMonotributoLegend':
          dynamicContent += `
             <div class="${isThermal ? 'dashed-bottom mt-2' : 'sep'}" style="margin-top: 10px;"></div>
             <div style="font-size: ${isThermal ? '7px' : '8px'}; color: #64748b; text-align: justify; border: 1px dotted #ccc; padding: 4px;">
               El crédito fiscal discriminado en el presente comprobante, es computable para el régimen general de impuestos (Ley 27.618).
             </div>
          `;
          break;

        case 'showQr':
          // Presupuesto no tiene QR AFIP, se usa en ticket_venta/factura. Pero lo parseamos igual por si acaso.
          dynamicContent += `
             <div class="${isThermal ? 'dashed-bottom mt-2' : 'sep'}" style="margin-top: 10px;"></div>
             <div class="${isThermal ? 'flex-between' : 'row'}" style="align-items: center;">
                <div style="width: 40px; height: 40px; border: 1px solid #ccc; display:flex; align-items:center; justify-content:center; font-size:6px;">QR</div>
                <div style="text-align: right; font-size: ${isThermal ? '7px' : '9px'};">
                  <div class="bold">CAE: N/A (Presupuesto)</div>
                  <div>Vto: N/A</div>
                </div>
             </div>
          `;
          break;
      }
    });

    return `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>
  @page { size: ${width} auto; margin: 0; }
  body { 
    font-family: ${isThermal ? "'Courier New', monospace" : 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'};
    margin: 0; 
    padding: ${isThermal ? '5px' : '20px'}; 
    width: ${width}; 
    box-sizing: border-box; 
    font-size: ${isThermal ? '9px' : '11px'}; 
    color: #1e293b; 
    line-height: 1.4;
  }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .uppercase { text-transform: uppercase; }
  .sep { border-top: 1px dashed #cbd5e1; margin: 10px 0; }
  .dashed-bottom { border-bottom: 1px dashed #000; padding-bottom: 5px; margin-bottom: 5px; }
  .dashed-top { border-top: 1px dashed #000; padding-top: 5px; margin-top: 5px; }
  .row { display: flex; justify-content: space-between; }
  .flex-between { display: flex; justify-content: space-between; }
  .mb-1 { margin-bottom: 2px; }
  .mt-2 { margin-top: 8px; }
  .logo { max-width: ${isThermal ? '60%' : '150px'}; max-height: ${isThermal ? '80px' : '60px'}; margin: 0 auto 10px; display: block; mix-blend-mode: multiply; filter: ${isThermal ? 'grayscale(100%) contrast(120%)' : 'none'}; }
  .footer { margin-top: 20px; font-size: ${isThermal ? '8px' : '10px'}; text-align: center; color: #64748b; }
  .text-emerald { color: #059669; }
  .text-rose { color: #e11d48; }
  .text-slate { color: #64748b; }
</style>
</head>
<body>
  ${config.showLogo && config.logoBase64 ? `<img src="${config.logoBase64}" class="logo"/>` : ''}
  
  <div class="center bold uppercase" style="font-size: ${isThermal ? '11px' : '13px'};">${escapeHtml(header)}</div>
  <div class="center text-slate" style="font-size: ${isThermal ? '9px' : '9px'};">${escapeHtml(subHeader)}</div>
  <div class="center bold uppercase" style="font-size: ${isThermal ? '14px' : '18px'}; margin: 5px 0; border: 1px solid #1e293b; display: inline-block; padding: 2px 6px;">${config.voucherLetter || 'X'}</div>
  ${empCuit ? `<div class="center text-slate" style="font-size:9px;">CUIT: ${escapeHtml(empCuit)}</div>` : ''}
  
  <div class="${isThermal ? 'dashed-bottom' : 'sep'}"></div>
  
  <div class="${isThermal ? 'flex-between mb-1' : 'row'}"><span>Fecha:</span> <span>${formatDateAR(fechaDoc)}</span></div>
  <div class="${isThermal ? 'flex-between mb-1 dashed-bottom' : 'row'}"><span>Ticket:</span> <span>#${String(saleId || '').slice(0, 8).toUpperCase()}</span></div>
  
  ${isThermal ? '' : '<div class="sep"></div>'}
  
  <div style="margin-bottom: 10px;">
    ${rows}
  </div>

  <!-- INYECCIÓN DINÁMICA DE BLOQUES ORDENADOS -->
  ${dynamicContent}
  ${ahorroHtml}

  <div class="${isThermal ? 'dashed-top mt-2' : 'sep'}"></div>

  <div class="${isThermal ? 'flex-between' : 'row'} bold" style="font-size: ${isThermal ? '11px' : '15px'};">
    <span>TOTAL</span>
    <span>$ ${Number(total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
  </div>

  <div class="footer">
    <div>${escapeHtml(footer)}</div>
    ${venceTxt ? `<div style="margin-top:5px">Válido hasta: ${venceTxt}</div>` : ''}
  </div>
</body>
</html>
    `;
  };

  // Comprobante de venta: motor único en src/utils/ticketFiscal.js (compartido
  // con Facturación, Historial y Pedidos web). Acá solo se adaptan los datos en
  // memoria del POS a la forma de las filas de la base.
  const buildTicketHtml = ({
    saleId, cliente, items, total, fechaStr,
    paymentMethodName, config = {}, cae = null, caeVto = null,
    numeroFactura = null, invoiceSentAt = null, invoiceCbteTipo = null,
    qrBase64 = null, billingEntityId = null,
  }) => {
    const billingEntity = findTicketBillingEntity(billingEntityId);
    const sale = {
      id: saleId,
      total,
      cae,
      cae_vencimiento: caeVto,
      numero_factura: numeroFactura,
      invoice_cbte_tipo: invoiceCbteTipo,
      invoice_sent_at: invoiceSentAt,
      fecha_venta: fechaStr || today,
      created_at: new Date().toISOString(),
      payment_method: paymentMethodName || 'Efectivo',
    };
    return buildFiscalTicketHtml({
      sale,
      items: items || [],
      client: cliente || null,
      emitter: resolveTicketEmitter({ organization: empresa, billingEntity }),
      config,
      qrDataUrl: qrBase64,
      siteName: (sitios || []).find((s) => s.id === sitioId)?.nombre || '',
    });
  };

  // === Impresión silenciosa: usa un iframe oculto para mandar el documento
  // directo a la impresora (sin modal de previsualización en Impulza).
  // Igual aparece el diálogo nativo del navegador, salvo que el usuario
  // tenga Chrome corriendo con --kiosk-printing y la impresora por defecto.
  const silentPrintViaIframe = (html, options = {}) => {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      document.body.appendChild(iframe);

      const cleanup = () => {
        try { document.body.removeChild(iframe); } catch (_) { /* noop */ }
      };

      iframe.onload = () => {
        try {
          // Pequeño delay para asegurar que el contenido pintó antes de imprimir
          setTimeout(() => {
            try {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            } catch (e) {
              console.error('Silent print exec failed', e);
              toast.error('No se pudo iniciar la impresión silenciosa.');
            }
            // Recién limpiamos el iframe luego de que el navegador
            // haya tomado el snapshot para imprimir
            setTimeout(cleanup, 2000);
          }, 200);
        } catch (e) {
          console.error('Silent print onload failed', e);
          cleanup();
        }
      };

      iframe.srcdoc = html || '';
    } catch (e) {
      console.error(e);
      toast.error('No se pudo iniciar la impresión.');
      // Fallback: si falla la impresión silenciosa, abrir el modal normal
      setPrintModal({
        open: true,
        html: html || '',
        autoPrint: true,
        afterCloseReset: Boolean(options.afterCloseReset),
      });
    }
  };

  const openPresupuestoPrint = (html, autoPrint = false, options = {}) => {
    // Si el usuario activó "Impresión automática (sin previsualización)" en
    // Config Reportes y este disparo es de auto-print, salteamos el modal
    // y mandamos directo a la impresora.
    const silentPrintEnabled = Boolean(printConfig?.silentPrint);
    if (silentPrintEnabled && autoPrint) {
      silentPrintViaIframe(html, options);
      return;
    }

    setPrintModal({
      open: true,
      html: html || '',
      autoPrint,
      afterCloseReset: Boolean(options.afterCloseReset),
    });
  };

  const doPrintIframe = () => {
    try {
      const ifr = iframeRef.current;
      if (!ifr) return;
      const w = ifr.contentWindow;
      if (!w) return;
      w.focus();
      w.print();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo iniciar la impresión.');
    }
  };

  const resetFormulario = () => {
    setAnimacionExito(false);
    setCarrito([]);
    // El modo de comprobante vuelve SIEMPRE a 'factura' tras cerrar una operación.
    // Si queda pegado en 'presupuesto', el cajero sigue vendiendo y cada operación
    // se graba como presupuesto: no impacta stock ni caja y queda fuera del
    // Historial de Ventas (que filtra invoice_status != 'presupuesto').
    setDocMode('factura');
    setMetodoPagoId('');
    setDescPct(0);
    setRecPct(0);
    setBusqueda('');
    setSalePaymentConditionTouched(false);
    setSalePaymentConditionDays(getClientPaymentConditionDays(clienteSeleccionado));
    fetchDatos();
    searchInputRef.current?.focus();
  };

  const facturarARCADesdePOS = async (saleId, entityId, total, docReceptor, saleItemsOriginal) => {
    try {
      const traceId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const emisor = billingEntities.find(e => e.id === entityId);
      if (!emisor) throw new Error("Emisor por defecto no encontrado");

      let projectUrl = supabase.supabaseUrl;
      if (projectUrl.endsWith('/')) projectUrl = projectUrl.slice(0, -1);
      const functionUrl = `${projectUrl}/functions/v1/arca-facturar`;

      const condEmisor = emisor.fiscal_configuration || emisor.tax_condition || empresa?.tax_condition || '';
      const condReceptor = docReceptor.tax_condition || 'Consumidor Final';
      const docNro = docReceptor.doc_number || 0;
      const docTipo = docReceptor.doc_type || 'DNI';

      let cbteTipo = 11;
      const cEm = String(condEmisor || '').toLowerCase();
      const cRe = String(condReceptor || '').toLowerCase();
      const esMonotributo = cEm.includes('monotrib');
      const esRI = cEm.includes('responsable') && cEm.includes('inscrip');

      if (esMonotributo) cbteTipo = 11;
      else if (esRI) {
        if (cRe.includes('responsable') && cRe.includes('inscrip')) cbteTipo = 1;
        else if (cRe.includes('monotrib')) cbteTipo = 1;
        else cbteTipo = 6;
      }

      let condicionIVAReceptorId = 5;
      if (cRe.includes('responsable') && cRe.includes('inscrip')) condicionIVAReceptorId = 1;
      else if (cRe.includes('monotrib')) condicionIVAReceptorId = 6;
      else if (cRe.includes('exento')) condicionIVAReceptorId = 4;

      await supabase.from('sales').update({ invoice_cbte_tipo: cbteTipo, invoice_status: 'enviado', invoice_sent_at: new Date().toISOString() }).eq('id', saleId);

      // IVA por alícuota: salvo Factura/NC C (monotributo, sin discriminar IVA),
      // mandamos el desglose explícito para que el CAE coincida con el ticket.
      const esComprobanteC = cbteTipo === 11 || cbteTipo === 13;
      const billableItems = selectBillableItems(saleItemsOriginal);
      const breakdown = esComprobanteC ? null : buildAfipIvaBreakdown(saleItemsOriginal, { pricesIncludeIva: true, saleTotal: Number(total) });

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          sale_id: saleId,
          trace_id: traceId,
          billing_entity_id: entityId,
          cbte_tipo: cbteTipo,
          condicion_iva_receptor_id: condicionIVAReceptorId,
          condicion_iva_receptor: condReceptor,
          total: total,
          prices_include_iva: true,
          cliente: { doc_tipo: docTipo, doc_nro: docNro, tax_condition: condReceptor },
          items: billableItems,
          ...(breakdown ? { iva: breakdown.iva, totals: breakdown.totals } : {})
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.message || 'Error desconocido AFIP');
      }
    } catch (e) {
      console.error('Facturación asíncrona POS falló:', e);
      await supabase.from('sales').update({ invoice_status: 'error' }).eq('id', saleId);
    }
  };

  // cobroDecision: 'ahora' | 'cuenta' | null. Lo fija el modal de cobro a cliente
  // identificado; con null (consumidor final) se decide como siempre. El onClick
  // del botón pasa el evento, que no trae cobroDecision → null.
  const finalizarVenta = async (opts) => {
    if (procesando) return;
    const cobroDecision = opts?.cobroDecision === 'ahora' || opts?.cobroDecision === 'cuenta'
      ? opts.cobroDecision
      : null;

    // === DEMO GUIADO = MOCK: venta simulada (no toca la base ni el stock) ===
    // Leemos el estado fresco (no dependemos de timing de React). Si el recorrido
    // esta en 'sell-product', calculamos los numeros en memoria, los guardamos en
    // el estado del demo y avanzamos a rentabilidad. Nunca creamos venta ni
    // descontamos stock, y por eso tampoco aplica el control de stock.
    const demoNow = readFirstSaleOnboarding();
    const inDemoSell =
      matchesFirstSaleOnboardingIdentity(demoNow, {
        organizationId: profile?.organization_id,
        userId: profile?.id || null,
      }) &&
      demoNow?.stepId === 'sell-product' &&
      Boolean(demoNow?.product?.id);

    if (inDemoSell) {
      const demoItem = carrito.find((it) => it.product_id === demoNow.product.id);
      if (!demoItem) {
        return toast.error('Agregá el producto del demo al carrito para continuar.');
      }
      const quantity = Number(demoItem.cantidad || 1);
      const unitPrice = Number(demoItem.unit_price || demoNow.product.sellPrice || 0);
      const revenue = Number((quantity * unitPrice).toFixed(2));
      const costUnit = Number(demoNow.product.costPrice || 0);
      const cost = Number((quantity * costUnit).toFixed(2));
      const profit = Number((revenue - cost).toFixed(2));
      const marginPct = revenue > 0 ? Number(((profit / revenue) * 100).toFixed(2)) : 0;
      const metodoDemo = metodosPago.find((m) => m.id === metodoPagoId);

      goToFirstSaleOnboardingStep('review-profitability', {
        sale: {
          id: `demo-sale-${Date.now()}`,
          date: fecha || new Date().toISOString().slice(0, 10),
          total: revenue,
          paymentMethodId: metodoPagoId || null,
          paymentMethodName: metodoDemo?.name || 'Efectivo',
          productId: demoNow.product.id,
          productName: demoNow.product.name,
          quantity,
          unitPrice,
          revenue,
          cost,
          profit,
          marginPct,
        },
      });
      toast.success('Venta de demo simulada (no se grabó). Vamos a ver la rentabilidad.');
      setCarrito([]);
      setTimeout(() => {
        if (onNavigate) onNavigate('rentabilidad-ventas');
        else navigate('/sistema/rentabilidad-ventas');
      }, 850);
      return;
    }
    // === fin demo ===

    const err = validarOperacion();
    if (err) return toast.error(err);

    // Descuento por producto: se revalida contra la config FRESCA de la base. Un
    // POS abierto antes de apagar la función o de bajar el tope no puede cobrar
    // con las reglas viejas. Solo consulta si el carrito tiene descuentos de línea.
    if (carrito.some(tieneDescuentoItem)) {
      let cfgDescuento;
      try {
        const { data: orgDesc, error: orgDescErr } = await supabase
          .from('organizations')
          .select('enable_item_discount, item_discount_max_pct, item_discount_reason_from_pct')
          .eq('id', profile.organization_id)
          .single();
        if (orgDescErr) throw orgDescErr;
        cfgDescuento = normalizarConfigDescuentoItem(orgDesc);
      } catch (e) {
        console.warn('[POS] No se pudo revalidar el descuento por producto:', e?.message || e);
        return toast.error('No se pudo verificar la configuración de descuentos. Reintentá.');
      }
      const problemas = validarDescuentosCarrito(carrito, cfgDescuento, { puedeAutorizar: puedeAutorizarDescuento });
      if (problemas.some((pr) => pr.tipo === 'desactivado')) {
        setCarrito((curr) => curr.map((it) => (tieneDescuentoItem(it) ? quitarDescuentoItem(it) : it)));
        // El organization del AuthContext quedó viejo: se refresca para que el
        // botón "Desc." desaparezca de las líneas.
        refreshOrganization?.();
        return toast.error('El descuento por producto fue desactivado en Empresa → Datos. Se quitaron los descuentos de las líneas: revisá el total y volvé a confirmar.', { duration: 6000 });
      }
      if (problemas.some((pr) => pr.tipo === 'supera_tope')) {
        return toast.error(`Un descuento supera el tope actual (${cfgDescuento.maxPct}%). Volvé a cargarlo para pedir autorización.`, { duration: 6000 });
      }
      if (problemas.some((pr) => pr.tipo === 'falta_motivo')) {
        return toast.error('Falta el motivo de un descuento por producto.', { duration: 6000 });
      }
    }

    // Confirmación explícita: un presupuesto NO es una venta. Sin este paso, un
    // cajero que dejó el modo en 'presupuesto' sigue operando sin registrar nada.
    if (docMode === 'presupuesto') {
      const confirmaPresupuesto = window.confirm(
        'Vas a generar un PRESUPUESTO, no una venta.\n\n' +
        '• No descuenta stock\n' +
        '• No registra el cobro en caja\n' +
        '• No aparece en el Historial de Ventas\n\n' +
        'Para cobrar, cancelá y elegí el modo "Factura".\n\n¿Generar el presupuesto igual?'
      );
      if (!confirmaPresupuesto) return;
    }

    // Cliente identificado (no el consumidor final genérico): el cajero decide si
    // está cobrando ahora o si la venta queda en cuenta corriente. Antes lo decidía
    // solo la condición de pago del cliente, sin que el cajero lo viera.
    if (
      docMode !== 'presupuesto' &&
      !cobroDecision &&
      clienteSeleccionado &&
      !isGenericConsumidorFinalClient(clienteSeleccionado)
    ) {
      setCobroClienteModal(true);
      return;
    }
    setCobroClienteModal(false);

    setProcesando(true);
    let createdSaleId = null;
    let stockRollbackContext = null;
    // Si el cobro MP QR ya fue aprobado, NUNCA borramos la venta ante un error
    // posterior: la plata ya entró y borrar el registro la dejaría sin rastro.
    let mpPagoAprobado = false;

    try {
      const orgId = profile.organization_id;
      const authUserId = profile?.user_id || null;

      const metodo = metodosPago.find((m) => m.id === metodoPagoId);
      const cliente = clientes.find((c) => c.id === clienteId);
      const sucursal = sitios.find((s) => s.id === sitioId);
      const comprobante = comprobantes.find((c) => c.id === comprobanteId);
      // Cobrar ahora = contado (0 días, etiqueta "Contado"): si se dejara la
      // condición del cliente (p. ej. 30 días), isCreditTermsSale la contaría como
      // deuda en la cuenta corriente aunque ya se cobró.
      const baseSalePaymentTerms = buildSalePaymentTerms({
        createdAt: getForgedPOSDate(fecha),
        client: cliente,
        paymentConditionDays: cobroDecision === 'ahora' ? 0 : salePaymentConditionDays,
      });
      // A cuenta corriente con condición contado (0 días): vence hoy, pero la
      // etiqueta no puede decir "Contado" o isCreditTermsSale no la toma como deuda.
      const salePaymentTerms = cobroDecision === 'cuenta' && baseSalePaymentTerms.days === 0
        ? { ...baseSalePaymentTerms, label: 'Cuenta corriente' }
        : baseSalePaymentTerms;
      const shouldDeferCollection = cobroDecision === 'ahora'
        ? false
        : cobroDecision === 'cuenta'
        ? true
        : shouldUseClientAccountForSale({
            organization: empresa,
            client: cliente,
            paymentConditionDays: salePaymentTerms.days,
          });



      const createdAt = getForgedPOSDate(fecha);
      const initialInvoiceStatus = 'pendiente';

      const isPresupuesto = docMode === 'presupuesto';
      const venceDias = Math.max(0, Number(presupuestoVenceEnDias || 0));
      const venceISO = isPresupuesto && venceDias > 0 ? new Date(Date.now() + venceDias * 86400000).toISOString() : null;

      const subtotalRaw = carrito.reduce((sum, item) => sum + (Number(item.subtotal) || 0), 0);
      let realDiscountAmt = 0;
      let realSurchargeAmt = 0;

      carrito.forEach(item => {
        const base = Number(item.subtotal);
        const discountFactor = !item.block_discounts && descPct > 0 ? (descPct / 100) : 0;
        const surchargeFactor = !item.block_surcharges && recPct > 0 ? (recPct / 100) : 0;

        const disc = base * discountFactor;
        const sur = (base - disc) * surchargeFactor;

        realDiscountAmt += disc;
        realSurchargeAmt += sur;
      });

      const metodosAFIP = printConfig?.facturacion_metodos;
      const isMetodoAuto = !metodosAFIP || metodosAFIP.length === 0 || metodosAFIP.includes(metodoPagoId);
      const isSitioAuto = !printConfig?.facturacion_sitio || printConfig.facturacion_sitio === sitioId;

      // El emisor por defecto y el modo auto se leen FRESCOS de la base al cerrar la
      // venta, no de la copia de `organization` del AuthContext (que se cachea una
      // sola vez por sesión). Así, si se cambia el "Emisor por defecto" en Facturación,
      // las ventas nuevas toman el valor actual sin necesidad de recargar el POS.
      let freshDefaultBillingEntityId = defaultBillingEntityId;
      let freshAutoInvoiceEnabled = autoInvoiceEnabled;
      try {
        const { data: orgFresh, error: orgFreshError } = await supabase
          .from('organizations')
          .select('default_billing_entity_id, auto_invoice_enabled')
          .eq('id', orgId)
          .single();
        if (!orgFreshError && orgFresh) {
          freshDefaultBillingEntityId = orgFresh.default_billing_entity_id ?? null;
          freshAutoInvoiceEnabled = orgFresh.auto_invoice_enabled ?? false;
          // Mantener el estado local y el indicador de UI en sincronía con lo leído.
          setDefaultBillingEntityId(freshDefaultBillingEntityId);
          setAutoInvoiceEnabled(freshAutoInvoiceEnabled);
        }
      } catch (e) {
        // Si la lectura falla, caemos a los valores en memoria (comportamiento previo).
        console.warn('[POS] No se pudo refrescar el emisor por defecto, uso valor en cache:', e?.message || e);
      }

      const isAutoInvoice = freshAutoInvoiceEnabled && freshDefaultBillingEntityId && docMode === 'factura' && isMetodoAuto && isSitioAuto;
      // Siempre asignar el emisor por defecto de la org (si existe), incluso sin auto-facturación.
      // Así la venta aparece correctamente en el módulo de Facturación desde el inicio.
      const billingEntityIdToSave = isAutoInvoice ? freshDefaultBillingEntityId : (freshDefaultBillingEntityId || null);

      const saleBasePayload = {
        organization_id: orgId,
        client_id: clienteId || null,
        site_id: sitioId,
        total: totalFinal,
        payment_method: metodo?.name || 'N/D',
        payment_method_id: metodoPagoId || null,
        status: (isPresupuesto || (!shouldDeferCollection && (metodo?.gateway_type === 'MERCADOPAGO' || metodo?.gateway_type === 'GETNET'))) ? 'pending' : 'completed',
        invoice_status: isPresupuesto ? 'presupuesto' : initialInvoiceStatus,
        active: true,
        user_id: authUserId,
        created_at: createdAt,
        billing_entity_id: billingEntityIdToSave,
        billing_source: isAutoInvoice ? 'auto_pos' : (billingEntityIdToSave ? 'default' : null),
        invoice_sent_at: null,
        invoice_type_label: isPresupuesto ? 'Presupuesto' : (comprobante?.name || null),
        invoice_cbte_tipo: null
      };
      const salePaymentTermsPayload = {
        payment_condition_days: salePaymentTerms.days,
        payment_condition_label: salePaymentTerms.label,
        payment_due_date: salePaymentTerms.dueDate,
      };

      const buildSaleInsertPayload = ({ includePaymentTerms = true, includeFechaVenta = true } = {}) => ({
        ...saleBasePayload,
        ...(includeFechaVenta ? { fecha_venta: fecha } : {}),
        ...(includePaymentTerms ? salePaymentTermsPayload : {}),
      });

      let includePaymentTerms = getSalePaymentTermsColumnsSupport() !== false;
      let includeFechaVenta = true;
      let saleResponse;

      while (true) {
        saleResponse = await supabase
          .from('sales')
          .insert([buildSaleInsertPayload({ includePaymentTerms, includeFechaVenta })])
          .select()
          .single();

        if (!saleResponse.error) {
          if (includePaymentTerms) setSalePaymentTermsColumnsSupport(true);
          break;
        }

        if (includeFechaVenta && isMissingColumnError(saleResponse.error, 'fecha_venta')) {
          includeFechaVenta = false;
          continue;
        }

        if (includePaymentTerms && isMissingSalePaymentTermsColumnsError(saleResponse.error)) {
          setSalePaymentTermsColumnsSupport(false);
          includePaymentTerms = false;
          continue;
        }

        break;
      }

      const { data: sale, error: saleErr } = saleResponse;

      if (saleErr) throw saleErr;
      createdSaleId = sale.id;

      // Construir filas de sale_items. Para combos/promos "explotamos" la venta
      // en una fila parent (resumen, sin impacto en CMV) + N filas component
      // (los ingredientes reales, con revenue prorrateado por costo). Esto permite
      // que la rentabilidad se calcule contra el costo real de cada componente y
      // que los rankings de productos vendidos incluyan a las empanadas, no a la promo.
      // Resuelve la alícuota de IVA de un producto del catálogo (snapshot).
      // Para ítems misceláneos (sin product_id) cae a 21% por defecto.
      const resolveItemIva = (productId) => {
        if (!productId) return 21;
        const prod = productos.find((p) => p.id === productId);
        const v = Number(prod?.iva_percentage);
        return Number.isNaN(v) ? 21 : v;
      };

      const itemsToInsert = [];
      for (const item of carrito) {
        const baseRow = {
          sale_id: sale.id,
          organization_id: orgId,
          product_id: item.is_misc ? null : item.product_id,
          variant_id: item.variant_id || null,
          product_name_snapshot: item.nombre,
          quantity: Number(item.cantidad) || 1,
          unit_price: Number(item.unit_price) || 0,
          subtotal: Number(item.subtotal) || ((Number(item.cantidad) || 1) * (Number(item.unit_price) || 0)),
          // Snapshot de alícuota: fuente de verdad para AFIP, reimpresión y reportes.
          iva_percentage: item.is_misc ? 21 : resolveItemIva(item.product_id),
          // Descuento por producto: unit_price/subtotal ya son el neto de la línea;
          // acá queda el precio de lista, el % y quién lo aplicó o autorizó.
          ...camposDescuentoSaleItem(item, { userId: profile?.id || null }),
        };

        const esCombo = !item.is_misc && item.product_type === 'Combo';
        if (!esCombo) {
          itemsToInsert.push(baseRow);
          continue;
        }

        // Resolver ingredientes igual que buildStockOperationsForSale: dinámico
        // toma los seleccionados en POS, estático toma product_combo_items.
        const esDinamico = item.is_dynamic_combo && Array.isArray(item.dynamic_components) && item.dynamic_components.length > 0;
        const comboDef = combos.find((c) => c.product_id === item.product_id);
        const ingredientes = esDinamico
          ? item.dynamic_components.map((dc) => ({
              product_id: dc.product_id,
              quantity: dc.quantity,
              name: dc.name,
            }))
          : (comboDef ? comboItems.filter((ci) => ci.combo_id === comboDef.id) : []);

        // Combo sin ingredientes: fallback al comportamiento viejo (1 sola fila),
        // mejor que romper la venta.
        if (!ingredientes.length) {
          itemsToInsert.push(baseRow);
          continue;
        }

        const cantidadCombo = Number(item.cantidad) || 1;
        const subtotalCombo = Number(baseRow.subtotal) || 0;

        // 1) Fila parent (resumen del combo). NO suma a CMV ni a ranking de vendidos.
        itemsToInsert.push({ ...baseRow, combo_role: 'parent' });

        // 2) Calcular costo total del combo (por unidad de combo) para prorratear.
        const ingredientesConCosto = ingredientes.map((ing) => {
          const prodComp = productos.find((p) => p.id === ing.product_id);
          const costoUnitario = Number(prodComp?.cost_price) || 0;
          const qtyEnCombo = Number(ing.quantity) || 0;
          return {
            ing,
            prodComp,
            qtyEnCombo,
            costoLinea: costoUnitario * qtyEnCombo,
          };
        });
        const costoTotalUnitario = ingredientesConCosto.reduce((acc, x) => acc + x.costoLinea, 0);

        // 3) Filas component con revenue prorrateado.
        for (const { ing, prodComp, qtyEnCombo, costoLinea } of ingredientesConCosto) {
          if (!prodComp || qtyEnCombo <= 0) continue;
          const cantidadComponente = qtyEnCombo * cantidadCombo;

          // Prorrateo por costo. Si todos los componentes tienen costo 0
          // (datos sucios), fallback a reparto equitativo para no perder revenue.
          let revenueComponente;
          if (costoTotalUnitario > 0) {
            revenueComponente = (costoLinea / costoTotalUnitario) * subtotalCombo;
          } else {
            revenueComponente = subtotalCombo / ingredientesConCosto.length;
          }
          const unitPriceComponente = cantidadComponente > 0 ? revenueComponente / cantidadComponente : 0;

          itemsToInsert.push({
            sale_id: sale.id,
            organization_id: orgId,
            product_id: ing.product_id,
            variant_id: null,
            product_name_snapshot: ing.name || prodComp.name,
            quantity: cantidadComponente,
            unit_price: Number(unitPriceComponente.toFixed(2)),
            subtotal: Number(revenueComponente.toFixed(2)),
            parent_combo_product_id: item.product_id,
            combo_role: 'component',
            iva_percentage: (() => { const v = Number(prodComp?.iva_percentage); return Number.isNaN(v) ? 21 : v; })(),
          });
        }
      }

      const { error: itemsErr } = await insertRowsWithMissingColumnFallback(
        'sale_items',
        itemsToInsert,
        [
          'variant_id', 'subtotal', 'parent_combo_product_id', 'combo_role', 'iva_percentage',
          'list_unit_price', 'item_discount_pct', 'item_discount_amount', 'item_discount_by', 'item_discount_reason',
        ]
      );
      if (itemsErr) throw itemsErr;

      // --- LÓGICA MERCADO PAGO QR PRESENCIAL ---
      // mp_mode ausente = método creado antes de Point: se trata como QR.
      const mpMode = String(metodo?.mp_mode || 'QR').toUpperCase();
      if (!isPresupuesto && !shouldDeferCollection && metodo?.gateway_type === 'MERCADOPAGO' && mpMode !== 'POINT') {
        // Se relee la sucursal por si se editó el ID de caja en otra pestaña.
        const { data: sucursalFresca } = await supabase.from('sitios').select('mp_pos_id').eq('id', sitioId).single();
        const qrInstoreId = sucursalFresca?.mp_pos_id || sucursal?.mp_pos_id;

        if (!qrInstoreId) {
          throw new Error(`La sucursal ${sucursal?.nombre || 'seleccionada'} no tiene ID de Caja QR. Cargalo en Configuración → Sucursales.`);
        }

        // PRE-CHEQUEO DE STOCK: lo validamos ANTES de cobrar. Antes el stock se
        // descontaba (y validaba) DESPUÉS de aprobar el pago; si fallaba por
        // "stock insuficiente", el catch borraba la venta entera con el cobro
        // ya hecho. Si acá falta stock, abortamos sin que haya plata en juego.
        if (!allowNegativeStock) {
          const preOperations = buildStockOperationsForSale(
            carrito,
            comprobante?.name || 'Comprobante',
            String(sale.id).slice(0, 8).toUpperCase(),
          );
          const requiredByProduct = new Map();
          for (const operation of preOperations) {
            const current = requiredByProduct.get(operation.productId);
            requiredByProduct.set(operation.productId, {
              product: operation.product,
              quantity: (current?.quantity || 0) + (Number(operation.quantity) || 0),
            });
          }
          for (const [productId, required] of requiredByProduct.entries()) {
            const snapshot = await loadStockSnapshotForSale({
              product: required.product,
              productId,
              siteId: sitioId,
            });
            if (snapshot.effectiveQty - required.quantity < 0) {
              throw new Error(`Stock insuficiente para cobrar por QR: ${snapshot.productName}. Disponible ${snapshot.effectiveQty}.`);
            }
          }
          await assertVariantStockBeforeCharge(preOperations, sitioId, 'QR');
        }

        toast.loading('Publicando pedido al QR...', { id: 'mp-instore' });
        const efRes = await supabase.functions.invoke('mp-instore-order', {
          body: {
            organizationId: orgId,
            posId: qrInstoreId,
            items: itemsToInsert.map(i => ({ id: i.product_id, title: i.product_name_snapshot, unit_price: i.unit_price, quantity: i.quantity })),
            totalAmount: totalFinal,
            externalReference: sale.id,
            title: `Ticket #${String(sale.id).slice(0, 8).toUpperCase()}`
          }
        });

        if (efRes.error || (efRes.data && efRes.data.error)) {
          toast.dismiss('mp-instore');
          throw new Error(`MP Error: ${efRes.error?.message || efRes.data?.error}`);
        }

        toast.success('¡Listo! Pida al cliente escanear el QR.', { id: 'mp-instore' });
        setQrImageUrl(null);
        setModoEspera('QR');
        setEsperandoPagoQR(true);
        
        supabase.functions.invoke('mp-get-qr', {
          body: { organizationId: orgId, posId: qrInstoreId }
        }).then(res => {
          if (res.data?.success && res.data.qr?.image) {
            setQrImageUrl(res.data.qr.image);
          }
        }).catch(err => console.log('Error fetching QR image:', err));

        let manualCancel = false;
        const mpSuccess = await new Promise((resolve) => {
          const timeoutId = setTimeout(() => { resolve(false); }, 180000);
          setCancelarQRPromise(() => () => { manualCancel = true; resolve(false); });

          const channel = supabase.channel(`sale_${sale.id}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sales', filter: `id=eq.${sale.id}` }, (payload) => {
              if (payload.new.status === 'Pagado' || payload.new.status === 'completed') {
                clearTimeout(timeoutId);
                channel.unsubscribe();
                resolve(true);
              }
            })
            .subscribe();
        });

        setEsperandoPagoQR(false);

        let pagoConfirmado = mpSuccess;
        if (!pagoConfirmado) {
          // RACE del webhook: el pago pudo aprobarse justo después del timeout
          // (o de la cancelación manual). Antes se borraba la venta sin mirar:
          // si el webhook ya la había marcado pagada, quedaba plata cobrada sin
          // venta. Re-chequeamos el estado real antes de decidir.
          const { data: saleCheck } = await supabase
            .from('sales')
            .select('status')
            .eq('id', sale.id)
            .maybeSingle();
          const lateStatus = String(saleCheck?.status || '').toLowerCase();
          if (lateStatus === 'pagado' || lateStatus === 'completed') {
            pagoConfirmado = true;
            toast.success('El pago se acreditó sobre el límite de tiempo: la venta continúa.');
          }
        }

        if (!pagoConfirmado) {
          await cleanupFailedSale(sale.id);
          createdSaleId = null;

          if (manualCancel) {
            toast.error("Cobro QR cancelado. El carrito se ha conservado para reintentar.");
          } else {
            toast.error("Expiró el tiempo de espera. El carrito se ha conservado para reintentar.");
          }
          setProcesando(false);
          // Quitamos setTimeout(resetFormulario...) para conservar carrito intacto
          return;
        }

        mpPagoAprobado = true;
        toast.success("¡Cobro MP Aprobado exitosamente!");
      }

      // --- LÓGICA MERCADO PAGO POINT: TERMINAL FÍSICA (POSNET) ---
      // Point NO usa la API de instore/QR: va por la Orders API. El cobro se
      // manda a la terminal de la sucursal y el webhook (topic "order") marca la
      // venta como Pagado. Ver PLAN_MP_POINT_POS.md.
      if (!isPresupuesto && !shouldDeferCollection && metodo?.gateway_type === 'MERCADOPAGO' && mpMode === 'POINT') {
        const { data: sucursalFresca } = await supabase
          .from('sitios')
          .select('mp_terminal_id')
          .eq('id', sitioId)
          .single();

        const terminalId = sucursalFresca?.mp_terminal_id || sucursal?.mp_terminal_id;
        if (!terminalId) {
          throw new Error(`La sucursal ${sucursal?.nombre || 'seleccionada'} no tiene terminal Point configurada. Cargala en Configuración → Sucursales.`);
        }

        // PRE-CHEQUEO DE STOCK: se valida ANTES de cobrar. Si el pago se aprueba
        // y recién ahí falla el descuento por falta de stock, el catch borra la
        // venta con la plata ya cobrada. Mismo criterio que las ramas QR y Getnet.
        if (!allowNegativeStock) {
          const preOperations = buildStockOperationsForSale(
            carrito,
            comprobante?.name || 'Comprobante',
            String(sale.id).slice(0, 8).toUpperCase(),
          );
          const requiredByProduct = new Map();
          for (const operation of preOperations) {
            const current = requiredByProduct.get(operation.productId);
            requiredByProduct.set(operation.productId, {
              product: operation.product,
              quantity: (current?.quantity || 0) + (Number(operation.quantity) || 0),
            });
          }
          for (const [productId, required] of requiredByProduct.entries()) {
            const snapshot = await loadStockSnapshotForSale({
              product: required.product,
              productId,
              siteId: sitioId,
            });
            if (snapshot.effectiveQty - required.quantity < 0) {
              throw new Error(`Stock insuficiente para cobrar con Point: ${snapshot.productName}. Disponible ${snapshot.effectiveQty}.`);
            }
          }
          await assertVariantStockBeforeCharge(preOperations, sitioId, 'Point');
        }

        toast.loading('Enviando el cobro a la terminal…', { id: 'mp-point' });

        const efRes = await supabase.functions.invoke('mp-point-order', {
          body: {
            organizationId: orgId,
            saleId: sale.id,
            terminalId,
            totalAmount: totalFinal,
            ticketNumber: String(sale.id).slice(0, 8).toUpperCase(),
            description: `Ticket #${String(sale.id).slice(0, 8).toUpperCase()}`,
            defaultType: metodo?.mp_default_type || 'credit_card',
            installments: metodo?.mp_default_installments || 1,
            printOnTerminal: Boolean(metodo?.mp_print_on_terminal),
          },
        });

        if (efRes.error || (efRes.data && efRes.data.error)) {
          toast.dismiss('mp-point');
          throw new Error(`Point: ${efRes.error?.message || efRes.data?.error}`);
        }

        const pointOrderId = efRes.data?.order_id || null;

        // Si la edge function no pudo registrar el intento, lo hacemos acá: sin
        // esa fila, una venta que termine en revisión no se puede resolver
        // porque la bandeja no sabe qué orden consultar.
        if (pointOrderId && efRes.data?.recorded === false) {
          const { error: registroError } = await supabase.from('sale_gateway_payments').insert({
            organization_id: orgId,
            sale_id: sale.id,
            provider: 'MERCADOPAGO',
            mode: 'POINT',
            gateway_order_id: pointOrderId,
            status: 'created',
            amount: totalFinal,
          });
          if (registroError) console.error('No se pudo registrar el intento de cobro:', registroError);
        }

        toast.success('Pedile al cliente que pase la tarjeta por la terminal.', { id: 'mp-point' });

        // Modal de espera sin QR: acá el cliente opera contra el posnet.
        setQrImageUrl(null);
        setModoEspera('TERMINAL');
        setEsperandoPagoQR(true);

        let manualCancel = false;
        const pointSuccess = await new Promise((resolve) => {
          // El canal se desuscribe en TODAS las salidas (pago, timeout y
          // cancelación manual). Las ramas QR y Getnet solo lo hacen en el pago
          // y dejan el canal abierto; acá no repetimos esa deuda.
          //
          // Declaramos finish ANTES del canal y del timeout: aunque el callback
          // del canal sea asincrónico, depender de eso para no caer en TDZ es
          // frágil (basta que Supabase cambie a invocarlo sincrónicamente).
          let settled = false;
          let timeoutId = null;
          let channel = null;

          const finish = (value) => {
            if (settled) return;
            settled = true;
            if (timeoutId) clearTimeout(timeoutId);
            try { channel?.unsubscribe(); } catch { /* el canal ya podía estar cerrado */ }
            resolve(value);
          };

          timeoutId = setTimeout(() => { finish(false); }, POINT_TIMEOUT_MS);
          setCancelarQRPromise(() => () => { manualCancel = true; finish(false); });

          channel = supabase.channel(`sale_${sale.id}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sales', filter: `id=eq.${sale.id}` }, (payload) => {
              if (payload.new.status === 'Pagado' || payload.new.status === 'completed') {
                finish(true);
              }
            })
            .subscribe();
        });

        setEsperandoPagoQR(false);

        let pagoConfirmado = pointSuccess;

        if (!pagoConfirmado) {
          // RACE del webhook: el pago pudo aprobarse justo sobre el límite.
          const { data: saleCheck } = await supabase
            .from('sales')
            .select('status')
            .eq('id', sale.id)
            .maybeSingle();
          const lateStatus = String(saleCheck?.status || '').toLowerCase();
          if (lateStatus === 'pagado' || lateStatus === 'completed') {
            pagoConfirmado = true;
            toast.success('El pago se acreditó sobre el límite de tiempo: la venta continúa.');
          }
        }

        if (!pagoConfirmado) {
          // CANCELACIÓN REAL. Este es el punto crítico de todo el flujo: si acá
          // borramos la venta y la terminal sigue pidiendo la tarjeta, el cliente
          // puede pagar una venta que ya no existe. Solo se limpia con la
          // cancelación CONFIRMADA por Mercado Pago.
          //
          // La venta pasa a revisión ANTES de intentar cancelar: si el invoke se
          // cuelga (que es justo el escenario probable, porque acá Mercado Pago
          // ya viene fallando), la venta queda en un estado que NO cuenta como
          // ingreso, en vez de quedar en 'pending' —que sí contaría—.
          await supabase
            .from('sales')
            .update({ status: SALE_STATUS_REVISION })
            .eq('id', sale.id)
            .eq('organization_id', orgId);

          toast.loading('Cancelando el cobro en la terminal…', { id: 'mp-point-cancel' });

          let cancelData = null;
          try {
            // Timeout propio: functions.invoke no lo trae, y sin esto un MP
            // degradado deja el POS bloqueado con el spinner girando.
            cancelData = await Promise.race([
              supabase.functions
                .invoke('mp-point-cancel', {
                  body: { organizationId: orgId, orderId: pointOrderId, saleId: sale.id },
                })
                .then((r) => r.data || null),
              new Promise((resolve) => setTimeout(() => resolve(null), 20000)),
            ]);
          } catch (cancelErr) {
            console.error('Error cancelando la orden de Point:', cancelErr);
          }
          toast.dismiss('mp-point-cancel');

          // El cobro entró mientras cancelábamos. NO se marca Pagado: esta venta
          // ya cortó antes de descontar stock y de asentar caja/diario, así que
          // pasarla a Pagado crearía un ingreso sin respaldo. Queda en revisión
          // con el cobro identificado y se resuelve desde la bandeja.
          if (cancelData?.alreadyPaid) {
            createdSaleId = null;
            toast.error(
              'El cobro se aprobó justo mientras se cancelaba. La venta quedó en revisión: anulala y registrala de nuevo con el cobro ya hecho.',
              { duration: 12000 },
            );
            setProcesando(false);
            return;
          }

          if (cancelData?.canceled) {
            // Cancelación confirmada: la terminal dejó de pedir tarjeta.
            await cleanupFailedSale(sale.id);
            createdSaleId = null;
            toast.error(manualCancel
              ? 'Cobro cancelado. El carrito se conservó para reintentar.'
              : 'Expiró el tiempo de espera. El carrito se conservó para reintentar.');
            setProcesando(false);
            return;
          } else {
            // NO sabemos si la terminal cobró (o el cancel se colgó). La venta ya
            // quedó en revisión más arriba: no cuenta como ingreso y no descontó
            // stock. Se resuelve desde Ventas → Cobros a verificar.
            // createdSaleId = null evita que el catch de más abajo la borre.
            createdSaleId = null;
            toast.error(
              'No se pudo confirmar la cancelación en la terminal. La venta quedó en revisión: verificá el posnet antes de reintentar.',
              { duration: 9000 },
            );
            setProcesando(false);
            return;
          }
        }

        mpPagoAprobado = true;
        toast.success('¡Cobro con terminal aprobado!');
      }

      // --- LÓGICA GETNET (GEOPAGOS): QR PRESENCIAL O TERMINAL SMARTPOS ---
      if (!isPresupuesto && !shouldDeferCollection && metodo?.gateway_type === 'GETNET') {
        const getnetMode = metodo?.getnet_mode || 'QR';
        const getnetPosId = metodo?.getnet_pos_id;

        if (!getnetPosId) {
          throw new Error('El método de cobro Getnet no tiene configurado el Point of Sale ID. Cargalo en Configuración → Métodos de Cobro.');
        }

        // PRE-CHEQUEO DE STOCK antes de cobrar (mismo criterio que el branch de MP/QR):
        // si el pago se aprueba y recién ahí falla el descuento por falta de stock, la
        // venta queda 'completed' sin stock/caja/diario. Abortamos antes de cobrar. Bug V3.
        if (!allowNegativeStock) {
          const preOperations = buildStockOperationsForSale(
            carrito,
            comprobante?.name || 'Comprobante',
            String(sale.id).slice(0, 8).toUpperCase(),
          );
          const requiredByProduct = new Map();
          for (const operation of preOperations) {
            const current = requiredByProduct.get(operation.productId);
            requiredByProduct.set(operation.productId, {
              product: operation.product,
              quantity: (current?.quantity || 0) + (Number(operation.quantity) || 0),
            });
          }
          for (const [productId, required] of requiredByProduct.entries()) {
            const snapshot = await loadStockSnapshotForSale({
              product: required.product,
              productId,
              siteId: sitioId,
            });
            if (snapshot.effectiveQty - required.quantity < 0) {
              throw new Error(`Stock insuficiente para cobrar con Getnet: ${snapshot.productName}. Disponible ${snapshot.effectiveQty}.`);
            }
          }
          await assertVariantStockBeforeCharge(preOperations, sitioId, 'Getnet');
        }

        toast.loading(getnetMode === 'TERMINAL' ? 'Enviando cobro a la terminal…' : 'Generando QR de Getnet…', { id: 'getnet' });

        const efRes = await supabase.functions.invoke('getnet-create-payment', {
          body: {
            organizationId: orgId,
            mode: getnetMode,           // 'QR' | 'TERMINAL'
            channel: 'pos',
            posId: getnetPosId,
            items: itemsToInsert.map(i => ({ id: i.product_id, title: i.product_name_snapshot, unit_price: i.unit_price, quantity: i.quantity })),
            totalAmount: totalFinal,
            externalReference: sale.id,
            title: `Ticket #${String(sale.id).slice(0, 8).toUpperCase()}`,
          }
        });

        if (efRes.error || (efRes.data && efRes.data.error)) {
          toast.dismiss('getnet');
          throw new Error(`Getnet Error: ${efRes.error?.message || efRes.data?.error}`);
        }

        toast.dismiss('getnet');
        setQrImageUrl(null);
        setModoEspera(getnetMode === 'TERMINAL' ? 'TERMINAL' : 'QR');
        setEsperandoPagoQR(true);

        // En modo QR mostramos el QR (imagen del intent o, si vino tramma/url, lo generamos local).
        if (getnetMode === 'QR') {
          const qrImg = efRes.data?.qr_image;
          if (qrImg) {
            // Puede venir como dataURL/imagen lista o como tramma para codificar.
            if (typeof qrImg === 'string' && qrImg.startsWith('data:')) {
              setQrImageUrl(qrImg);
            } else {
              try { setQrImageUrl(await QRCode.toDataURL(String(qrImg), { margin: 1, scale: 4 })); }
              catch (e) { console.log('No se pudo renderizar el QR Getnet:', e); }
            }
          }
          toast.success('Pedí al cliente que escanee el QR.');
        } else {
          toast.success('Cobro enviado a la terminal. Esperando confirmación…');
        }

        // Espera de confirmación: mismo canal realtime que MP (el webhook marca "Pagado").
        let manualCancel = false;
        const getnetSuccess = await new Promise((resolve) => {
          const timeoutId = setTimeout(() => { resolve(false); }, 180000);
          setCancelarQRPromise(() => () => { manualCancel = true; resolve(false); });

          const channel = supabase.channel(`sale_${sale.id}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sales', filter: `id=eq.${sale.id}` }, (payload) => {
              if (payload.new.status === 'Pagado' || payload.new.status === 'completed') {
                clearTimeout(timeoutId);
                channel.unsubscribe();
                resolve(true);
              }
            })
            .subscribe();
        });

        setEsperandoPagoQR(false);

        let pagoConfirmado = getnetSuccess;
        if (!pagoConfirmado) {
          // RACE del webhook: el pago pudo acreditarse justo sobre el timeout.
          const { data: saleCheck } = await supabase
            .from('sales')
            .select('status')
            .eq('id', sale.id)
            .maybeSingle();
          const lateStatus = String(saleCheck?.status || '').toLowerCase();
          if (lateStatus === 'pagado' || lateStatus === 'completed') {
            pagoConfirmado = true;
            toast.success('El pago se acreditó sobre el límite de tiempo: la venta continúa.');
          }
        }

        if (!pagoConfirmado) {
          await cleanupFailedSale(sale.id);
          createdSaleId = null;
          if (manualCancel) {
            toast.error('Cobro Getnet cancelado. El carrito se conservó para reintentar.');
          } else {
            toast.error('Expiró el tiempo de espera. El carrito se conservó para reintentar.');
          }
          setProcesando(false);
          return;
        }

        mpPagoAprobado = true;
        toast.success('¡Cobro Getnet aprobado exitosamente!');
      }

      // --- CASO PRESUPUESTO ---
      if (isPresupuesto) {
        setAnimacionExito(true);
        const configPresupuesto = printConfig?.presupuesto || DEFAULT_PRINT_CONFIG.presupuesto;

        const html = buildPresupuestoHtml({
          saleId: sale.id,
          cliente,
          sucursal,
          metodo,
          items: itemsToInsert,
          total: totalFinal,
          fechaStr: fecha,
          venceISO,
          subtotalRaw,
          discountAmount: realDiscountAmt,
          surchargeAmount: realSurchargeAmt,
          config: configPresupuesto
        });

        const action = configPresupuesto.printAction || 'ask';

        if (action === 'always') {
          toast.success('Presupuesto Generado');
          openPresupuestoPrint(html, true);
          setTimeout(resetFormulario, 900);
        } else if (action === 'none') {
          toast.success('Presupuesto Generado');
          setTimeout(resetFormulario, 900);
        } else {
          // NUEVO CÓDIGO: Modal nativo para preguntar (reemplaza toast infinito)
          toast.success('Presupuesto Generado');
          openPresupuestoPrint(html, false, { afterCloseReset: true });
        }

        return;
      }

      // --- CASO VENTA REAL ---
      const saleShortId = String(sale.id).slice(0, 8).toUpperCase();
      const stockOperations = buildStockOperationsForSale(
        carrito,
        comprobante?.name || 'Comprobante',
        saleShortId,
      );
      stockRollbackContext = await applyStockOperationsForSale({
        operations: stockOperations,
        organizationId: orgId,
        siteId: sitioId,
        saleId: sale.id,
      });


      if (shouldDeferCollection && clienteId) {
        await syncSaleClientDebit({
          saleId: sale.id,
          organizationId: orgId,
          clientId: clienteId,
          totalAmount: totalFinal,
          movementType: 'Venta POS',
          observations: `Venta POS pendiente de cobro. Cliente: ${cliente?.full_name || 'Cliente'}.`,
          shouldApply: true,
        });

        await syncSaleReceivable({
          saleId: sale.id,
          organizationId: orgId,
          clientId: clienteId,
          siteId: sitioId,
          source: 'pos',
          description: `Venta POS #${String(sale.id).slice(0, 8)} - ${cliente?.full_name || 'Cliente'} (${salePaymentTerms.label})`,
          dueDate: salePaymentTerms.dueDate,
          totalAmount: totalFinal,
          active: true,
        });
      } else {
      const commissionPercentage = Math.max(0, Number(metodo?.commission_percentage) || 0);
      const accreditationDays = Math.max(0, Number(metodo?.accreditation_days) || 0);
      const businessDaysOnly = Boolean(metodo?.accreditation_business_days);
      const commissionAmount = totalFinal * (commissionPercentage / 100);
      const bankNetAmount = Math.max(0, totalFinal - commissionAmount);
      const settlementDate = addSettlementDays(createdAt, accreditationDays, businessDaysOnly);
      const settlesImmediately = Boolean(metodo?.bank_id) && accreditationDays === 0;

      let saldoResultante = null;
      if (settlesImmediately) {
        const { data: banco, error: bancoError } = await supabase
          .from('banks')
          .select('id, current_balance')
          .eq('id', metodo.bank_id)
          .single();

        if (bancoError) throw bancoError;

        saldoResultante = (Number(banco?.current_balance) || 0) + bankNetAmount;

        const { error: bankUpdateError } = await supabase
          .from('banks')
          .update({ current_balance: saldoResultante })
          .eq('id', metodo.bank_id);

        if (bankUpdateError) throw bankUpdateError;
      }

      const descripcion = `Venta POS: ${sucursal?.nombre || 'Sucursal'}. Cliente: ${cliente?.full_name || 'Consumidor Final'
        }. Comprobante: ${comprobante?.name || 'N/D'}.${metodo?.bank_id ? ` Bruto: $${currency(totalFinal)}. Comision ${commissionPercentage}%: $${currency(commissionAmount)}. Neto acreditado: $${currency(bankNetAmount)}. Acreditacion: ${accreditationDays} dia(s)${businessDaysOnly ? ' habiles' : ''}.` : ''}`;

      const { error: mfErr } = await supabase.from('movimientos_financieros').insert([
        {
          organization_id: orgId,
          bank_id: metodo?.bank_id || null,
          sitio_id: sitioId,
          tipo: 'Ingreso',
          categoria: 'Venta',
          monto: metodo?.bank_id ? bankNetAmount : totalFinal,
          saldo_resultante: saldoResultante,
          entidad_tipo: 'Cliente',
          entidad_id: clienteId || null,
          documento_tipo: 'Venta',
          documento_id: sale.id,
          descripcion,
          fecha_operacion: metodo?.bank_id ? settlementDate : createdAt,
          user_id: authUserId
        }
      ]);

      if (mfErr) {
        console.warn('Libro diario: no se pudo registrar el movimiento financiero', mfErr);
        toast.error('Aviso: venta guardada, pero no se registró en Libro Diario.');
      }

      if (turnoCajaId) {
        const { error: cajaErr } = await supabase.from('movimientos_caja').insert({
          turno_id: turnoCajaId,
          tipo: 'Ingreso',
          categoria: 'Venta',
          monto: totalFinal,
          descripcion: `Venta POS #${String(sale.id).slice(0, 6)} (${metodo?.name || 'Pago'})`,
          organization_id: orgId,
          payment_method_id: metodoPagoId,
          fecha: createdAt
        });

        if (cajaErr) {
          console.error("Error impactando en caja:", cajaErr);
          toast.error("Venta OK, pero error al impactar en turno de caja.");
        }
      }
      }


      // Seleccionar la configuración correcta según docMode.
      // Si docMode es factura, buscar printConfig.factura. Si es presupuesto, printConfig.presupuesto.
      // Por fallback, siempre usar la configuración global DEFAULT_PRINT_CONFIG si hay fallo.
      if (posUiConfig.sendPosSalesToLogistics) {
        try {
          const existingLogisticsOrderRes = await supabase
            .from('store_orders')
            .select('id')
            .eq('sale_id', sale.id)
            .maybeSingle();

          if (existingLogisticsOrderRes.error) throw existingLogisticsOrderRes.error;

          if (!existingLogisticsOrderRes.data?.id) {
            const requestedDate =
              /^\d{4}-\d{2}-\d{2}$/.test(String(fecha || '').trim())
                ? String(fecha).trim()
                : today;
            const trackingCode = `POS-${String(sale.id).slice(0, 4).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;
            const customerName = cliente?.full_name || 'Consumidor Final';
            const customerPhone = String(cliente?.phone || '').trim() || null;

            const { error: logisticsOrderError } = await supabase
              .from('store_orders')
              .insert({
                organization_id: orgId,
                site_id: sitioId, // ✅ FIX: trazabilidad multi-sucursal en logística
                sale_id: sale.id,
                tracking_code: trackingCode,
                delivery_type: 'retiro',
                shipping_cost: 0,
                shipping_address: {
                  pickup_person: customerName,
                  requested_date: requestedDate,
                  order_origin: 'pos',
                },
                customer_snapshot: {
                  name: customerName,
                  contact: customerPhone || customerName,
                  phone: customerPhone,
                  requested_date: requestedDate,
                  order_origin: 'pos',
                },
                customer_phone: customerPhone,
                status: 'recibido',
                notes: 'Generado automaticamente desde Punto de Venta.',
                created_at: createdAt,
              });

            if (logisticsOrderError) throw logisticsOrderError;
          }
        } catch (logisticsOrderError) {
          console.error('No se pudo enviar la venta POS a logistica:', logisticsOrderError);
          toast.error('Venta guardada, pero no se pudo crear la tarea de logistica.');
        }
      }

      if (!isPresupuesto && isAutoInvoice) {
        facturarARCADesdePOS(
          sale.id,
          billingEntityIdToSave,
          totalFinal,
          cliente || { tax_condition: 'Consumidor Final', doc_number: 0, doc_type: 'DNI' },
          itemsToInsert,
        );
      }

      const baseKey = docMode === 'presupuesto' ? 'presupuesto' : 'factura';
      const ticketConfig = printConfig?.[baseKey] || printConfig?.ticket_venta || printConfig?.factura || DEFAULT_PRINT_CONFIG.factura;
      const printAction = ticketConfig.printAction || 'ask';
      
      let correlativeSaleCount = 1;
      try {
        const { count, error: countErr } = await supabase
          .from('sales')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', orgId)
          .eq('site_id', sitioId);
        
        if (!countErr && count !== null) {
          correlativeSaleCount = count;
        }
      } catch (countCatch) {
        console.warn('Error fetching correlative count', countCatch);
      }

      const ticketHtml = buildTicketHtml({
        saleId: sale.id,
        cliente,
        items: itemsToInsert,
        total: totalFinal,
        fechaStr: fecha,
        subtotalRaw,
        discountAmount: realDiscountAmt,
        surchargeAmount: realSurchargeAmt,
        paymentMethodName: metodo?.name,
        config: ticketConfig,
        billingEntityId: billingEntityIdToSave
      });

      setAnimacionExito(true);

      // Nota: el recorrido guiado de primera venta es 100% simulado y se intercepta
      // al inicio de finalizarVenta (no llega aca, porque alli retornamos). Esta
      // rama corresponde solo a ventas reales.

      const posDefaultAction = printConfig?.posDefaultAction || 'popout'; // 'popout', 'none', 'ticket', 'factura'

      if (posDefaultAction === 'none') {
        toast.success('VENTA REGISTRADA OK');
        setTimeout(resetFormulario, 900);
      } else if (posDefaultAction === 'ticket') {
        toast.success('Venta Registrada (Imprimiendo Ticket...)');
        openPresupuestoPrint(ticketHtml, true);
        setTimeout(resetFormulario, 900);
      } else if (posDefaultAction === 'factura') {
        if (isAutoInvoice && docMode === 'factura') {
          toast.success('Generando Factura AFIP (Segundo plano)...');
          setSilentPolling({
            active: true,
            saleId: sale.id,
            docMode,
            saleData: {
              saleId: sale.id,
              saleNumberBySite: correlativeSaleCount,
              cliente,
              items: itemsToInsert,
              total: totalFinal,
              fechaStr: fecha,
              subtotalRaw,
              discountAmount: realDiscountAmt,
              surchargeAmount: realSurchargeAmt,
              paymentMethodName: metodo?.name,
              config: ticketConfig,
              billingEntityId: billingEntityIdToSave
            }
          });
          setTimeout(resetFormulario, 900);
        } else {
          if (printAction !== 'none') {
            toast.success('Venta Registrada (Imprimiendo Ticket...)');
            openPresupuestoPrint(ticketHtml, true);
          } else {
            toast.success('VENTA REGISTRADA OK');
          }
          setTimeout(resetFormulario, 900);
        }
      } else {
        // 'popout' o fallback
        if (printAction === 'always') {
          toast.success('Venta Registrada (Imprimiendo...)');
          openPresupuestoPrint(ticketHtml, true);
          setTimeout(resetFormulario, 900);
        } else if (printAction === 'ask') {
          setSmartModal({
            open: true,
            htmlInterno: ticketHtml,
            saleId: sale.id,
            saleNumberBySite: correlativeSaleCount,
            docMode,
            // Emisor con el que se guardó la venta: el botón "Emitir factura ARCA"
            // debe usar este valor y no el estado defaultBillingEntityId (cache de montaje).
            billingEntityId: billingEntityIdToSave,
            invoiceStatus: isAutoInvoice ? 'pendiente' : (billingEntityIdToSave ? 'manual_ready' : 'manual'),
            cae: null,
            numeroFactura: null,
            caeVto: null,
            saleData: {
              saleId: sale.id,
              saleNumberBySite: correlativeSaleCount,
              cliente: cliente || { tax_condition: 'Consumidor Final', doc_number: 0, doc_type: 'DNI' },
              items: itemsToInsert,
              total: totalFinal,
              fechaStr: fecha,
              subtotalRaw,
              discountAmount: realDiscountAmt,
              surchargeAmount: realSurchargeAmt,
              paymentMethodName: metodo?.name,
              config: ticketConfig,
              billingEntityId: billingEntityIdToSave
            }
          });
        } else {
          toast.success('VENTA REGISTRADA OK');
          setTimeout(resetFormulario, 900);
        }
      }

    } catch (err2) {
      console.error(err2);

      if (mpPagoAprobado && createdSaleId) {
        // El cobro MP ya fue aprobado: conservamos la venta sí o sí (borrarla
        // dejaría plata cobrada sin registro). No revertimos stock: si llegó a
        // descontarse, la mercadería se entrega igual. Lo que haya fallado
        // después (kardex, caja, libro diario) se corrige a mano.
        await supabase
          .from('sales')
          .update({ status: 'completed' })
          .eq('id', createdSaleId);

        toast.error(
          `COBRO REALIZADO: la venta se guardó, pero falló un paso posterior (${err2?.message || 'error técnico'}). Revisá stock y caja de esta venta.`,
          { duration: 12000 },
        );
      } else {
        if (stockRollbackContext) {
          await rollbackStockOperations(stockRollbackContext);
          stockRollbackContext = null;
        }

        if (createdSaleId) {
          await cleanupFailedSale(createdSaleId);
        }
        if (isSessionExpiredError(err2)) {
          // La venta NO se grabó: la sesión venció y el reintento automático
          // (src/supabase.js) no pudo renovarla. El carrito vive en memoria y
          // se pierde al re-loguear, por eso se pide anotarlo.
          toast.error(
            'La venta NO se guardó: tu sesión venció. Anotá los productos, volvé a iniciar sesión y cargala de nuevo.',
            { duration: 15000 },
          );
        } else {
          toast.error(err2?.message || 'Error técnico al procesar la transacción.');
        }
      }
    } finally {
      setProcesando(false);
    }
  };

  const posZoomStyle = useMemo(
    () => ({ zoom: posUiConfig.zoomPercent / 100 }),
    [posUiConfig.zoomPercent]
  );

  const renderCategoryDropdown = ({ extraClassName = '', embedded = false } = {}) => (
    <div className={embedded ? `flex flex-col gap-1 ${extraClassName}` : extraClassName}>
      {embedded ? (
        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
          Categoria
        </p>
      ) : (
        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">
          Categoria
        </label>
      )}

      <div className={embedded ? 'relative' : ''}>
        <select
          value={catSeleccionada}
          onChange={(e) => setCatSeleccionada(e.target.value)}
          className={embedded
            ? 'w-full bg-transparent border-0 p-0 pr-7 text-[10px] font-bold outline-none uppercase text-slate-700 appearance-none'
            : 'w-full bg-slate-50 border border-slate-100 p-3 rounded-2xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner'}
          title="Filtrar catalogo por categoria"
        >
          <option value="Todos">Todas</option>
          {categoriasOrdenadas.map((category) => (
            <option key={category.id} value={category.name}>
              {category.name}
            </option>
          ))}
        </select>

        {embedded ? (
          <ChevronDown
            size={16}
            className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-slate-400"
          />
        ) : null}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Iniciando Terminal...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100dvh-64px)] md:h-[calc(100dvh-80px)] bg-[#F8FAFC] font-sans text-slate-700 p-2 md:p-4 overflow-auto custom-scrollbar notranslate flex flex-col" translate="no">
      <div className="flex flex-col lg:flex-row gap-2 md:gap-3 flex-1 min-h-0 h-full origin-top-left" style={posZoomStyle}>
        <div className="flex flex-col gap-2 min-w-0 flex-1 min-h-0 h-full">

          {/* CÓDIGO ANTERIOR: Encabezado simple
          <div className="flex items-center gap-3 mb-1 px-1"> ... </div> */}

          {/* NUEVO HEADER: Modelo Productos (Cabecera + KPI) */}
          {posUiConfig.showHeader ? <div className="flex items-center justify-between mb-2 px-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0B2A36] rounded-xl flex items-center justify-center shadow-md shrink-0">
                <Monitor size={20} className="text-[#3FF2AA]" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase leading-none">Punto de Venta</h1>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">Terminal de Facturación</p>
              </div>
            </div>

            {/* ELIMINADO A PEDIDO DEL USUARIO EL CARTEL 'TOTAL EN CARRITO' DE LA CABECERA. */}
          </div> : null}

          <div
            data-onboarding-id="ventas-pos-sale-context"
            className="bg-white rounded-2xl p-3 shadow-sm border border-slate-200 relative overflow-hidden"
          >
            {!turnoCajaId && (
              <div className="absolute top-0 left-0 w-full h-1 bg-rose-500 z-10" />
            )}

            {posUiConfig.startCollapsedSaleContext ? (
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Contexto de venta</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2.5 py-1 rounded-xl bg-slate-50 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                      {docMode === 'presupuesto' ? 'Presupuesto' : 'Factura'}
                    </span>
                    <span className="px-2.5 py-1 rounded-xl bg-slate-50 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                      {clienteSeleccionado?.full_name || 'Consumidor Final'}
                    </span>
                    <span className="px-2.5 py-1 rounded-xl bg-slate-50 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                      {sitioSeleccionado?.nombre || 'Sin sucursal'}
                    </span>
                    <span className="px-2.5 py-1 rounded-xl bg-slate-50 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                      {fechaResumen}
                    </span>
                    {!turnoCajaId && docMode === 'factura' ? (
                      <span className="px-2.5 py-1 rounded-xl bg-rose-50 border border-rose-200 text-[9px] font-black uppercase tracking-widest text-rose-500">
                        Sin turno abierto
                      </span>
                    ) : null}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsSaleContextCollapsed((current) => !current)}
                  className="shrink-0 p-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-white transition-all"
                  title={isSaleContextCollapsed ? 'Desplegar datos de venta' : 'Minimizar datos de venta'}
                >
                  <ChevronDown size={18} className={`transition-transform ${isSaleContextCollapsed ? '' : 'rotate-180'}`} />
                </button>
              </div>
            ) : null}

            {(!posUiConfig.startCollapsedSaleContext || !isSaleContextCollapsed) ? (
              <>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
              <div className="space-y-1 md:col-span-3">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                  <FileText size={12} /> Documento
                </label>

                <div className="flex items-center bg-slate-50 border border-slate-100 rounded-xl overflow-hidden shadow-inner">
                  <button
                    type="button"
                    onClick={() => setDocMode('factura')}
                    className={`flex-1 px-2.5 py-1.5 text-[9px] font-black uppercase transition whitespace-nowrap ${docMode === 'factura' ? 'bg-[#0B2A36] text-white' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    style={{ letterSpacing: docMode === 'factura' ? '0.14em' : '0.1em' }}
                    title="Registrar venta real"
                  >
                    Factura
                  </button>
                  <button
                    type="button"
                    onClick={() => setDocMode('presupuesto')}
                    className={`flex-1 px-2.5 py-1.5 text-[9px] font-black uppercase transition whitespace-nowrap ${docMode === 'presupuesto' ? 'bg-[#0B2A36] text-white' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    style={{ letterSpacing: docMode === 'presupuesto' ? '0.08em' : '0.06em' }}
                    title="No impacta stock/tesorería"
                  >
                    Presupuesto
                  </button>
                </div>

                {docMode === 'presupuesto' ? (
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      value={presupuestoVenceEnDias ?? ''}
                      onChange={(e) => setPresupuestoVenceEnDias(e.target.value)}
                      className="w-16 bg-white border border-slate-200 px-2 py-1 rounded-lg text-[10px] font-black outline-none"
                      title="Vence en (días)"
                    />
                    <span className="text-[9px] font-bold text-slate-400">vence en días</span>
                  </div>
                ) : null}
              </div>

              <div className="space-y-1 md:col-span-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                  <User size={12} /> Cliente
                </label>
                <select
                  className="w-full bg-slate-50 border border-slate-100 p-2 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                  value={clienteId}
                  onChange={(e) => {
                    const nextClientId = e.target.value;
                    const nextClient = clientes.find((client) => client.id === nextClientId) || null;
                    setClienteId(nextClientId);
                    setSalePaymentConditionTouched(false);
                    setSalePaymentConditionDays(getClientPaymentConditionDays(nextClient));
                    setTimeout(() => applySuggestedComprobante({ force: true }), 0);
                  }}
                  title="Selecciona el cliente para aplicar sus datos fiscales y condiciones."
                >
                  <option value="">Consumidor Final</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name}
                    </option>
                  ))}
                </select>
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">
                  {activePriceList ? `Lista aplicada: ${activePriceList.name}` : 'Sin lista asignada'}
                </p>
              </div>

              <div className="space-y-1 md:col-span-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                  <Calendar size={12} /> Cond. Venta
                </label>
                <input
                  type="number"
                  min={0}
                  value={salePaymentConditionDays ?? ''}
                  onChange={(e) => {
                    setSalePaymentConditionTouched(true);
                    setSalePaymentConditionDays(Math.max(0, Number(e.target.value) || 0));
                  }}
                  className="w-full bg-slate-50 border border-slate-100 p-2 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                  title="Plazo de la venta en dias. 0 = contado."
                />
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">
                  {clienteSeleccionado
                    ? `Default cliente: ${formatPaymentConditionLabel(getClientPaymentConditionDays(clienteSeleccionado))}`
                    : 'Sin cliente: contado'}
                </p>
              </div>

              <div className="space-y-1 md:col-span-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                  <Calendar size={12} /> Fecha
                </label>
                <input
                  type="date"
                  className="w-full bg-slate-50 border border-slate-100 p-2 rounded-xl text-[10px] font-bold outline-none shadow-inner"
                  value={fecha ?? ''}
                  onChange={(e) => setFecha(e.target.value)}
                  title="Fecha operativa de la venta."
                />
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">
                  {sitioSeleccionado?.nombre || 'Sucursal desde topbar'}
                </p>
              </div>
            </div>

            {docMode === 'presupuesto' ? (
              <div className="mt-2 flex items-start gap-2 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                <Info size={14} className="text-slate-300 mt-0.5" />
                <p className="text-[9px] font-bold text-slate-500 leading-relaxed">
                  Estás generando un <span className="font-black">presupuesto</span>: no impacta stock ni tesorería. Luego podés convertirlo en venta.
                </p>
              </div>
            ) : null}

            {!turnoCajaId && docMode === 'factura' && (
              <div className="mt-2 flex items-center gap-2 p-2 rounded-xl bg-rose-50 border border-rose-100">
                <AlertTriangle size={14} className="text-rose-500" />
                <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest">
                  Atención: No hay turno de caja abierto. Esta venta se guardará pero no aparecerá en el arqueo actual.
                </p>
              </div>
            )}
              </>
            ) : null}
          </div>

          <div
            data-onboarding-id="ventas-pos-search"
            className={`bg-white rounded-2xl p-3 shadow-sm border border-slate-200 ${posUiConfig.showSearchBar ? '' : 'hidden'}`}
          >
            {posUiConfig.showSearchBar ? (
              <div className="flex flex-col gap-2">
                <div
                  className={`w-full bg-slate-50 border border-slate-100 rounded-2xl shadow-inner overflow-hidden ${
                    posUiConfig.useCategoryDropdown ? 'flex flex-col lg:flex-row lg:items-stretch' : 'relative'
                  }`}
                >
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                ref={searchInputRef}
                type="text"
                inputMode="none"
                placeholder="ESCANEAR EAN O BUSCAR..."
                className={`w-full pl-11 py-3 text-[11px] font-bold outline-none uppercase ${
                  posUiConfig.useCategoryDropdown
                    ? 'pr-4 bg-transparent border-0 shadow-none'
                    : 'pr-12 bg-transparent border-0 shadow-none rounded-2xl'
                }`}
                title="Escanea un codigo de barras o busca por nombre."
                value={busqueda ?? ''}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onClick={(e) => {
                  // Pequeño workaround para focus forzado sin teclado:
                  e.currentTarget.focus();
                }}
              />
                    {!posUiConfig.useCategoryDropdown ? (
                      <button
                        type="button"
                onClick={handleCameraScan}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#51A7FD] bg-slate-100 p-2 rounded-xl border border-slate-200"
                title="Escanear con Cámara"
              >
                <ScanBarcode size={18} />
                </button>
              ) : null}
            </div>

            {posUiConfig.useCategoryDropdown ? (
              <>
                <div className="mx-3 h-px bg-slate-200 lg:mx-0 lg:my-3 lg:h-auto lg:w-px" />
                {renderCategoryDropdown({
                  embedded: true,
                  extraClassName: 'px-3 pb-3 lg:px-4 lg:py-3 lg:w-[240px] shrink-0',
                })}
                <div className="px-3 pb-3 lg:px-3 lg:py-3 lg:pl-0 flex items-center">
                  <button
                    type="button"
                    onClick={handleCameraScan}
                    className="w-full lg:w-auto text-slate-400 hover:text-[#51A7FD] bg-white p-2 rounded-xl border border-slate-200"
                    title="Escanear con CÃ¡mara"
                  >
                    <ScanBarcode size={18} />
                  </button>
                </div>
              </>
            ) : null}
                </div>

            <button
              onClick={() => setShowCatMenu(true)}
              className={`${posUiConfig.useCategoryDropdown ? 'hidden' : 'lg:hidden self-end'} p-3 bg-[#0B2A36] text-white rounded-2xl shadow-md border border-[#0B2A36] flex items-center gap-2`}
              title="Abrir filtro de categorias"
            >
              <Filter size={18} />
              <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Categorías</span>
            </button>
          </div>
            ) : null}
          </div>

          <div
            data-onboarding-id="ventas-pos-catalog"
            className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col flex-1 min-h-0"
          >
            <div className="bg-[#0B2A36] px-5 py-2.5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Package size={14} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Catálogo</span>
              </div>
              <div className="flex items-center gap-2">
                {!posUiConfig.useCategoryDropdown && !posUiConfig.showSearchBar ? (
                  <button
                    type="button"
                    onClick={() => setShowCatMenu(true)}
                    className="lg:hidden p-2 bg-white/10 text-white rounded-xl border border-white/10 flex items-center gap-2"
                    title="Abrir filtro de categorias"
                  >
                    <Filter size={16} />
                  </button>
                ) : null}

                <div className="text-[9px] font-black uppercase tracking-widest text-white/30 hidden lg:block">
                  {filtrados.length} resultado(s)
                </div>
              </div>
            </div>

            {posUiConfig.useCategoryDropdown && !posUiConfig.showSearchBar ? (
              <div className="px-4 py-3 border-b border-slate-100 bg-white shrink-0">
                {renderCategoryDropdown()}
              </div>
            ) : null}

            <div className={`${posUiConfig.useCategoryDropdown ? 'hidden' : 'hidden lg:flex'} px-4 py-2 border-b border-slate-100 bg-white shrink-0 flex-wrap items-center gap-2`}>
              <button
                type="button"
                onClick={() => setCatSeleccionada('Todos')}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition border whitespace-nowrap ${catSeleccionada === 'Todos'
                    ? 'bg-[#0B2A36] text-white border-[#0B2A36]'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-white'
                  }`}
                style={{ letterSpacing: '0.12em' }}
                title="Mostrar todo el catalogo"
              >
                Todos
              </button>

              {categoriasOrdenadas.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  // Toggle: si la categoría ya está activa, volver a "Todos". Evita el
                  // click sin efecto (no-op de setState al mismo valor) que se veía como
                  // rage click en el POS (ref IMPULZA-8 / IMPULZA-9).
                  onClick={() => setCatSeleccionada((prev) => (prev === c.name ? 'Todos' : c.name))}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition border whitespace-nowrap ${catSeleccionada === c.name
                      ? 'bg-[#0B2A36] text-white border-[#0B2A36]'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-white'
                    }`}
                  style={{ letterSpacing: '0.12em' }}
                  title={c.name}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <div className="p-3 md:p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-2 gap-y-3 content-start overflow-y-auto min-h-0 flex-1 pr-1 custom-scrollbar">

              {enableMiscProduct && (
                <button
                  onClick={agregarMiscItem}
                  className={`bg-amber-50 rounded-[1.5rem] p-0 border border-amber-200 hover:border-amber-400 hover:shadow-md transition-all flex flex-col h-24 ${showPosImages ? 'lg:h-52' : 'lg:h-32'} group relative text-left overflow-hidden`}
                  title="Agregar un item manual con precio libre."
                >
                  {showPosImages ? (
                    <div className="hidden lg:flex h-28 w-full bg-white items-center justify-center overflow-hidden border-b border-amber-100">
                      <Calculator size={32} className="text-amber-300" />
                    </div>
                  ) : null}

                  <div className="p-2 lg:p-3 flex flex-col flex-1 justify-between">
                    <h3 className="font-black text-amber-900 uppercase text-[10px] leading-tight line-clamp-2 mb-2">
                      PRODUCTOS VARIOS
                    </h3>

                    <div className="flex justify-between items-end w-full mt-auto">
                      <div>
                        <p className="text-[8px] font-black uppercase tracking-tighter text-amber-600/70">
                          PRECIO LIBRE
                        </p>
                        <p className="text-amber-600 font-black text-[12px] leading-none">--</p>
                      </div>
                      <div className="hidden lg:block p-1.5 bg-amber-100 text-amber-600 rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-sm">
                        <Plus size={12} strokeWidth={3} />
                      </div>
                    </div>
                  </div>
                </button>
              )}

              {/* Uso del componente memorizado con el estado showPosImages */}
              {filtradosConDemo.map((prod) => (
                <ProductCard
                  key={prod.id}
                  prod={prod}
                  stockActual={getStock(prod, sitioId)}
                  price={Number(getEffectiveVariantPrice(prod, null)) || 0}
                  allowNegativeStock={allowNegativeStock}
                  showNegativeStock={showNegativeStock}
                  showImages={showPosImages} // ✅ NUEVO PARÁMETRO
                  showBrand={Boolean(posUiConfig.showProductBrand)}
                  abbreviateProductNames={abbreviateProductNames}
                  dataOnboardingId={prod.id === firstSaleProductId ? `ventas-pos-product-${prod.id}` : undefined}
                  onAdd={agregarAlCarrito}
                />
              ))}

              {/* Empty-state: sin esto la grilla quedaba en blanco cuando el filtro
                  no devuelve productos (búsqueda sin match, categoría sin items
                  visibles en la lista de precios activa, etc.). El usuario no tenía
                  feedback y volvía a clickear (ref rage clicks IMPULZA-8 / IMPULZA-9). */}
              {filtradosConDemo.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                    {(busqueda || '').trim()
                      ? 'Sin resultados para tu búsqueda'
                      : catSeleccionada !== 'Todos'
                        ? `No hay productos para mostrar en “${catSeleccionada}”`
                        : 'No hay productos para mostrar'}
                  </p>
                  <p className="text-[10px] font-semibold text-slate-400 max-w-xs">
                    {(busqueda || '').trim()
                      ? 'Probá con otro nombre o código.'
                      : catSeleccionada !== 'Todos'
                        ? 'Puede que estén ocultos en la lista de precios activa o dados de baja.'
                        : 'Cargá productos desde Inventario para verlos acá.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="flex flex-col w-full min-h-[280px] lg:min-h-0 shrink-0 lg:w-[420px] lg:h-full border-t border-slate-200 pt-2 lg:pt-0 lg:border-t-0">
          <div data-onboarding-id="ventas-pos-cart-panel" className="bg-white rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden flex flex-col flex-1 min-h-0 md:h-full">
            <div className="px-5 py-2.5 border-b border-slate-100 flex justify-between items-center bg-[#0B2A36] shrink-0">
              <h2 className="font-black text-[10px] text-white uppercase tracking-[0.2em] flex items-center gap-3">
                <ShoppingCart className="text-[#3FF2AA]" size={16} /> Carrito
              </h2>
              <span className="bg-[#3FF2AA] text-[#0B2A36] text-[8px] px-3 py-1 rounded-lg font-black uppercase border border-emerald-500/20">
                {carrito.length} Items
              </span>
            </div>

            <div className="overflow-y-auto p-3 space-y-1 custom-scrollbar min-h-0 flex-1">
              {carrito.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center opacity-10 grayscale">
                  <ShoppingCart size={56} strokeWidth={1} />
                  <p className="text-[10px] font-black uppercase tracking-widest mt-4">Esperando selección...</p>
                </div>
              ) : (
                carrito.map((item) => {
                  const ck = item.cart_key || item.product_id;
                  const canEditCartPrice = item.is_misc || posUiConfig.allowCartPriceEdit;
                  const quantityStep = decimalCartQuantityEnabled ? DECIMAL_CART_QUANTITY_STEP : 1;
                  return (
                  <div
                    key={ck}
                    data-onboarding-id={item.product_id === firstSaleProductId ? 'ventas-pos-cart-item' : undefined}
                    className={`flex items-center gap-2 p-2 rounded-2xl border hover:bg-white transition-all shadow-sm ${item.is_misc ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'
                      }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-[10px] font-black text-slate-800 uppercase leading-tight mb-1 ${abbreviateProductNames ? 'truncate' : 'break-words whitespace-normal'}`}>{sanitizeProductDisplayName(item.nombre || 'Producto')}</p>

                      {canEditCartPrice ? (
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-[10px] font-bold text-slate-400">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.unit_price ?? ''}
                            onChange={(e) => modificarPrecio(ck, e.target.value)}
                            className={`w-24 bg-white border rounded px-1 py-0.5 text-[10px] font-black outline-none ${item.is_misc ? 'border-slate-200 focus:border-amber-400' : 'border-slate-200 focus:border-[#51A7FD]'}`}
                            autoFocus={item.is_misc}
                            title={item.is_misc ? 'Editar precio del item manual.' : 'Editar precio unitario del producto en carrito.'}
                          />
                          <span className="text-[9px] text-slate-400 font-bold tracking-widest">
                            Sub: $ {currency(item.subtotal)}
                          </span>
                        </div>
                      ) : (
                        <p className="text-[9px] text-slate-400 font-bold tracking-widest">
                          ${Number(item.unit_price).toLocaleString('es-AR')} • Sub: $ {currency(item.subtotal)}
                        </p>
                      )}

                      {(itemDiscountConfig.enabled || tieneDescuentoItem(item)) && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {tieneDescuentoItem(item) && (
                            <span className="text-[9px] font-black text-emerald-600 tracking-tight">
                              <span className="line-through text-slate-400 font-bold">${currency(precioListaItem(item))}</span>
                              {' '}-{Number(Number(item.item_discount_pct).toFixed(2))}%
                            </span>
                          )}
                          {itemDiscountConfig.enabled && (!item.block_discounts || puedeAutorizarDescuento) && (
                            <button
                              type="button"
                              onClick={() => abrirDescuentoItem(item)}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-emerald-200 bg-white text-[8px] font-black uppercase tracking-widest text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title="Aplicar un descuento solo a este producto"
                            >
                              <Percent size={9} /> {tieneDescuentoItem(item) ? 'Editar desc.' : 'Desc.'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 bg-white p-1 rounded-xl border border-slate-200">
                      <button
                        onClick={() => modificarCantidad(ck, -quantityStep)}
                        className="p-1 text-slate-300 hover:text-[#51A7FD] transition-colors"
                        title="Disminuir"
                      >
                        <Minus size={10} />
                      </button>
                      <input
                        type="text"
                        inputMode={decimalCartQuantityEnabled ? 'decimal' : 'numeric'}
                        value={Object.prototype.hasOwnProperty.call(cartQuantityDrafts, ck) ? cartQuantityDrafts[ck] : formatCartQuantityValue(item.cantidad ?? 1)}
                        onChange={(e) => handleCantidadInputChange(ck, e.target.value)}
                        onBlur={() => commitCantidadInput(ck, item.cantidad)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitCantidadInput(ck, item.cantidad);
                            e.currentTarget.blur();
                          }
                        }}
                        className={`bg-transparent text-[11px] font-black text-center text-slate-800 outline-none ${decimalCartQuantityEnabled ? 'w-16' : 'w-10'}`}
                        title={decimalCartQuantityEnabled ? 'Escribi la cantidad con coma o punto hasta 3 decimales.' : 'Escribí la cantidad directamente.'}
                      />
                      <button
                        onClick={() => modificarCantidad(ck, quantityStep)}
                        className="p-1 text-slate-300 hover:text-[#51A7FD] transition-colors"
                        title="Aumentar"
                      >
                        <Plus size={10} />
                      </button>
                    </div>

                    <button
                      onClick={() => quitarItem(ck)}
                      className="text-slate-300 hover:text-rose-500 transition-colors p-1.5 ml-1"
                      title="Quitar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  );
                })
              )}
            </div>

            <div
              data-onboarding-id="ventas-pos-checkout"
              className="p-3 bg-slate-50 border-t border-slate-200 space-y-2 shrink-0"
            >
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[8px] font-black uppercase text-slate-400 tracking-widest ml-1 mb-1 block">
                      % Desc.
                    </label>
                    <div className="relative">
                      <Percent size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-300" />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={descPct ?? ''}
                        onChange={(e) => setDescPct(e.target.value)}
                        className="w-full pl-6 pr-2 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none"
                        title="Descuento porcentual aplicado al total."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[8px] font-black uppercase text-slate-400 tracking-widest ml-1 mb-1 block">
                      % Recargo
                    </label>
                    <div className="relative">
                      <Percent size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-300" />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={recPct ?? ''}
                        onChange={(e) => setRecPct(e.target.value)}
                        className="w-full pl-6 pr-2 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none"
                        title="Recargo porcentual aplicado al total."
                      />
                    </div>
                  </div>
                </div>

                <label className="text-[8px] font-black uppercase text-slate-400 tracking-widest ml-1 flex items-center gap-1">
                  <BadgeDollarSign size={12} /> Medio de pago
                </label>
                <select
                  data-onboarding-id="ventas-pos-method-select"
                  className={`w-full p-3 border rounded-2xl font-bold text-[10px] uppercase outline-none transition-all shadow-md ${!metodoPagoId
                      ? 'bg-blue-50 border-[#51A7FD] text-[#51A7FD]'
                      : 'bg-white border-slate-200 text-slate-700'
                    }`}
                  value={metodoPagoId}
                  onChange={(e) => handleMetodoPagoChange(e.target.value)}
                  title="Selecciona el medio de pago para calcular recargos, comisiones y acreditacion."
                >
                  <option value="">SELECCIONAR...</option>
                  {metodosDisponibles.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>

                <div className="flex justify-between items-center py-2 border-y border-slate-200 mt-2">
                  <span className="font-black text-slate-400 uppercase text-[9px] tracking-widest">Total</span>
                  <span className="text-2xl font-black text-slate-900 tracking-tighter leading-none">$ {currency(totalFinal)}</span>
                </div>

                {/* Prueba de venta registrada para el recorrido guiado: el clic
                    en "Cobrar" no alcanza (puede fallar por stock o caja). */}
                {animacionExito && (
                  <span data-onboarding-id="ventas-pos-sale-success" className="sr-only">
                    Venta registrada
                  </span>
                )}
                <button
                  data-onboarding-id="ventas-pos-submit"
                  disabled={carrito.length === 0 || procesando || !metodoPagoId}
                  onClick={finalizarVenta}
                  className={`w-full h-14 rounded-[2rem] font-black uppercase tracking-[0.3em] text-[10px] shadow-lg transition-all flex items-center justify-center gap-3
                    ${animacionExito
                      ? 'bg-[#3ff2aa] text-[#0B2A36] scale-[1.01]'
                      : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.01] active:scale-95 shadow-emerald-200/20 disabled:opacity-40'
                    }`}
                  title={
                    carrito.length === 0
                      ? 'Agrega productos al carrito para continuar.'
                      : !metodoPagoId
                      ? 'Selecciona un medio de pago antes de finalizar.'
                      : docMode === 'presupuesto'
                      ? 'Generar presupuesto sin impactar stock ni tesoreria.'
                      : 'Confirmar y cobrar la venta.'
                  }
                >
                  {animacionExito ? (
                    <Check size={26} strokeWidth={4} className="animate-in zoom-in duration-300" />
                  ) : procesando ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      <Check size={18} strokeWidth={3} /> {docMode === 'presupuesto' ? 'Generar presupuesto' : 'Cobrar'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {showCatMenu && !posUiConfig.useCategoryDropdown && (
        <div className="fixed inset-0 z-[9999] bg-slate-100 flex flex-col animate-in fade-in zoom-in-95 duration-200">
          <div className="bg-white px-6 py-4 flex items-center justify-between border-b border-slate-200 shadow-sm shrink-0">
            <div>
              <h2 className="text-lg font-black uppercase tracking-tight text-slate-800">Seleccionar Categoría</h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Filtrar catálogo</p>
            </div>
            <button
              onClick={() => setShowCatMenu(false)}
              className="p-3 bg-slate-50 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pb-20">
              <button
                onClick={() => {
                  setCatSeleccionada('Todos');
                  setShowCatMenu(false);
                }}
                className={`p-3 rounded-xl text-center border-2 transition-all flex flex-col items-center justify-center ${catSeleccionada === 'Todos'
                    ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-lg shadow-blue-900/20'
                    : 'bg-white text-slate-600 border-slate-100 shadow-sm'
                  }`}
              >
                <span className="text-[10px] font-black uppercase tracking-widest">Todas</span>
              </button>

              {categoriasOrdenadas.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setCatSeleccionada(c.name);
                    setShowCatMenu(false);
                  }}
                  className={`p-3 rounded-xl text-center border-2 transition-all flex flex-col items-center justify-center ${catSeleccionada === c.name
                      ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-lg shadow-blue-900/20'
                      : 'bg-white text-slate-600 border-slate-100 shadow-sm'
                    }`}
                >
                  <span className="text-[10px] font-black uppercase tracking-widest line-clamp-2 leading-tight">
                    {c.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {smartModal.open && (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] max-w-sm w-full shadow-2xl flex flex-col items-center text-center animate-in zoom-in-95 duration-300 relative">
            <button
              onClick={() => {
                setSmartModal(prev => ({ ...prev, open: false }));
                resetFormulario();
              }}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 rounded-full transition-all"
            >
              <X size={20} />
            </button>
            <div className="p-6 flex flex-col items-center w-full">
              <div className="w-16 h-16 bg-[#3FF2AA]/20 rounded-full flex items-center justify-center mb-4 mt-2">
                <Printer size={32} className="text-[#0B2A36]" />
              </div>
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">
                Venta Registrada
              </h3>
              <p className="text-sm text-slate-500 font-bold mb-6">
                Venta {smartModal.saleNumberBySite}<br/>Seleccione qué documento desea procesar:
              </p>

            <div className="flex flex-col gap-3 w-full">
              <button
                onClick={() => {
                  setSmartModal(prev => ({ ...prev, open: false }));
                  openPresupuestoPrint(smartModal.htmlInterno, true);
                  resetFormulario();
                }}
                className="w-full bg-[#3FF2AA] text-[#0B2A36] py-3 rounded-xl font-black uppercase tracking-widest text-xs transition-all hover:bg-[#2fe09b] shadow-sm"
              >
                Imprimir comprobante
              </button>

              {smartModal.docMode === 'factura' && (
                <button
                  onClick={async () => {
                    if (smartModal.invoiceStatus === 'facturado') {
                      const base64QR = await generarQrBase64Afip(smartModal.saleData, {
                        cae: smartModal.cae,
                        numero_factura: smartModal.numeroFactura,
                        fecha_facturacion: smartModal.fechaFacturacion,
                        invoice_sent_at: smartModal.invoiceSentAt,
                        invoice_cbte_tipo: smartModal.invoiceCbteTipo,
                        billing_entity_id: smartModal.billingEntityId
                      });
                      const finalHtml = buildTicketHtml({
                        ...smartModal.saleData,
                        cae: smartModal.cae,
                        caeVto: smartModal.caeVto,
                        numeroFactura: smartModal.numeroFactura,
                        fechaFacturacion: smartModal.fechaFacturacion,
                        invoiceSentAt: smartModal.invoiceSentAt,
                        invoiceCbteTipo: smartModal.invoiceCbteTipo,
                        billingEntityId: smartModal.billingEntityId,
                        qrBase64: base64QR
                      });
                      setSmartModal(prev => ({ ...prev, open: false }));
                      openPresupuestoPrint(finalHtml, true);
                      resetFormulario();
                    } else if (smartModal.invoiceStatus === 'error') {
                      toast.error('Facturación fallida. Vea el módulo Facturación para más detalles.');
                    } else if (smartModal.invoiceStatus === 'manual_ready') {
                      // Trigger manual billing via AFIP!
                      setSmartModal(prev => ({ ...prev, invoiceStatus: 'pendiente' }));
                      facturarARCADesdePOS(
                        smartModal.saleId,
                        smartModal.billingEntityId || defaultBillingEntityId,
                        smartModal.saleData.total,
                        smartModal.saleData.cliente,
                        smartModal.saleData.items
                      );
                    }
                  }}
                  className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-xs transition-all flex justify-center items-center gap-2
                    ${['pendiente', 'enviado'].includes(smartModal.invoiceStatus) ? 'bg-amber-100 text-amber-700 opacity-80 cursor-wait' :
                      smartModal.invoiceStatus === 'facturado' ? 'bg-[#0B2A36] text-[#3FF2AA] shadow-lg hover:shadow-xl hover:scale-105' :
                        smartModal.invoiceStatus === 'manual_ready' ? 'bg-[#3FF2AA] text-[#0B2A36] shadow-sm hover:bg-[#2fe09b] hover:scale-105' :
                        smartModal.invoiceStatus === 'manual' ? 'bg-slate-100 text-slate-400 opacity-80 cursor-not-allowed' :
                          'bg-rose-100 text-rose-700'
                    }`}
                >
                  {['pendiente', 'enviado'].includes(smartModal.invoiceStatus) && <><Loader2 size={16} className="animate-spin" /> Generando Fiscal en AFIP...</>}
                  {smartModal.invoiceStatus === 'facturado' && <><Check size={16} /> Imprimir Factura AFIP</>}
                  {smartModal.invoiceStatus === 'manual_ready' && <><CheckCircle2 size={16} /> Emitir factura ARCA</>}
                  {smartModal.invoiceStatus === 'manual' && 'Modo Manual (Sin emisor asignado)'}
                  {smartModal.invoiceStatus === 'error' && 'Error en facturación AFIP'}
                </button>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL ESPERANDO PAGO QR ===== */}
      {esperandoPagoQR && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="bg-[#009EE3] px-6 py-4 flex items-center justify-between shrink-0">
              <div>
                <p className="text-[10px] font-black text-white uppercase tracking-widest leading-tight">
                  {modoEspera === 'TERMINAL' ? 'COBRO CON TERMINAL' : 'MERCADO PAGO'}
                </p>
                <h3 className="text-xl font-black text-white uppercase tracking-tight leading-tight">
                  {modoEspera === 'TERMINAL' ? 'Pasá la tarjeta' : 'Escanea el QR'}
                </h3>
              </div>
            </div>
            <div className="p-8 flex flex-col items-center justify-center min-h-[300px]">
              {modoEspera === 'TERMINAL' ? (
                <div className="flex flex-col items-center">
                  <CreditCard size={64} className="text-[#009EE3] mb-4" />
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest text-center">
                    El cobro está en la terminal.<br />Pedile al cliente que pase la tarjeta.
                  </p>
                </div>
              ) : qrImageUrl ? (
                <img src={qrImageUrl} alt="QR Mercado Pago" className="w-56 h-56 object-contain bg-white rounded-2xl shadow-sm border border-slate-100 p-2" />
              ) : (
                <div className="flex flex-col items-center">
                  <Loader2 size={40} className="animate-spin text-[#009EE3] mb-4" />
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest text-center">Cargando código QR...</p>
                </div>
              )}
              <div className="mt-6 flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 text-[#009EE3]">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-xs font-black uppercase tracking-widest">Esperando pago...</span>
                </div>
                <p className="text-[10px] text-slate-400 font-bold text-center w-64">El cliente puede escanear este QR desde la pantalla o usar el QR físico de la caja.</p>
              </div>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => {
                  if (cancelarQRPromise) cancelarQRPromise();
                }}
                className="w-full bg-white border-2 border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700 py-3 rounded-xl font-black uppercase tracking-widest text-xs transition-all"
              >
                Cancelar Cobro
              </button>
            </div>
          </div>
        </div>
      )}

      {printModal.open ? (
        <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3">
          <div className="w-full max-w-5xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Impresión de comprobante</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={doPrintIframe}
                  className="px-4 py-2 rounded-xl font-black uppercase tracking-widest text-[10px] shadow-sm border"
                  style={{ background: '#3FF2AA', color: '#0B2A36', borderColor: 'rgba(63,242,170,0.35)' }}
                >
                  Imprimir
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const shouldResetAfterClose = Boolean(printModal.afterCloseReset);
                    setPrintModal({ open: false, html: '', autoPrint: false, afterCloseReset: false });
                    if (shouldResetAfterClose) resetFormulario();
                  }}
                  className="px-3 py-2 rounded-xl bg-white/10 text-white border border-white/10 hover:bg-white/20 transition-all"
                  title="Cerrar"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-3 bg-slate-50">
              <iframe
                ref={iframeRef}
                title="print-preview"
                className="w-full h-[70vh] rounded-xl border border-slate-200 bg-white"
                srcDoc={printModal.html || ''}
                onLoad={(e) => {
                  // NUEVO CÓDIGO: Asegurar que imprima cuando termina de renderizar el iFrame
                  if (printModal.autoPrint) {
                    const iframe = e.target;
                    if (printTimeoutRef.current) clearTimeout(printTimeoutRef.current);
                    printTimeoutRef.current = setTimeout(() => {
                      printTimeoutRef.current = null;
                      // El iframe puede haberse desmontado (modal cerrado, nueva venta o
                      // re-render) antes de que dispare el timeout: su contentWindow queda
                      // "no longer runnable" y print() lanza una DOMException no manejada.
                      const win = iframe && iframe.isConnected ? iframe.contentWindow : null;
                      if (!win) return;
                      try {
                        win.focus();
                        win.print();
                      } catch (err) {
                        console.warn('Auto-impresión cancelada: la ventana de impresión ya no está disponible', err);
                      }
                    }, 500);
                  }
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* ===== MODAL COMBO DINÁMICO (multi-paso) ===== */}
      {dynamicComboModal.open && dynamicComboModal.prod && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] w-full max-w-2xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300">

            {/* Header */}
            <div className="bg-[#0B2A36] px-6 py-4 flex items-center justify-between shrink-0">
              <div className="min-w-0">
                <p className="text-[9px] font-black text-[#3FF2AA] uppercase tracking-widest mb-0.5">Armá tu Combo</p>
                <h3 className="text-sm font-black text-white uppercase tracking-tight leading-tight truncate">
                  {dynamicComboModal.prod.name}
                </h3>
              </div>
              <button
                onClick={() => { setDynamicComboModal({ open: false, prod: null, steps: [] }); setDynamicSelections({}); }}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Pasos */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
              {dynamicComboModal.steps.map((st, idx) => {
                const sel = dynamicSelections[st.id] || [];
                const totalSel = sel.reduce((acc, x) => acc + Number(x.qty || 0), 0);
                const reqQty = Number(st.quantity || 1);
                const completo = totalSel === reqQty;
                return (
                  <div key={st.id} className="rounded-2xl border border-slate-200 overflow-hidden">
                    <div className={`px-4 py-3 flex items-center justify-between ${completo ? 'bg-emerald-50 border-b border-emerald-100' : 'bg-slate-50 border-b border-slate-100'}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black ${completo ? 'bg-emerald-500 text-white' : 'bg-[#0B2A36] text-[#3FF2AA]'}`}>
                          {completo ? <Check size={14} /> : (idx + 1)}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[11px] font-black text-slate-800 uppercase tracking-tight truncate">
                            {st.step_name}
                          </div>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                            Eleg{reqQty === 1 ? 'í' : 'í'} {reqQty} {reqQty === 1 ? 'producto' : 'productos'}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${completo ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-500'}`}>
                        {totalSel} / {reqQty}
                      </span>
                    </div>

                    {/* Opciones del paso */}
                    <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {st.options.length === 0 ? (
                        <div className="col-span-full p-4 text-center text-[9px] font-bold uppercase tracking-widest text-slate-300">
                          Sin opciones disponibles
                        </div>
                      ) : st.options.map(opt => {
                        const sItem = sel.find(s => s.product_id === opt.product_id);
                        const qty = Number(sItem?.qty || 0);
                        const cantElegidaTotal = sel.reduce((acc, x) => acc + Number(x.qty || 0), 0);
                        const cuploLleno = cantElegidaTotal >= reqQty && qty === 0;

                        return (
                          <div
                            key={opt.product_id}
                            className={`relative rounded-xl border p-3 flex flex-col gap-2 transition-all ${qty > 0 ? 'bg-blue-50 border-[#51A7FD] shadow-sm' : cuploLleno ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
                          >
                            <button
                              type="button"
                              disabled={cuploLleno}
                              onClick={() => setSelectionQty(st.id, opt, +1)}
                              className="text-left disabled:cursor-not-allowed"
                            >
                              <div className="text-[10px] font-black uppercase tracking-tight text-slate-800 leading-tight line-clamp-2 min-h-[26px]">
                                {opt.name}
                              </div>
                            </button>
                            <div className="flex items-center justify-between">
                              {qty > 0 ? (
                                <div className="flex items-center gap-1 bg-white p-0.5 rounded-lg border border-slate-200">
                                  <button
                                    type="button"
                                    onClick={() => setSelectionQty(st.id, opt, -1)}
                                    className="w-6 h-6 rounded-md hover:bg-slate-50 text-slate-500 hover:text-slate-700 flex items-center justify-center"
                                  >
                                    <Minus size={11} />
                                  </button>
                                  <span className="w-6 text-center text-[11px] font-black text-slate-800">{qty}</span>
                                  <button
                                    type="button"
                                    disabled={cantElegidaTotal >= reqQty}
                                    onClick={() => setSelectionQty(st.id, opt, +1)}
                                    className="w-6 h-6 rounded-md hover:bg-slate-50 text-slate-500 hover:text-slate-700 flex items-center justify-center disabled:opacity-30"
                                  >
                                    <Plus size={11} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  disabled={cuploLleno}
                                  onClick={() => setSelectionQty(st.id, opt, +1)}
                                  className="px-2.5 py-1 rounded-lg bg-[#0B2A36] text-[#3FF2AA] text-[9px] font-black uppercase tracking-widest disabled:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                  <Plus size={11} /> Elegir
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="bg-white border-t border-slate-100 px-5 py-4 shrink-0 flex items-center justify-between gap-3">
              <button
                onClick={() => { setDynamicComboModal({ open: false, prod: null, steps: [] }); setDynamicSelections({}); }}
                className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarComboDinamico}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#3FF2AA] text-[#0B2A36] text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] transition-transform shadow-lg flex items-center justify-center gap-2"
              >
                <Check size={14} /> Confirmar y agregar al carrito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: ¿SE COBRA AHORA? (cliente identificado) ===== */}
      {cobroClienteModal && clienteSeleccionado && (() => {
        const metodoSel = metodosPago.find((m) => m.id === metodoPagoId);
        const comprobanteSel = comprobantes.find((c) => c.id === comprobanteId);
        const condicionDias = Math.max(0, Number(salePaymentConditionDays) || 0);
        const sugiereCuenta = shouldUseClientAccountForSale({
          organization: empresa,
          client: clienteSeleccionado,
          paymentConditionDays: condicionDias,
        });
        const cerrar = () => setCobroClienteModal(false);
        return (
          <div
            className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200"
            onKeyDown={(e) => { if (e.key === 'Escape') cerrar(); }}
          >
            <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300">
              <div className="bg-[#0B2A36] px-6 py-4 flex items-center justify-between shrink-0">
                <div>
                  <p className="text-[9px] font-black text-[#3FF2AA] uppercase tracking-widest mb-0.5">Cobro a cliente</p>
                  <h3 className="text-sm font-black text-white uppercase tracking-tight leading-tight break-words">
                    ¿Estás cobrando esta venta ahora?
                  </h3>
                </div>
                <button onClick={cerrar} className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all" title="Volver al carrito sin cobrar">
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 space-y-2 text-[11px]">
                <div className="flex justify-between gap-3"><span className="font-black uppercase text-slate-400 text-[9px] tracking-widest">Cliente</span><span className="font-bold text-slate-800 text-right break-words">{clienteSeleccionado.full_name}</span></div>
                <div className="flex justify-between gap-3"><span className="font-black uppercase text-slate-400 text-[9px] tracking-widest">Medio de pago</span><span className="font-bold text-slate-800 text-right">{metodoSel?.name || 'N/D'}</span></div>
                {comprobanteSel && (
                  <div className="flex justify-between gap-3"><span className="font-black uppercase text-slate-400 text-[9px] tracking-widest">Comprobante</span><span className="font-bold text-slate-800 text-right">{comprobanteSel.name}</span></div>
                )}
                <div className="flex justify-between gap-3"><span className="font-black uppercase text-slate-400 text-[9px] tracking-widest">Condición</span><span className="font-bold text-slate-800 text-right">{formatPaymentConditionLabel(condicionDias)}</span></div>
                <div className="flex justify-between items-center py-2 border-t border-slate-100 mt-1">
                  <span className="font-black text-slate-400 uppercase text-[9px] tracking-widest">Total</span>
                  <span className="text-xl font-black text-slate-900 tracking-tighter">$ {currency(totalFinal)}</span>
                </div>
              </div>

              <div className="px-5 pb-5 space-y-2">
                <button
                  autoFocus={!sugiereCuenta}
                  onClick={() => finalizarVenta({ cobroDecision: 'ahora' })}
                  className="w-full h-12 rounded-2xl bg-[#3ff2aa] text-[#0B2A36] font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 hover:scale-[1.01] active:scale-95 transition-all"
                  title="Registra el cobro ahora con el medio de pago elegido (caja, banco o terminal)."
                >
                  <Check size={16} strokeWidth={3} /> Sí, cobrar ahora ({metodoSel?.name || 'Pago'})
                </button>
                <button
                  autoFocus={sugiereCuenta}
                  onClick={() => finalizarVenta({ cobroDecision: 'cuenta' })}
                  className="w-full h-12 rounded-2xl bg-white border-2 border-[#51A7FD] text-[#51A7FD] font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 hover:bg-blue-50 active:scale-95 transition-all"
                  title="No entra plata ahora: la venta queda como deuda en la cuenta corriente del cliente."
                >
                  <User size={16} strokeWidth={3} /> No, a cuenta corriente
                </button>
                {sugiereCuenta && (
                  <p className="text-[9px] font-bold text-slate-400 text-center">Por su condición, este cliente normalmente va a cuenta corriente.</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== MODAL DE SELECCIÓN DE VARIANTE ===== */}
      {variantModal.open && variantModal.prod && (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300">
            {/* Header */}
            <div className="bg-[#0B2A36] px-6 py-4 flex items-center justify-between shrink-0">
              <div>
                <p className="text-[9px] font-black text-[#3FF2AA] uppercase tracking-widest mb-0.5">Seleccionar variante</p>
                <h3 className={`text-sm font-black text-white uppercase tracking-tight leading-tight ${abbreviateProductNames ? 'line-clamp-1' : 'break-words whitespace-normal'}`}>
                  {variantModal.prod.name}
                </h3>
              </div>
              <button
                onClick={() => setVariantModal({ open: false, prod: null })}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Lista de variantes */}
            <div className="p-4 space-y-2 overflow-y-auto max-h-[60vh] custom-scrollbar">
              {(variantModal.prod.product_variants || []).map((v) => (
                <button
                  key={v.id}
                  onClick={() => confirmarVariante(variantModal.prod, v)}
                  className="w-full flex items-center justify-between bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-[#51A7FD] rounded-2xl px-5 py-3.5 transition-all group"
                >
                  <span className="text-[11px] font-black text-slate-800 uppercase tracking-wide group-hover:text-[#51A7FD] transition-colors">
                    {getCleanVariantLabel(getVariantName(v)) || 'Variante'}
                  </span>
                  <span className="text-[13px] font-black text-[#51A7FD]">
                    $ {Number(getEffectiveVariantPrice(variantModal.prod, v) || 0).toLocaleString('es-AR')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== DESCUENTO POR PRODUCTO ===== */}
      {descItemModal && descItemModalItem && (() => {
        const item = descItemModalItem;
        const lista = precioListaItem(item);
        const pct = calcularPctDescItem(descItemModal, item);
        const ev = evaluarDescItemModal(descItemModal, item);
        const unitFinal = Math.round(lista * (1 - pct / 100) * 100) / 100;
        const unitMostrado = descItemModal.modo === 'precio' && parseValorDescItem(descItemModal.valor) !== null
          ? Math.min(lista, Math.max(0, parseValorDescItem(descItemModal.valor)))
          : unitFinal;
        const setCampo = (campo, valor) => setDescItemModal((m) => (m ? { ...m, [campo]: valor } : m));
        return (
          <div className="fixed inset-0 z-[120] bg-[#0B2A36]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !descItemModal.enviando && setDescItemModal(null)}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Descuento por producto</p>
                <p className="text-sm font-black text-slate-800 leading-tight mt-1">{sanitizeProductDisplayName(item.nombre || 'Producto')}</p>
                <p className="text-[10px] font-bold text-slate-400 mt-0.5">Precio de lista: $ {currency(lista)} · Cant. {formatCartQuantityValue(item.cantidad ?? 1)}</p>
              </div>

              <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded-xl">
                {[['pct', '% Descuento'], ['precio', 'Precio final']].map(([modo, label]) => (
                  <button
                    key={modo}
                    type="button"
                    onClick={() => setDescItemModal((m) => (m ? { ...m, modo, valor: '' } : m))}
                    className={`py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${descItemModal.modo === modo ? 'bg-white text-[#0B2A36] shadow-sm' : 'text-slate-400'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-300">{descItemModal.modo === 'pct' ? '%' : '$'}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  value={descItemModal.valor}
                  onChange={(e) => setCampo('valor', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmarDescuentoItem(); } }}
                  placeholder={descItemModal.modo === 'pct' ? 'Ej: 10' : `Ej: ${Math.round(lista * 0.9)}`}
                  className="w-full pl-8 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black outline-none focus:border-[#51A7FD]"
                />
              </div>

              <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                <span className="text-slate-400">Queda</span>
                <span className="text-emerald-600">$ {currency(unitMostrado)} c/u · -{Number(pct.toFixed(2))}%</span>
              </div>

              {!ev.ok && pct > 0 && (
                <p className="text-[10px] font-bold text-rose-500">{ev.error}</p>
              )}

              {(ev.requiereMotivo || descItemModal.motivo) && (
                <div className="space-y-1.5">
                  <label className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                    Motivo{ev.requiereMotivo ? ' (obligatorio)' : ''}
                  </label>
                  <select
                    value={descItemModal.motivo}
                    onChange={(e) => setCampo('motivo', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-bold outline-none"
                  >
                    <option value="">Elegí un motivo…</option>
                    {MOTIVOS_DESCUENTO_ITEM.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  {descItemModal.motivo === 'Otro' && (
                    <input
                      type="text"
                      value={descItemModal.motivoOtro}
                      onChange={(e) => setCampo('motivoOtro', e.target.value)}
                      placeholder="Contá el motivo"
                      maxLength={120}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-bold outline-none"
                    />
                  )}
                </div>
              )}

              {ev.ok && ev.requiereAutorizacion && (
                <div className="space-y-1.5 p-3 rounded-2xl bg-amber-50 border border-amber-100">
                  <p className="text-[9px] font-black uppercase tracking-widest text-amber-700">
                    Supera el tope de {itemDiscountConfig.maxPct}% · Autorización
                  </p>
                  <p className="text-[10px] font-bold text-amber-700/80">Un usuario habilitado ingresa su email y clave.</p>
                  <input
                    type="email"
                    autoComplete="off"
                    value={descItemModal.email}
                    onChange={(e) => setCampo('email', e.target.value)}
                    placeholder="email del autorizante"
                    className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl text-[11px] font-bold outline-none"
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={descItemModal.password}
                    onChange={(e) => setCampo('password', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmarDescuentoItem(); } }}
                    placeholder="clave"
                    className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl text-[11px] font-bold outline-none"
                  />
                </div>
              )}

              <div className="flex gap-2 pt-1">
                {tieneDescuentoItem(item) && (
                  <button
                    type="button"
                    onClick={() => quitarDescuentoLinea(descItemModal.cartKey)}
                    disabled={descItemModal.enviando}
                    className="px-3 py-2.5 rounded-xl border border-rose-100 text-[9px] font-black uppercase tracking-widest text-rose-500 hover:bg-rose-50"
                  >
                    Quitar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDescItemModal(null)}
                  disabled={descItemModal.enviando}
                  className="flex-1 py-2.5 rounded-xl bg-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmarDescuentoItem}
                  disabled={descItemModal.enviando || (pct > 0 && !ev.ok)}
                  className="flex-1 py-2.5 rounded-xl bg-[#0B2A36] text-[9px] font-black uppercase tracking-widest text-[#3FF2AA] disabled:opacity-40"
                >
                  {descItemModal.enviando ? 'Autorizando…' : ev.requiereAutorizacion ? 'Autorizar y aplicar' : 'Aplicar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== SELECTOR DE TALLE Y COLOR ===== */}
      {tallesModal.open && tallesModal.prod && (
        <SelectorTalleColor
          product={tallesModal.prod}
          siteId={sitioId}
          allowNegative={allowNegativeStock}
          enCarrito={Object.fromEntries(
            carrito
              .filter((item) => item.product_id === tallesModal.prod.id && item.variant_id)
              .map((item) => [item.variant_id, Number(item.cantidad) || 0]),
          )}
          getUnitPrice={(variant) => getEffectiveVariantPrice(tallesModal.prod, variant)}
          onConfirm={(selecciones) => agregarVariantesAlCarrito(tallesModal.prod, selecciones)}
          onClose={() => setTallesModal({ open: false, prod: null })}
          abbreviateName={abbreviateProductNames}
        />
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
